import { Application, Assets, ColorMatrixFilter, Container, Graphics, MeshRope, Point, Sprite, TilingSprite, Texture } from 'pixi.js';
import { CONFIG, computeScore } from '../config';
import { Pool } from '../core/pool';
import { Rng } from '../core/rng';
import { SpatialHash } from '../core/spatial';
import { Input } from '../core/input';
import { resolveVisual, type Visual } from '../core/assets';
import {
  RAMPS, bolt, enemySprite, gemTexture, pickupTexture, rampFromColor,
  type Attr, type FxFrames, type PickupKind,
} from '../core/pixelfx';
import { FX_RAMPS, burstFrames, fieldFrames, jagged, projectileFrames, setWhipArt, type BurstStyle, type WhipArt } from '../core/skillfx';
import { lookOf, rampOf } from './looks';
import { loadAtlas, makePixelPerfect, type Atlas } from '../core/atlas';
import { CharacterAnimator, FOOT_DROP, type AnimDef } from '../core/anim';
import { audio, type Sfx } from '../core/audio';
import type {
  CharacterDef, EnemyDef, FusionDef, PassiveDef, RunResult, StatusApply, WeaponDef,
} from '../core/types';
import { Enemy, EnemyShot, FieldZone, Gem, Projectile } from './entities';
import { computePlayerStats, type OwnedPassive, type PlayerStats } from './stats';
import {
  FORM_IGNITE, WeaponInstance, setSwordArt, type ActiveState, type ChainSpec, type FieldOpts, type LeapSpec, type ProjectileSpec,
  type StrikeSpec, type SwordArt, type WeaponHost,
} from './weapons';
import { buildChoices, type LevelUpChoice } from './levelup';
import { WorldMap } from './worldmap';
import { loadSpiritArt } from './spirits';
import { loadTempestArt } from './tempest';
import { artFlip, enemyArtOf, fxArt, loadEnemyArt, type EFrame } from './enemyart';
import { makeBrain, type BossHost, type FxSpec, type ShotSpec } from './bosses';
import { hazards, warnDash, warnSector } from './warn';
import { CharmFx } from './charmfx';
import { chestTexture, groundTile } from '../core/mapfx';

/** 상태효과 상수를 짧게 쓰기 위한 별칭 */
const ST = CONFIG.STATUS;

type BoltStyle = 'arc' | 'static' | 'streak' | 'beam' | 'violet';

/** 번개·잔상 선 하나 */
interface BoltLine {
  g: Graphics;
  life: number;
  max: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  style: BoltStyle;
  color: number;
  /** 감전 선은 사는 동안 한 번 다시 그린다 */
  redrawn: boolean;
  /** 이만큼(초) 기다렸다가 나타난다 — 전류가 한 마리씩 차례로 옮겨붙는 게 보이게 */
  wait: number;
  /** 굵기 배율 (전류의 bolt 스탯) */
  scale: number;
}

/**
 * 1.24 대시 잔상 한 장 — 대시를 시작하는 순간 몸 그림을 그대로 찍어 길 위에 뿌린다.
 * 더하기(add)로 깔아서 번개에 타 버린 잔상처럼 보이게 하고, 그 자리에서 가만히 바래며 사라진다.
 */
interface Ghost {
  /** 보통 섞기 — 몸 모양 */
  body: Sprite;
  /** 더하기 — 그 위에 얹는 번개 빛 */
  glow: Sprite;
  t: number;
  /** 0 이면 안 쓰는 칸 */
  life: number;
  /** 처음 진하기 */
  a0: number;
}

/** 도약 중인 플레이어 */
interface DashState {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  t: number;
  dur: number;
  dirX: number;
  dirY: number;
  /** 사냥 준비(도약)이면 착지 규칙, 1.24 대시면 null */
  spec: LeapSpec | null;
  /** 1.24 전격 질주로 쓴 대시 (없으면 기본 대시 — 피해 없음) */
  zap: { damage: number; status?: StatusApply } | null;
  /** 1.24 다음 잔상까지 남은 시간(초). 도약(사냥 준비)은 안 쓴다 */
  ghostT: number;
}

/**
 * 1.24 기본 대시 — 누구나 Shift 로 쓴다. 찔끔 가고(118px) 쿨이 길어서(3.2초) 함부로 못 쓴다.
 * 대신 가는 동안은 잠깐 무적이라, 보스 경고를 읽고 빠져나가는 수단이다.
 * 전격 질주(behavior 'dash')를 가지면 이 대시가 번개 대시로 바뀐다 — 길고, 지나간 줄을 긋고, 전기 잔류가 남는다.
 */
const DASH_BASE = { dist: 118, dur: 0.11, cd: 3.2, iframe: 0.24 };

/**
 * 1.24 잔상 색. 몸 그림을 한 장만 깔면(색만 입히면) 어두운 옷이 어두운 채로 남아 배경에 묻히고,
 * 더하기(add)로만 깔면 밝은 데가 타 버려서 몸 모양이 아니라 빛 덩어리가 된다.
 * 그래서 두 장을 겹친다 — 보통 섞기로 몸 모양을 잡고(body), 그 위에 더하기로 번개 빛을 얹는다(glow).
 * 변신한 몸의 불꽃 테두리(auraGlow)가 쓰는 것과 같은 수법이다.
 *   [0] 전격 질주 = 보랏빛 번개 / [1] 맨몸 대시 = 희푸른 바람
 */
const GHOST_COLORS = [
  { r: 0.30, g: 0.10, b: 0.82, glow: 0x4a38a8 },
  { r: 0.12, g: 0.34, b: 0.62, glow: 0x2f5880 },
] as const;

/**
 * 잔상 몸 색을 만드는 필터. 몸 그림에 색만 입히면(tint) 곱셈이라 어두운 옷은 어두운 채로 남아
 * 배경에 묻힌다. 그래서 밝기만 조금 남기고(k) 나머지는 한 가지 빛으로 칠해, 어두운 옷을 입었든
 * 밝은 옷을 입었든 몸 모양이 또렷한 빛으로 읽히게 한다.
 */
function ghostFilter(c: { r: number; g: number; b: number }, k = 1.25): ColorMatrixFilter {
  const f = new ColorMatrixFilter();
  const [lr, lg, lb] = [0.21 * k, 0.72 * k, 0.07 * k];
  // 가로 다섯 칸이 한 줄 (빨강·초록·파랑·투명도·더할 값). 투명도 줄은 건드리지 않는다
  f.matrix = [
    lr, lg, lb, 0, c.r,
    lr, lg, lb, 0, c.g,
    lr, lg, lb, 0, c.b,
    0, 0, 0, 1, 0,
  ];
  return f;
}

/** 질주하는 동안 잔상을 남기는 간격(초) */
const GHOST_STEP = 0.05;

/** 도약이 끝까지 날아가는 시간(초). 짧아야 "휙" 하고 뛰어든다. */
const DASH_TIME = 0.16;

/** 여제의 명령이 남긴 전기장 한 줄 */
interface StormBand {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  length: number;
  halfWidth: number;
  life: number;
  tick: number;
  damage: number;
  link: number;
}

/**
 * 상태효과 반응·점화·쇄화로 한 박자 늦게 터지는 것. 순회 도중 범위 피해를 또 부르면 공간 해시 버퍼가 꼬이므로
 * 프레임 끝에 한꺼번에 터뜨린다.
 */
interface Blast {
  kind: 'steam' | 'shatter' | 'blast' | 'ignite';
  x: number;
  y: number;
  radius: number;
  damage: number;
  status?: StatusApply;
}

/** 회광반조가 바닥에 남긴 검상 하나 */
interface Wound {
  rope: MeshRope;
  /** 드러나는 동안 씌우는 부채꼴 가리개 (다 드러나면 없앤다) */
  mask: Graphics | null;
  x: number;
  y: number;
  radius: number;
  a0: number;
  a1: number;
  t: number;
  reveal: number;
  life: number;
  tick: number;
  damage: number;
  status: StatusApply;
}

/** 검상이 바닥에 남는 시간(초)과 한 번에 둘 수 있는 수 */
const WOUND_LIFE = 1.8;
const MAX_WOUNDS = 14;

/** 엘리트가 처음 나오는 시각과 그 뒤 간격(초) */
const ELITE_FIRST = 100;
const ELITE_EVERY = 80;

/**
 * 여제의 명령 번개 그림. tools/build_fx.py 가 받은 번개 사진에서 만든다.
 * anchor 는 번개가 시작하는 점(시전자 자리), length 는 그 점에서 끝까지의 길이(px),
 * halfWidth 는 줄기 빛의 반폭(px) — 이 폭만큼 적이 맞는다.
 */
interface LightningArt {
  texture: Texture;
  anchorX: number;
  anchorY: number;
  length: number;
  halfWidth: number;
}

/** 떨어진 번개 하나 (그림) */
interface StrikeFx {
  sprite: Sprite;
  t: number;
}

export type GameState = 'idle' | 'playing' | 'levelup' | 'paused' | 'over';

export interface HudSnapshot {
  hp: number;
  maxHp: number;
  level: number;
  xp: number;
  xpNeed: number;
  kills: number;
  elapsed: number;
  bossesDown: number;
  /** 다음 보스까지 남은 시간(초) */
  nextBossIn: number;
  bossName: string | null;
  bossHpRatio: number;
  weapons: Array<{ name: string; level: number; color: string; shape: string; sprite?: string }>;
  passives: Array<{ name: string; level: number; color: string; shape: string; sprite?: string }>;
  /**
   * 액티브 스킬 칸들 (사냥 준비 · 회광반조). 먼저 얻은 것이 Space, 다음이 E. (대시는 Shift 로 따로)
   * ratio 는 다음 한 번이 차는 진행도(변신은 기력), 가득이면 1. combo 는 다음 도약이 몇 단째 연속인지
   */
  actives: Array<ActiveState & { name: string; color: string; shape: string; sprite?: string; key: string }>;
  /** 회광반조 기력 (0~100) — 화면 위에 게이지로. 변신 스킬이 없으면 null */
  vigor: { value: number; on: boolean; tired: boolean; ready: boolean; key: string } | null;
  /** 1.24 대시 — 모아 둔 횟수 · 최대 · 다음 한 번이 차는 진행도 · 전격 질주로 세진 대시인가 */
  dash: { charges: number; max: number; ratio: number; zap: boolean };
  /** 세포 역설의 보호막 (0 이면 없음) */
  shield: number;
  enemyCount: number;
  fps: number;
}

export interface GameData {
  characters: CharacterDef[];
  weapons: WeaponDef[];
  passives: PassiveDef[];
  enemies: EnemyDef[];
  fusions: FusionDef[];
}

export interface GameCallbacks {
  onHud(snap: HudSnapshot): void;
  /** source 'chest' 면 엘리트가 떨군 보물 상자에서 나온 카드 */
  onLevelUp(choices: LevelUpChoice[], source: 'level' | 'chest'): void;
  /** 화면 가운데 큰 띠 (조합 순간·엘리트 출현 · 1.22 보스 등장 — 그림과 함께) */
  onBanner(text: string, sub: string, color: string, kind: 'fusion' | 'notice' | 'boss', image?: string): void;
  onOver(result: RunResult): void;
  onStateChange(state: GameState): void;
}

/** 1.22 쓰러지는 적 — 죽는 동작을 보여 주고 사라진다 */
interface Corpse {
  sprite: Sprite;
  frames: EFrame[] | null;
  fps: number;
  t: number;
  life: number;
  /** 광풍에 휘말린 채 죽으면 시체가 마저 날아간다: 땅 위치·속도·남은 비행·높이·바퀴 수·도는 쪽 */
  x: number;
  y: number;
  vx: number;
  vy: number;
  flyT: number;
  flyDur: number;
  flyH: number;
  turns: number;
  face: number;
}

/** 1.22 한 번 보여 주고 사라지는 그림 (보스 기술 · 적 탄이 맞은 자리) */
interface FxSprite {
  sprite: Sprite;
  t: number;
  spec: FxSpec | null;
}

/** 1.22 [매혹] — 끌려가는 빠르기(이동 속도 대비) · 방향을 바꿀 때마다 줄어드는 시간 · 풀린 뒤 면역 */
const CHARM_PULL = 0.35;
const CHARM_STRUGGLE = 0.12;
const CHARM_IMMUNE = 2.5;

/** 1.22 보스 등장 띠의 두 번째 줄 */
const BOSS_TITLE: Record<string, string> = {
  gatekeeper: '월도를 든 관문지기',
  rakshasa: '여섯 칼의 붉은 연꽃',
  king: '천하를 삼키는 괴왕',
  ryu: '핏빛 칼의 검신',
};

export class Game implements WeaponHost {
  state: GameState = 'idle';

  /** 마지막으로 제시한 레벨업 선택지. 시뮬레이터가 자동으로 고를 때 쓴다. */
  lastChoices: LevelUpChoice[] = [];
  /** 자동 조종. 설정되면 입력 대신 이 벡터로 움직인다(시뮬레이션·테스트용). */
  autoPilot: ((elapsed: number) => { x: number; y: number }) | null = null;
  /** 시뮬레이션·측정용: 액티브 스킬(도약)을 사람 대신 알아서 쓴다 — 적이 가장 두꺼운 쪽으로 */
  autoActive = false;
  /** 도약 중이면 그 경로 */
  private dash: DashState | null = null;
  /** 1.24 대시 — 남은 쿨(초) · 모아 둔 횟수 */
  private dashCd = 0;
  private dashCharges = 1;
  /** 마지막 판의 최종 점수. */
  lastScore = 0;

  get enemyCount(): number {
    return this.enemies.count;
  }

  get weaponSummary(): string[] {
    return this.weapons.map((w) => `${w.def.name}${w.level}`);
  }

  get passiveSummary(): string[] {
    return this.passives.map((p) => `${p.def.name}${p.level}`);
  }

  // 플레이어
  px = 0;
  py = 0;
  faceX = 1;
  faceY = 0;
  hp = 100;
  playerStats!: PlayerStats;
  private character!: CharacterDef;
  private iframe = 0;

  // 진행
  private elapsed = 0;
  level = 1;
  private xp = 0;
  private xpNeed = CONFIG.xpToNext(1);
  kills = 0;
  private pendingLevelUps = 0;
  private activeChoiceSource: 'level' | 'chest' | null = null;

  rng = new Rng(1);

  // 컨테이너
  private readonly world = new Container();
  readonly fxLayer = new Container();
  private readonly fieldLayer = new Container();
  private readonly gemLayer = new Container();
  /** 1.22 적은 발이 화면 아래인 쪽이 앞 */
  private readonly enemyLayer = new Container({ sortableChildren: true });
  /** 1.22 적 발밑 그림자 · 쓰러지는 적 (살아 있는 적보다 아래) */
  private readonly shadowLayer = new Container();
  private readonly corpseLayer = new Container();
  private readonly projLayer = new Container();
  /** 캐릭터와 정령왕 — 발 y 로 앞뒤를 가린다(화면 아래가 앞) */
  private readonly playerLayer = new Container({ sortableChildren: true });
  private bg!: TilingSprite;
  /** 사천 야생 — 바닥 장식 (판정 없음) */
  private readonly map = new WorldMap();
  private playerSprite!: Sprite;

  // 풀
  private enemies!: Pool<Enemy>;
  private projectiles!: Pool<Projectile>;
  private gems!: Pool<Gem>;
  private fields!: Pool<FieldZone>;
  private shots!: Pool<EnemyShot>;
  private shotTex!: Texture;

  private readonly hash = new SpatialHash(CONFIG.SPATIAL_CELL);
  private readonly scratch: number[] = [];
  private readonly scratch2: number[] = [];
  private readonly scratch3: number[] = [];
  private readonly spawnScratch = { x: 0, y: 0 };
  private maxEnemyRadius = 32;
  /** 이번 1초 동안 흡혈로 회복한 양 */
  private healedThisSecond = 0;
  private healWindow = 0;
  /** [감전] 연쇄는 순회 도중에 처리하면 위험하므로 프레임 끝으로 미룬다. */
  private readonly pendingShocks: Array<{ x: number; y: number; dmg: number; uid: number }> = [];

  // 무기·패시브
  private weapons: WeaponInstance[] = [];
  private passives: OwnedPassive[] = [];

  // 스폰
  private spawnTimer = 0;
  private nextBossIndex = 0;
  private bossesDown = 0;
  private uidCounter = 1;

  // 연출
  private shakeT = 0;
  private shakeMag = 0;
  private accumulator = 0;
  private fps = 60;
  /** 슬로모션 남은 실제 시간(초)과 그동안의 시간 배율 (조합 순간·보스 처치) */
  private slowT = 0;
  private slowScale = 1;
  /** 타격 멈춤 남은 실제 시간, 다음 멈춤까지 쉬는 시간 */
  private hitstopT = 0;
  private hitstopCd = 0;
  private flashMax = 0.12;
  private flashAlpha = 0.12;

  // 1.11: 고유 스킬·반응·엘리트
  /** 이번 프레임 몸 크기(타이탄). 무기들이 매 프레임 다시 알려 준다 */
  private bodyScale = 1;
  private bodyScaleNext = 1;
  private baseScale = 1;
  /** 세포 역설 보호막: 지금 양, 최대(절대값), 깨질 때 폭발 피해·상태효과 */
  private shield = 0;
  private shieldCap = 0;
  private shieldCapNext = 0;
  private shieldBurst = 0;
  /** 이번 프레임에 광풍에서 떨어진 적의 흙먼지 수 (몇 번만 그린다) */
  private landPuffs = 0;
  /** 판이 멈춰 있을 때(메뉴·레벨업·일시정지)만 하나씩 구울 그림 일감. 싸우는 도중에는 안 굽는다 */
  private readonly warmQueue: Array<() => void> = [];
  private warmPumping = false;
  /** 멀리서 오래 묵어 치운 경험치 보석들의 경험치. 다음에 떨어지는 보석에 얹는다 — 경험치는 한 점도 버리지 않는다 */
  private xpCarry = 0;
  /** 지금 화면이 덮는 반폭·반높이(월드 좌표). 이 밖의 그림은 안 그린다. 카메라가 처음 잡히기 전에는 끝없이 크다 */
  private viewHW = Infinity;
  private viewHH = Infinity;
  private shieldStatus: StatusApply = {};
  private shieldG!: Graphics;
  /** 가진 무기 중 가장 센 쇄화 (얼어붙은 적이 맞으면 산산조각) */
  private shatterPower = 0;
  private readonly blasts: Blast[] = [];
  private readonly storms: StormBand[] = [];
  private eliteTimer = ELITE_FIRST;
  private pendingChests = 0;
  private chestTex!: Texture;
  private readonly targetBuf: Enemy[] = [];
  private readonly scratch4: number[] = [];

  // 캐시
  private readonly enemyVisuals = new Map<string, Visual>();
  private readonly weaponVisuals = new Map<string, Visual>();
  private gemTex!: Texture;
  private pickupTex!: Record<PickupKind, Texture>;
  private healTimer = 0;
  private xpTimer = 0;
  private xpBigTimer = 0;
  private xpHugeTimer = 0;
  private fieldVisual!: Visual;
  private readonly enemyTex = new Map<string, Texture>();
  /** 무기별 탄 그림. 여러 장이면 번갈아 보여 준다. */
  private readonly weaponTex = new Map<string, Texture[]>();
  /** 장판은 레벨마다 크기가 달라져서 (무기, 반지름)으로 캐시한다. */
  private readonly fieldTexCache = new Map<string, Texture[]>();
  /** 폭발 연출용 스프라이트 풀 */
  private readonly bursts: Array<{ sprite: Sprite; frames: FxFrames | null; t: number; wait: number }> = [];
  private readonly burstCache = new Map<string, FxFrames>();
  /** 번개·잔상 선 (Graphics 를 재활용한다) */
  private readonly bolts: BoltLine[] = [];
  /** 여제의 명령 번개 그림과 떨어진 번개들 */
  private lightningArt: LightningArt | null = null;
  private readonly strikeFx: StrikeFx[] = [];
  /** 번개가 칠 때 화면 전체가 번쩍 */
  private flash!: Sprite;
  private flashT = 0;

  /** 지금 캐릭터의 도트 애니메이터. 그 캐릭터 그림이 없으면 null(도형으로 그린다). */
  private animator: CharacterAnimator | null = null;
  /** 애니메이터는 하나만 만들어 두고 캐릭터가 바뀌면 그림만 갈아 끼운다 */
  private animatorObj: CharacterAnimator | null = null;
  private atlas: Atlas | null = null;
  private baseAnim: AnimDef | null = null;
  /** 캐릭터마다 그림 (id → 아틀라스·동작·변신 모습들) */
  private readonly charArt = new Map<string, { atlas: Atlas; anim: AnimDef; forms: Map<string, FormArt> }>();
  /** 지금 캐릭터의 변신 모습들 (이름 → 아틀라스·동작·변신/풀림 프레임) */
  private forms = new Map<string, FormArt>();
  /** 지금 모습 (null = 기본) */
  private form: string | null = null;
  /** 변신 연출: 몸이 검어지는 시간(초, 남은 것) */
  private darkenT = 0;
  // 변신한 몸의 효과: 불꽃 테두리(몸 그림을 붉게 한 겹 크게 뒤에 — aura 모습만), 잿불(모두), 식는 색, 움츠러듦, 부풀기
  private auraGlow!: Sprite;
  private auraOn = false;
  private auraK = 0;
  private emberG!: Graphics;
  private readonly embers: Ember[] = [];
  private emberAcc = 0;
  /** 풀린 뒤 몸이 잿빛에서 제 색으로 식는 남은 시간 */
  private coolT = 0;
  /** 지쳐서 풀릴 때 몸이 한 번 움츠러드는 남은 시간 (풀림 그림이 없는 캐릭터) */
  private slumpT = 0;
  /** 변신할 때 몸이 한 번 부풀었다 가라앉는 남은 시간 (변신 그림이 없는 캐릭터) */
  private popT = 0;
  /** 머리 위 한마디 ("지쳤다…") — 글자는 캔버스에 한 번 그려 둔 그림(Pixi Text 를 들이면 번들이 50KB 커진다) */
  private sayText!: Sprite;
  private readonly sayTex = new Map<string, Texture>();
  private sayT = 0;
  /** 머리 위 글씨처럼 캐릭터보다 위에 그리는 층 */
  private readonly overLayer = new Container();
  // 회광반조 검상
  private woundTex: Texture | null = null;
  // 소한공 한기 (날아가는 그림)
  private palmTex: Texture | null = null;
  private readonly palmFx: Array<{ sprite: Sprite; t: number; x0: number; y0: number; x1: number; y1: number; hw: number; big: boolean }> = [];
  private readonly wounds: Wound[] = [];

  // ── 1.22 적 그림 · 매혹 · 보스 ──
  /** 바닥 경고 (스텝마다 다시 그린다) */
  private readonly warnG = new Graphics();
  private shadowTex: Texture = Texture.EMPTY;
  private readonly corpses: Corpse[] = [];
  private readonly fxSprites: FxSprite[] = [];
  /** 1.24 대시 잔상 — 떠난 자리·지나온 길·닿은 자리에 남는 몸 그림자 */
  private readonly ghosts: Ghost[] = [];
  /** 잔상 몸 색: [0] 전격 질주(보랏빛) · [1] 맨몸 대시(희푸름) */
  private readonly ghostFilters = GHOST_COLORS.map((c) => ghostFilter(c));
  /** 이번 스텝만 보이는 그림 (칼 고리 · 이무기) */
  private readonly stamps: Sprite[] = [];
  private stampN = 0;
  /** [매혹] 남은 시간 · 처음 걸린 길이 · 끌어당기는 적 · 풀린 뒤 면역 · 몸부림(마지막 방향) */
  private charmT = 0;
  private charmMax = 1;
  private charmSrc: Enemy | null = null;
  private charmUid = 0;
  private charmX = 0;
  private charmY = 0;
  private charmImmune = 0;
  private struggleSector = -1;
  /** 몸부림은 이 간격(초)에 한 번만 센다 — 마구 눌러도 초당 1초 남짓만 줄어든다 */
  private struggleT = 0;
  private charmFx!: CharmFx;
  /** 홀린 몸에 더하는 분홍 빛 (어두운 캐릭터도 보이게 — 곱하기 색만으로는 옅었다) */
  private charmGlow!: Sprite;
  /** 보스 머리가 쓰는 창구 */
  private readonly bossHost: BossHost = this.makeBossHost();

  constructor(
    private readonly app: Application,
    private readonly data: GameData,
    private readonly cb: GameCallbacks,
    private readonly input: Input,
  ) {
    this.buildScene();
    this.buildPools();
    this.app.ticker.add(this.tick);
  }

  // ── 초기 구성 ──────────────────────────────────────────

  private buildScene(): void {
    // 바닥: 사천 땅의 흙·이끼 타일. 월드가 아니라 스테이지에 붙이고 tilePosition으로 스크롤한다.
    this.bg = new TilingSprite({ texture: groundTile(), width: 100, height: 100 });
    this.app.stage.addChild(this.bg);

    // 맵 장식(대숲·나무·바위…)은 전부 적·장판보다 아래. 판정은 없다.
    this.world.addChild(
      this.map.flatLayer, this.map.standLayer, this.map.mistLayer,
      this.fieldLayer, this.shadowLayer, this.corpseLayer, this.gemLayer, this.enemyLayer, this.fxLayer, this.projLayer, this.playerLayer, this.overLayer,
    );
    this.app.stage.addChild(this.world);

    this.playerSprite = new Sprite(Texture.WHITE);
    this.playerSprite.anchor.set(0.5);
    this.shieldG = new Graphics();
    // 변신한 몸의 불꽃 테두리: 몸 그림을 붉게 더해(add) 한 겹 크게 몸 뒤에 둔다
    this.auraGlow = new Sprite(Texture.WHITE);
    this.auraGlow.blendMode = 'add';
    this.auraGlow.tint = 0xff4a14;
    this.auraGlow.visible = false;
    this.emberG = new Graphics();
    this.playerLayer.addChild(this.shieldG, this.auraGlow, this.playerSprite, this.emberG);
    this.sayText = new Sprite(Texture.EMPTY);
    this.sayText.anchor.set(0.5, 1);
    this.sayText.scale.set(0.5); // 두 배로 그려 둔 글자
    this.sayText.visible = false;
    this.overLayer.addChild(this.sayText);
    this.charmFx = new CharmFx(this.overLayer);
    this.charmGlow = new Sprite(Texture.WHITE);
    this.charmGlow.blendMode = 'add';
    this.charmGlow.tint = 0xff4f9e;
    this.charmGlow.visible = false;
    this.playerLayer.addChild(this.charmGlow);

    this.flash = new Sprite(Texture.WHITE);
    this.flash.tint = 0xfff6d8;
    this.flash.alpha = 0;
    this.flash.visible = false;
    this.app.stage.addChild(this.flash);
  }

  private buildPools(): void {
    this.enemies = new Pool(CONFIG.MAX_ENEMIES, () => {
      const e = new Enemy();
      this.shadowLayer.addChild(e.shadow);
      this.enemyLayer.addChild(e.sprite, e.gild);
      return e;
    });
    this.projectiles = new Pool(CONFIG.MAX_PROJECTILES, () => {
      const p = new Projectile();
      this.projLayer.addChild(p.sprite);
      return p;
    });
    this.gems = new Pool(CONFIG.MAX_GEMS, () => {
      const gm = new Gem();
      this.gemLayer.addChild(gm.sprite);
      return gm;
    });
    this.fields = new Pool(CONFIG.MAX_FIELDS, () => {
      const f = new FieldZone();
      this.fieldLayer.addChild(f.sprite);
      return f;
    });
    this.shots = new Pool(CONFIG.MAX_ENEMY_SHOTS, () => {
      const s = new EnemyShot();
      this.projLayer.addChild(s.sprite);
      return s;
    });
    // 폭발 자국 스프라이트 — 정령왕의 불티 꼬리·충격파가 한꺼번에 많이 쓴다 (정령왕 + 만렙 넷에서 최대 56개 동시)
    for (let i = 0; i < 96; i++) {
      const sp = new Sprite();
      sp.anchor.set(0.5);
      sp.visible = false;
      this.fxLayer.addChild(sp);
      this.bursts.push({ sprite: sp, frames: null, t: 0, wait: 0 });
    }
    // 1.22 쓰러지는 적 · 한 번 보여 주는 그림 · 스텝마다 찍는 그림 · 바닥 경고
    for (let i = 0; i < 120; i++) {
      const sp = new Sprite();
      sp.visible = false;
      this.corpseLayer.addChild(sp);
      this.corpses.push({ sprite: sp, frames: null, fps: 8, t: 0, life: 0, x: 0, y: 0, vx: 0, vy: 0, flyT: 0, flyDur: 1, flyH: 0, turns: 0, face: 1 });
    }
    for (let i = 0; i < 64; i++) {
      const sp = new Sprite();
      sp.anchor.set(0.5);
      sp.visible = false;
      this.fxLayer.addChild(sp);
      this.fxSprites.push({ sprite: sp, t: 0, spec: null });
    }
    for (let i = 0; i < 200; i++) {
      const sp = new Sprite();
      sp.anchor.set(0.5);
      sp.visible = false;
      this.fxLayer.addChild(sp);
      this.stamps.push(sp);
    }
    // 1.24 대시 잔상 — 몸보다 뒤(fxLayer)에, 그중에서도 맨 아래(addChildAt 0)에 깔린다.
    // 번개 그림(고리·줄기)이 잔상 위에 와야 "번개가 지나가고 몸이 남았다"로 읽힌다
    for (let i = 0; i < 16; i++) {
      const body = new Sprite();
      const glow = new Sprite();
      for (const sp of [body, glow]) {
        sp.anchor.set(0.5);
        sp.visible = false;
        this.fxLayer.addChildAt(sp, 0);
      }
      glow.blendMode = 'add';
      this.ghosts.push({ body, glow, t: 0, life: 0, a0: 1 });
    }
    this.fieldLayer.addChild(this.warnG);
  }

  /** 스프라이트 로드가 끝난 뒤 한 번 호출. 텍스처 조회 결과를 캐싱한다. */
  prepareVisuals(): void {
    // 적: 종류마다 제 색으로 구운 도트 덩어리. 그림 파일이 있으면 그걸 우선한다.
    this.data.enemies.forEach((d, i) => {
      const v = resolveVisual(d.sprite, d.shape, d.color);
      this.enemyVisuals.set(d.id, v);
      if (!d.sprite) {
        this.enemyTex.set(d.id, enemySprite(d.silhouette ?? 'blob', d.radius * 1.5, rampFromColor(d.color), i + 1));
      }
    });
    // 무기 탄환: 스킬마다 다른 모양으로 구운 도트 (core/skillfx.ts)
    for (const d of [...this.data.weapons, ...this.data.fusions]) {
      const size = Math.max(3, (d.base.size ?? 8));
      if (d.behavior === 'projectile' || d.behavior === 'nova') {
        this.weaponTex.set(d.id, projectileFrames(d.fx, size, rampOf(d), d.base.width ?? 0));
      }
      this.weaponVisuals.set(d.id, resolveVisual(d.sprite, d.shape, d.color));
    }
    this.maxEnemyRadius = this.data.enemies.reduce((m, e) => Math.max(m, e.radius), 0);
    this.shotTex = bolt('orb', 7, ['#ffd9e0', '#ff8fa3', '#e0475f', '#8f1f30', '#3a0a12']);
    this.gemTex = gemTexture(5, RAMPS.blue);
    this.pickupTex = {
      heal: pickupTexture('heal', 17, ['#ffffff', '#b8ffd0', '#3ddc84', '#1f8f55', '#0b3a22']),
      xp: pickupTexture('xp', 8, RAMPS.blue),
      xpBig: pickupTexture('xpBig', 12, ['#ffffff', '#e0c8ff', '#b06bff', '#6a2fb0', '#2a1046']),
      xpHuge: pickupTexture('xpHuge', 17, ['#ffffff', '#fff3b0', '#ffd23a', '#c98a00', '#4a3000']),
    };
    this.fieldVisual = resolveVisual(undefined, 'circle', '#ffffff');
    makePixelPerfect(this.fieldVisual.texture);
    this.chestTex = chestTexture();
    this.shadowTex = shadowTexture();
  }

  /** 그림 파일로 된 이펙트(여제의 명령 번개, 기사의 검, 회광반조 잿빛대검·검상). 없으면 코드로 그린 것으로 대신한다. */
  async loadFxArt(): Promise<void> {
    await loadEnemyArt(this.data.enemies);
    await loadSpiritArt();
    await loadTempestArt();
    try {
      const res = await fetch('assets/fx/ash_sword.json');
      if (!res.ok) throw new Error(String(res.status));
      const meta = (await res.json()) as Omit<SwordArt, 'texture'> & { image: string };
      const tex = (await Assets.load('assets/fx/' + meta.image)) as Texture;
      tex.source.scaleMode = 'nearest';
      setSwordArt({ texture: tex, anchorX: meta.anchorX, anchorY: meta.anchorY, length: meta.length, guard: meta.guard }, 'ash');
    } catch (e) {
      console.warn('[art] 잿빛대검 그림을 못 읽었다. 코드로 찍은 검으로 대신한다.', e);
    }
    try {
      // 화염 채찍: 굽는 동안 그림 칸을 직접 읽어 휘어 붙이므로 텍스처가 아니라 픽셀로 들고 있는다
      const res = await fetch('assets/fx/fire_whip.json');
      if (!res.ok) throw new Error(String(res.status));
      const meta = (await res.json()) as Omit<WhipArt, 'px'> & { image: string };
      const img = new Image();
      img.src = 'assets/fx/' + meta.image;
      await img.decode();
      const cv = document.createElement('canvas');
      cv.width = img.width;
      cv.height = img.height;
      const ctx = cv.getContext('2d', { willReadFrequently: true })!;
      ctx.drawImage(img, 0, 0);
      setWhipArt({ ...meta, px: ctx.getImageData(0, 0, img.width, img.height).data });
    } catch (e) {
      console.warn('[art] 화염 채찍 그림을 못 읽었다. 코드로 그린 채찍으로 대신한다.', e);
    }
    try {
      const tex = (await Assets.load('assets/fx/sohangong_wave.png')) as Texture;
      tex.source.scaleMode = 'nearest';
      this.palmTex = tex;
    } catch (e) {
      console.warn('[art] 소한공 한기 그림을 못 읽었다. 선으로 대신한다.', e);
    }
    try {
      const tex = (await Assets.load('assets/fx/sword_wound.png')) as Texture;
      tex.source.scaleMode = 'nearest';
      this.woundTex = tex;
    } catch (e) {
      console.warn('[art] 검상 그림을 못 읽었다. 잿불 자국으로 대신한다.', e);
    }
    try {
      const res = await fetch('assets/fx/knight_sword.json');
      if (!res.ok) throw new Error(String(res.status));
      const meta = (await res.json()) as Omit<SwordArt, 'texture'> & { image: string };
      const tex = (await Assets.load('assets/fx/' + meta.image)) as Texture;
      tex.source.scaleMode = 'nearest';
      setSwordArt({ texture: tex, anchorX: meta.anchorX, anchorY: meta.anchorY, length: meta.length, guard: meta.guard });
    } catch (e) {
      console.warn('[art] 기사의 검 그림을 못 읽었다. 코드로 찍은 검으로 대신한다.', e);
    }
    try {
      const res = await fetch('assets/fx/lightning_royal.json');
      if (!res.ok) throw new Error(String(res.status));
      const meta = (await res.json()) as Omit<LightningArt, 'texture'> & { image: string };
      const tex = (await Assets.load('assets/fx/' + meta.image)) as Texture;
      this.lightningArt = { texture: tex, anchorX: meta.anchorX, anchorY: meta.anchorY, length: meta.length, halfWidth: meta.halfWidth };
    } catch (e) {
      console.warn('[art] 번개 그림을 못 읽었다. 선으로 대신 그린다.', e);
    }
  }

  /** 캐릭터마다 도트 아틀라스를 읽는다. 못 읽은 캐릭터는 도형으로 남는다. */
  async loadCharacterArt(): Promise<void> {
    for (const ch of this.data.characters) {
      if (!ch.atlas || !ch.anim) continue;
      let atlas: Atlas;
      try {
        atlas = await loadAtlas(ch.atlas);
      } catch (e) {
        console.warn(`[art] ${ch.name} 아틀라스를 못 읽었다. 도형으로 진행한다.`, e);
        continue;
      }
      // 변신한 모습들 (회광반조 등). 못 읽으면 원래 그림에 불꽃 테두리만 입는다(formOf)
      const forms = new Map<string, FormArt>();
      for (const [name, f] of Object.entries(ch.forms ?? {})) {
        try {
          forms.set(name, {
            atlas: f.atlas === ch.atlas ? atlas : await loadAtlas(f.atlas),
            anim: f.anim,
            transform: f.transform ?? [],
            untransform: f.untransform ?? [],
            aura: f.aura ?? false,
          });
        } catch (e) {
          console.warn(`[art] ${ch.name} 변신 모습 ${name} 을 못 읽었다.`, e);
        }
      }
      this.charArt.set(ch.id, { atlas, anim: ch.anim, forms });
      if (!this.animatorObj) {
        this.animatorObj = new CharacterAnimator(this.playerSprite, atlas, ch.anim);
        this.playerSprite.scale.set(1);
      }
    }
  }

  // ── 판 시작/종료 ───────────────────────────────────────

  start(characterId: string, seed = (Math.random() * 0xffffffff) >>> 0): void {
    const ch = this.data.characters.find((c) => c.id === characterId) ?? this.data.characters[0];
    this.character = ch;
    this.rng = new Rng(seed);

    for (const w of this.weapons) w.destroy();
    this.weapons = [];
    this.passives = [];

    const startPassive = ch.passive ? this.data.passives.find((p) => p.id === ch.passive) : undefined;
    if (startPassive) this.passives.push({ def: startPassive, level: 1 });

    // 시작 무기가 비어 있으면 맨손으로 시작한다(준). 없는 id 면 예전처럼 첫 무기
    if (ch.startWeapon) {
      const startWeapon = this.data.weapons.find((w) => w.id === ch.startWeapon) ?? this.data.weapons[0];
      this.weapons.push(new WeaponInstance(startWeapon));
    }

    this.playerStats = this.statsFor(ch);
    this.hp = this.playerStats.maxHp;

    this.px = 0;
    this.py = 0;
    this.faceX = 1;
    this.faceY = 0;
    this.iframe = 0;
    this.dash = null;
    this.dashCd = 0;
    this.dashCharges = 1;
    this.elapsed = 0;
    this.level = 1;
    this.xp = 0;
    this.xpCarry = 0;
    this.xpNeed = CONFIG.xpToNext(1);
    this.kills = 0;
    this.pendingLevelUps = 0;
    this.activeChoiceSource = null;
    this.spawnTimer = 0;
    this.nextBossIndex = 0;
    this.bossesDown = 0;
    this.healTimer = 8;
    this.xpTimer = 3;
    this.xpBigTimer = 20;
    this.xpHugeTimer = 40;
    this.accumulator = 0;
    this.shakeT = 0;
    this.slowT = 0;
    this.hitstopT = 0;
    this.bodyScale = 1;
    this.shield = 0;
    this.shieldCap = 0;
    this.shatterPower = 0;
    this.blasts.length = 0;
    this.storms.length = 0;
    this.eliteTimer = ELITE_FIRST;
    this.pendingChests = 0;
    this.shieldG.clear();

    this.enemies.clear();
    this.projectiles.clear();
    this.gems.clear();
    this.fields.clear();
    this.shots.clear();
    for (const e of this.enemies.items) {
      e.sprite.visible = false;
      e.shadow.visible = false;
      e.gild.visible = false;
      e.brain = null;
    }
    for (const c of this.corpses) {
      c.life = 0;
      c.sprite.visible = false;
    }
    for (const f of this.fxSprites) {
      f.spec = null;
      f.sprite.visible = false;
    }
    for (const g of this.ghosts) {
      g.life = 0;
      g.body.visible = false;
      g.glow.visible = false;
    }
    this.resetStamps();
    this.warnG.clear();
    this.charmT = 0;
    this.charmImmune = 0;
    this.charmSrc = null;
    this.struggleSector = -1;
    this.struggleT = 0;
    this.charmFx.reset();
    // 적 번호도 판마다 1 부터 — 요화가 도는 쪽(uid 홀짝)이 앞 판의 적 수에 따라 달라져
    // 같은 시드가 다른 판이 됐다(1.22 밸런스 마무리 때 찾음)
    this.uidCounter = 1;
    this.healWindow = 0;
    this.healedThisSecond = 0;
    this.playerSprite.rotation = 0;
    for (const s of this.shots.items) s.sprite.visible = false;
    for (const p of this.projectiles.items) p.sprite.visible = false;
    for (const g of this.gems.items) g.sprite.visible = false;
    for (const f of this.fields.items) f.sprite.visible = false;
    for (const b of this.bursts) b.sprite.visible = false;
    for (const b of this.bolts) {
      b.life = 0;
      b.g.visible = false;
    }
    for (const f of this.strikeFx) f.sprite.visible = false;
    this.flashT = 0;
    this.flash.visible = false;

    for (const w of this.wounds) this.removeWound(w);
    this.wounds.length = 0;
    for (const p of this.palmFx) p.sprite.visible = false;
    this.form = null;
    this.darkenT = 0;
    this.auraOn = false;
    this.auraK = 0;
    this.auraGlow.visible = false;
    this.embers.length = 0;
    this.emberG.clear();
    this.coolT = 0;
    this.slumpT = 0;
    this.popT = 0;
    this.sayT = 0;
    this.sayText.visible = false;
    // 이 캐릭터의 그림으로 (없으면 도형)
    const art = this.charArt.get(ch.id);
    this.animator = art ? this.animatorObj : null;
    this.atlas = art?.atlas ?? null;
    this.baseAnim = art?.anim ?? null;
    this.forms = art?.forms ?? new Map();
    if (this.animator) {
      if (this.atlas && this.baseAnim) this.animator.setForm(this.atlas, this.baseAnim);
      this.animator.revive();
      this.baseScale = 1;
      this.playerSprite.tint = 0xffffff;
    } else {
      const pv = resolveVisual(ch.sprite, ch.shape, ch.color);
      this.playerSprite.texture = pv.texture;
      this.playerSprite.tint = pv.tint;
      this.baseScale = (CONFIG.PLAYER_RADIUS * 1.35) / pv.baseRadius;
    }
    this.playerSprite.scale.set(this.baseScale);
    this.playerSprite.alpha = 1;
    // 시작 무기 이펙트를 첫 프레임 전에 굽는다(첫 프레임에 굽느라 20ms 멈칫했다)
    for (const w of this.weapons) w.prepareFx(this);

    this.input.reset();
    this.setState('playing');
  }

  private setState(s: GameState): void {
    this.state = s;
    this.cb.onStateChange(s);
  }

  pause(): void {
    if (this.state === 'playing') this.setState('paused');
  }

  resume(): void {
    if (this.state === 'paused') {
      this.input.reset();
      this.setState('playing');
    }
  }

  quitToMenu(): void {
    this.setState('idle');
  }

  /** 적 탄 하나 (1.22 — 장풍 · 하트 · 불구슬처럼 그림이 있고, 휘거나 매혹을 걸거나 쏜 적 둘레를 돌다 날아간다) */
  private enemyShot(spec: ShotSpec): void {
    const s = this.shots.spawn();
    if (!s) return;
    s.x = spec.x;
    s.y = spec.y;
    s.vx = spec.vx;
    s.vy = spec.vy;
    s.damage = spec.damage;
    s.life = spec.life;
    s.radius = spec.radius;
    s.reflected = false;
    s.charm = spec.charm ?? 0;
    s.src = spec.src ?? null;
    s.srcUid = spec.src?.uid ?? 0;
    s.homing = spec.homing ?? 0;
    s.hitFx = spec.hitFx ?? '';
    s.spin = spec.spin ?? 0;
    s.upright = !!spec.upright;
    s.orbit = spec.orbit ?? 0;
    s.orbitR = spec.orbitR ?? 0;
    s.orbitA = spec.orbitA ?? 0;
    s.orbitW = spec.orbitW ?? 0;
    s.speed = spec.speed ?? 0;
    s.cx = spec.x;
    s.cy = spec.y;
    const art = spec.art ? fxArt(spec.art) : null;
    s.frames = art && art.frames.length > 1 ? art.frames : null;
    s.animT = Math.random();
    const sp = s.sprite;
    sp.texture = art ? art.frames[0] : this.shotTex;
    sp.blendMode = art?.add ? 'add' : 'normal';
    sp.tint = 0xffffff;
    sp.alpha = 1;
    sp.scale.set(spec.scale ?? 1);
    sp.rotation = s.upright || s.spin ? 0 : Math.atan2(s.vy, s.vx);
    if (s.orbit > 0) {
      s.x = s.cx + Math.cos(s.orbitA) * s.orbitR;
      s.y = s.cy + Math.sin(s.orbitA) * s.orbitR * 0.8;
    }
    sp.position.set(Math.round(s.x), Math.round(s.y));
    sp.visible = true;
  }

  /** 원거리 적(권귀 장풍 · 요화 하트)이 쏜다. big = 모아서 쏜 큰 장풍 */
  private fireEnemyShot(e: Enemy, charm: boolean, big: boolean): void {
    const hx = e.x + e.face * (e.art ? 18 : 0);
    const hy = e.y - (e.art ? 8 : 0);
    const dx = this.px - hx;
    const dy = this.py - hy;
    const sp = (e.def.shotSpeed ?? 250) * (big ? 0.8 : 1);
    const dmg = (e.def.shotDamage ?? 8) * CONFIG.enemyDamageScale(this.elapsed);
    e.shots++;
    const base = Math.atan2(dy, dx);
    // 1.23 잡몹 단계: 요화는 3단계부터 하트 둘(5단계 셋), 권귀는 2단계부터 큰 장풍이 부채꼴 셋 · 4단계부터 작은 장풍도 둘
    const fan = (n: number, gap: number): number[] => Array.from({ length: n }, (_, k) => base + (k - (n - 1) / 2) * gap);
    if (charm) {
      for (const a of fan(e.tier >= 5 ? 3 : e.tier >= 3 ? 2 : 1, 0.24)) {
        this.enemyShot({
          x: hx, y: hy - 14, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, damage: dmg, radius: 10, life: 3.6,
          art: 'heart_shot', scale: 0.75, charm: e.def.charmTime ?? 1.6, src: e, homing: 0.4, upright: true,
        });
      }
      if (e.art) audio.play('chime', { rate: 1.45, gain: 0.45 });
      return;
    }
    const gale = e.def.shot === 'gale';
    const n = big ? (e.tier >= 2 ? 3 : 1) : e.tier >= 4 ? 2 : 1;
    for (const a of fan(n, big ? 0.28 : 0.16)) {
      this.enemyShot({
        x: hx, y: hy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, damage: dmg * (big ? 1.5 : 1), radius: big ? 11 : 7, life: 3.2,
        art: gale ? (big ? 'gale_big' : 'gale_fly') : null, scale: big ? 0.62 : 0.55, hitFx: gale ? 'gale_hit' : '',
      });
    }
    if (gale) audio.play('palm', { rate: big ? 0.75 : 1.25, gain: big ? 0.8 : 0.45 });
  }

  private updateEnemyShots(dt: number): void {
    const items = this.shots.items;
    for (let i = 0; i < this.shots.count; i++) {
      const s = items[i];
      const src = s.src && !s.src.dead && s.src.uid === s.srcUid ? s.src : null;
      if (s.orbit > 0) {
        // 쏜 적 둘레를 돌다가 바깥으로 (공손찬 불구슬)
        s.orbit -= dt;
        if (src) {
          s.cx = src.x;
          s.cy = src.y - 60;
        }
        s.orbitA += s.orbitW * dt;
        s.x = s.cx + Math.cos(s.orbitA) * s.orbitR;
        s.y = s.cy + Math.sin(s.orbitA) * s.orbitR * 0.8;
        if (s.orbit <= 0) {
          s.vx = Math.cos(s.orbitA) * s.speed;
          s.vy = Math.sin(s.orbitA) * s.speed;
        }
      } else {
        if (s.homing > 0 && !s.reflected) {
          // 플레이어 쪽으로 조금씩 휜다 (하트)
          const want = Math.atan2(this.py - 12 - s.y, this.px - s.x);
          const cur = Math.atan2(s.vy, s.vx);
          const m = s.homing * dt;
          const da = Math.max(-m, Math.min(m, Math.atan2(Math.sin(want - cur), Math.cos(want - cur))));
          const v = Math.hypot(s.vx, s.vy);
          s.vx = Math.cos(cur + da) * v;
          s.vy = Math.sin(cur + da) * v;
        }
        s.x += s.vx * dt;
        s.y += s.vy * dt;
      }
      s.life -= dt;
      const sp = s.sprite;
      sp.position.set(Math.round(s.x), Math.round(s.y));
      if (s.frames) {
        s.animT += dt;
        sp.texture = s.frames[Math.floor(s.animT * 8) % s.frames.length];
      }
      if (s.spin) sp.rotation += s.spin * dt;
      else if (s.upright) sp.scale.x = Math.abs(sp.scale.x) * (s.vx < 0 ? -1 : 1);
      else if (!s.reflected && s.orbit <= 0) sp.rotation = Math.atan2(s.vy, s.vx);
      sp.alpha = s.life < 0.25 ? Math.max(0, s.life / 0.25) : 1;

      const dx = this.px - s.x;
      const dy = this.py - s.y;
      const rr = CONFIG.PLAYER_RADIUS + s.radius;
      let gone = s.life <= 0;
      if (s.reflected) {
        // 기사의 검에 되받아친 탄: 처음 닿는 적을 맞힌다
        const near = this.hash.query(s.x, s.y, 12 + this.maxEnemyRadius, this.scratch4);
        for (let k = 0; k < near.length && !gone; k++) {
          const j = near[k];
          if (j >= this.enemies.count) continue;
          const e = this.enemies.items[j];
          if (e.dead) continue;
          const ex = e.x - s.x;
          const ey = e.y - s.y;
          const rr2 = e.radius + 8;
          if (ex * ex + ey * ey > rr2 * rr2) continue;
          this.damageEnemy(e, s.damage, s.vx, s.vy, 120);
          this.spawnBurst(s.x, s.y, 24, 'steel', 'spark', RAMPS.white);
          gone = true;
        }
      } else if (!gone && dx * dx + dy * dy < rr * rr) {
        this.hurtPlayer(s.damage);
        if (s.charm > 0) this.applyCharm(src, s.charm);
        if (s.hitFx) this.spawnFxSprite({ name: s.hitFx, x: s.x, y: s.y, life: 0.32, scale: 0.55, scale1: 0.85 });
        gone = true;
      }
      if (gone) {
        s.sprite.visible = false;
        this.shots.removeAt(i);
        i--;
      }
    }
  }

  /** 죽을 때 벌어지는 일 — 쪼개지거나 터지거나. */
  private onEnemyDeath(e: Enemy): void {
    if (e.elite) this.dropChest(e.x, e.y);
    const ai = e.def.ai;
    if (ai === 'splitter' && !e.noSplit) {
      // 1.23 3단계부터 하나 더, 5단계부터 둘 더
      const n = (e.def.splitCount ?? 2) + (e.tier >= 3 ? 1 : 0) + (e.tier >= 5 ? 1 : 0);
      const into = e.def.splitInto ? this.data.enemies.find((d) => d.id === e.def.splitInto) : undefined;
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + this.rng.next();
        if (into) this.spawnSplit(into, e, a);
        else this.spawnChild(e, e.x + Math.cos(a) * 18, e.y + Math.sin(a) * 18);
      }
    } else if (ai === 'exploder') {
      // 1.23 3단계부터 터지는 자리가 넓다
      const r = (e.def.deathRadius ?? 90) * (e.tier >= 3 ? 1.15 : 1);
      this.spawnBurst(e.x, e.y, r, 'red');
      if (e.art) this.spawnBurst(e.x, e.y - 6, r * 0.7, 'red', 'fire', FX_RAMPS.flame, 0.04);
      const dx = this.px - e.x;
      const dy = this.py - e.y;
      if (dx * dx + dy * dy < r * r) {
        this.hurtPlayer((e.def.deathDamage ?? 18) * CONFIG.enemyDamageScale(this.elapsed));
      }
    }
  }

  /** 1.22 왕미물: 죽으면 다른 적(미물) 여럿이 튀어나온다 — 크기·체력은 그 적 그대로 */
  private spawnSplit(def: EnemyDef, parent: Enemy, a: number): void {
    const e = this.enemies.spawn();
    if (!e) return;
    this.initEnemy(e, def, false, 1);
    e.x = parent.x + Math.cos(a) * 14;
    e.y = parent.y + Math.sin(a) * 14;
    // 옛 알주머니 새끼만큼: 체력 34%, 조금 작게. 튀어나오는 힘은 약하게
    e.vx = Math.cos(a) * 160;
    e.vy = Math.sin(a) * 160;
    e.maxHp *= 0.34;
    e.hp = e.maxHp;
    e.drawScale = 0.8;
    e.radius = def.radius * 0.8;
    // 경험치도 옛 새끼처럼 어미의 40% (미물 그대로면 3 이라 레벨이 늦었다)
    e.xp = Math.max(1, Math.round(parent.def.xp * 0.4));
    e.noSplit = true;
    this.applyTier(e, parent.tier);
    e.sprite.position.set(Math.round(e.x), Math.round(e.y));
  }

  /** 쪼개져 나온 새끼. 작고 약하고 또 쪼개지지 않는다. */
  private spawnChild(parent: Enemy, x: number, y: number): void {
    const e = this.enemies.spawn();
    if (!e) return;
    this.initEnemy(e, parent.def, false, 1);
    e.x = x;
    e.y = y;
    e.maxHp = parent.maxHp * 0.34;
    e.hp = e.maxHp;
    e.radius = parent.def.radius * 0.62;
    e.xp = Math.max(1, Math.round(parent.def.xp * 0.4));
    e.noSplit = true;
    e.drawScale = 0.62;
    e.sprite.scale.set(0.62);
    e.speed = parent.speed * 1.25;
    e.sprite.position.set(Math.round(x), Math.round(y));
  }

  /** [감전] 연쇄를 실제로 터뜨린다. 순회가 모두 끝난 뒤에만 부른다. */
  private resolveShocks(): void {
    while (this.pendingShocks.length > 0) {
      const s = this.pendingShocks.pop()!;
      const near = this.hash.query(s.x, s.y, ST.SHOCK_RADIUS + this.maxEnemyRadius, this.scratch2);
      let hit = 0;
      for (let k = 0; k < near.length && hit < 3; k++) {
        const i = near[k];
        if (i >= this.enemies.count) continue;
        const e = this.enemies.items[i];
        if (e.dead || e.uid === s.uid) continue;
        const dx = e.x - s.x;
        const dy = e.y - s.y;
        if (dx * dx + dy * dy > ST.SHOCK_RADIUS * ST.SHOCK_RADIUS) continue;
        this.damageEnemy(e, s.dmg, dx, dy, 40);
        hit++;
      }
    }
  }

  /**
   * 전류: 가장 가까운 적을 때리고 거기서 다시 옮겨붙는다. 같은 적은 두 번 안 맞는다.
   * 피해는 한 번에 들어가지만, 선은 한 칸씩 늦게 보여서 옮겨붙는 게 눈으로 따라가진다.
   */
  chainDamage(spec: ChainSpec): number {
    const seen = spec.exclude;
    let cx = spec.x;
    let cy = spec.y;
    let hits = 0;
    for (let j = 0; j < spec.jumps; j++) {
      // 첫 대상은 시전자에게서 firstRange 안, 그 뒤로는 옮겨붙는 거리 안
      const range = j === 0 ? spec.firstRange : spec.jumpRange;
      const near = this.hash.query(cx, cy, range + this.maxEnemyRadius, this.scratch3);
      let best: Enemy | null = null;
      let bestD = Infinity;
      for (let k = 0; k < near.length; k++) {
        const i = near[k];
        if (i >= this.enemies.count) continue;
        const e = this.enemies.items[i];
        if (e.dead || seen.has(e.uid)) continue;
        const d = (e.x - cx) ** 2 + (e.y - cy) ** 2;
        if (d < bestD && d <= range * range) {
          bestD = d;
          best = e;
        }
      }
      if (!best) break;
      seen.add(best.uid);
      this.damageEnemy(best, spec.damage, best.x - cx, best.y - cy, spec.knockback);
      this.applyStatus(best, spec.status);
      const delay = j * 0.028;
      if (spec.splash > 0) {
        this.areaDamage(best.x, best.y, spec.splash, spec.damage * 0.4, spec.knockback * 0.5, 0, 0, -1, 0, spec.status);
        this.spawnBurst(best.x, best.y, spec.splash, 'yellow', spec.splashStyle, spec.splashRamp, delay);
      }
      this.spawnBolt(cx, cy, best.x, best.y, spec.style, 0xffffff, delay, spec.bolt);
      cx = best.x;
      cy = best.y;
      hits++;
    }
    return hits;
  }

  /**
   * 여제의 명령: 번개가 한 번에 내리꽂힌다.
   * 시작점에서 dir 방향 length 까지, 폭 halfWidth 의 띠 안에 걸친 적은 전부 맞는다.
   */
  lightningStrike(spec: StrikeSpec): number {
    const { x, y, dirX, dirY, length, halfWidth } = spec;
    const hits = this.bandDamage(x, y, dirX, dirY, length, halfWidth, spec.damage, spec.knockback, spec.status);
    this.spawnStrikeFx(x, y, dirX, dirY, length, halfWidth);
    this.shake(0.16, 6);
    this.flashFx(0.12, 0.12, 0xfff6d8);
    if (spec.storm && spec.storm > 0) {
      this.storms.push({
        x, y, dirX, dirY, length, halfWidth, life: spec.storm, tick: 0.15,
        damage: spec.damage * 0.06, link: spec.damage * 0.12,
      });
    }
    return hits;
  }

  /**
   * 여제의 번개가 지나간 자리: 전기장이 잠깐 남아 계속 지지고,
   * 그 안에서 [자기장](기절)에 묶인 적들은 서로 전류로 이어져 함께 맞는다.
   */
  private updateStorms(dt: number): void {
    for (let i = 0; i < this.storms.length; i++) {
      const b = this.storms[i];
      b.life -= dt;
      b.tick -= dt;
      if (b.tick <= 0) {
        b.tick = 0.3;
        this.bandDamage(b.x, b.y, b.dirX, b.dirY, b.length, b.halfWidth, b.damage, 0, { shock: 1 });
        // 띠 위에 지지직 — 가로지르는 짧은 정전기 두 가닥
        for (let k = 0; k < 2; k++) {
          const u = this.rng.next() * b.length;
          const cx = b.x + b.dirX * u;
          const cy = b.y + b.dirY * u;
          const w = b.halfWidth * 1.2;
          this.spawnBolt(cx - b.dirY * w, cy + b.dirX * w, cx + b.dirY * w, cy - b.dirX * w, 'static', 0xffffff, k * 0.05);
        }
        // 묶인 적끼리 잇는다 (띠를 따라 차례로, 열 줄까지)
        const linked: Enemy[] = [];
        const items = this.enemies.items;
        for (let j = 0; j < this.enemies.count && linked.length < 11; j++) {
          const e = items[j];
          if (e.dead || e.stun <= 0) continue;
          const rx = e.x - b.x;
          const ry = e.y - b.y;
          const along = rx * b.dirX + ry * b.dirY;
          if (along < 0 || along > b.length) continue;
          if (Math.abs(rx * -b.dirY + ry * b.dirX) > b.halfWidth * 2.5) continue;
          linked.push(e);
        }
        linked.sort((a, c) => (a.x - b.x) * b.dirX + (a.y - b.y) * b.dirY - ((c.x - b.x) * b.dirX + (c.y - b.y) * b.dirY));
        for (let k = 1; k < linked.length; k++) {
          const a = linked[k - 1];
          const c = linked[k];
          this.spawnBolt(a.x, a.y, c.x, c.y, 'static', 0xffffff, k * 0.02);
          this.damageEnemy(c, b.link, 0, 0, 0);
        }
      }
      if (b.life <= 0) {
        this.storms.splice(i, 1);
        i--;
      }
    }
  }

  private spawnStrikeFx(x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number): void {
    const art = this.lightningArt;
    if (!art) {
      // 그림이 없으면 굵은 선 번개로 대신한다
      this.spawnBolt(x, y, x + dirX * length, y + dirY * length, 'arc');
      return;
    }
    let fx = this.strikeFx.find((f) => !f.sprite.visible);
    if (!fx) {
      if (this.strikeFx.length >= 8) return;
      const sp = new Sprite(art.texture);
      sp.blendMode = 'add';
      this.fxLayer.addChild(sp);
      fx = { sprite: sp, t: 0 };
      this.strikeFx.push(fx);
    }
    const sp = fx.sprite;
    sp.texture = art.texture;
    sp.anchor.set(art.anchorX, art.anchorY);
    const k = length / art.length;
    // 폭은 판정 폭에 맞추되 너무 가늘거나 뚱뚱해지지 않게
    const ky = Math.max(k * 0.7, Math.min(k * 1.4, halfWidth / art.halfWidth));
    sp.scale.set(k, (Math.random() < 0.5 ? -1 : 1) * ky);
    sp.rotation = Math.atan2(dirY, dirX);
    sp.position.set(Math.round(x), Math.round(y));
    sp.alpha = 1;
    sp.visible = true;
    fx.t = 0;
  }

  /** 떨어진 번개: 번쩍 → 잠깐 떨림 → 서서히 투명해지며 사라진다 */
  private updateStrikeFx(dt: number): void {
    for (const f of this.strikeFx) {
      if (!f.sprite.visible) continue;
      f.t += dt;
      const t = f.t;
      if (t < 0.12) f.sprite.alpha = 0.8 + Math.random() * 0.2;
      else if (t < 0.8) f.sprite.alpha = Math.pow(1 - (t - 0.12) / 0.68, 1.6);
      else f.sprite.visible = false;
    }
    if (this.flashT > 0) {
      this.flashT -= dt;
      this.flash.visible = this.flashT > 0;
      this.flash.alpha = Math.max(0, this.flashT / this.flashMax) * this.flashAlpha;
      this.flash.width = this.app.renderer.width;
      this.flash.height = this.app.renderer.height;
    }
  }

  /**
   * 칼날이 a0 → a1 로 쓸고 지나간 부채꼴 안의 적을 벤다(한 번 휘두를 때 한 번만).
   * 적의 몸 크기만큼 각도에 여유를 줘서, 칼끝이 스치기만 해도 맞는다.
   */
  sweepDamage(
    x: number, y: number, a0: number, a1: number, radius: number,
    damage: number, knockback: number, status: StatusApply, seen: Set<number>,
  ): number {
    const lo = Math.min(a0, a1);
    const span = Math.abs(a1 - a0);
    const TAU = Math.PI * 2;
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch);
    let hits = 0;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead || seen.has(e.uid)) continue;
      const dx = e.x - x;
      const dy = e.y - y;
      const d = Math.hypot(dx, dy);
      if (d > radius + e.radius) continue;
      if (d > 18) {
        const margin = Math.min(0.6, e.radius / d);
        const rel = (((Math.atan2(dy, dx) - lo) % TAU) + TAU) % TAU;
        if (rel > span + margin && rel < TAU - margin) continue;
      }
      seen.add(e.uid);
      this.damageEnemy(e, damage, dx, dy, knockback);
      this.applyStatus(e, status);
      hits++;
    }
    return hits;
  }

  /**
   * radius 안의 적을 toRadius 근처까지 끌어당긴다(보스는 안 끌려온다).
   * 넉백 속도는 매 스텝 0.86배로 줄어서 총 이동 거리 = 속도 × 0.119 — 거꾸로 풀어 속도를 정한다.
   */
  pullEnemies(x: number, y: number, radius: number, toRadius: number): number {
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch);
    let n = 0;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead || e.boss) continue;
      const dx = e.x - x;
      const dy = e.y - y;
      const d = Math.hypot(dx, dy);
      if (d > radius || d <= toRadius) continue;
      const v = Math.min(2200, (d - toRadius) / 0.119);
      e.vx -= (dx / d) * v;
      e.vy -= (dy / d) * v;
      n++;
    }
    return n;
  }

  liftEnemies(x: number, y: number, radius: number, height: number, dur: number, turns: number, cd: number): number {
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch);
    let n = 0;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      // 가벼운 것만 뜬다 — 덩치 큰 것(털괴·왕미물·거상)이 빙글빙글 날아가면 우스꽝스럽다. 단계가 올라 커져도 종류로 가른다
      if (e.dead || e.boss || e.elite || e.def.radius > LIFT_MAX_RADIUS) continue;
      const rr = radius + e.radius;
      if ((e.x - x) ** 2 + (e.y - y) ** 2 > rr * rr) continue;
      if (cd > 0 && (e.airCd > 0 || e.airT > 0)) continue;
      // 이미 떠 있으면 더 높이·더 오래 다시 띄운다(조화가 회오리보다 세다)
      if (e.airT > 0 && height <= e.airH) continue;
      e.airT = dur;
      e.airDur = dur;
      e.airH = height;
      e.airTurns = turns;
      e.airCd = Math.max(e.airCd, cd);
      e.stun = Math.max(e.stun, dur);
      n++;
    }
    return n;
  }

  /**
   * 전류가 튄 자리를 잠깐 보여주는 선.
   *  arc    — 전류: 굵고 노란 번개. 한 번 꺾인 모양이 그대로 남는다.
   *  static — 감전: 가늘고 흰 두 가닥에 보랏빛. 사는 동안 모양이 바뀌며 떨고, 맞은 자리에 불똥이 튄다.
   *  streak — 순간이동 잔상: 곧은 굵은 줄
   */
  private spawnBolt(
    x0: number, y0: number, x1: number, y1: number, style: BoltStyle, color = 0xffffff, delay = 0, scale = 1,
  ): void {
    let b = this.bolts.find((v) => v.life <= 0 && v.wait <= 0);
    if (!b) {
      if (this.bolts.length >= 64) return;
      const g = new Graphics();
      this.fxLayer.addChild(g);
      b = { g, life: 0, max: 0, x0: 0, y0: 0, x1: 0, y1: 0, style: 'arc', color: 0xffffff, redrawn: false, wait: 0, scale: 1 };
      this.bolts.push(b);
    }
    b.wait = delay;
    b.x0 = x0;
    b.y0 = y0;
    b.x1 = x1;
    b.y1 = y1;
    b.style = style;
    b.color = color;
    b.scale = scale;
    // 굵은 번개는 조금 더 오래 남는다
    b.max = style === 'arc' || style === 'violet' ? 0.16 + 0.05 * Math.max(0, scale - 1) : style === 'static' ? 0.12 : style === 'beam' ? 0.34 : 0.24;
    b.life = b.max;
    b.redrawn = false;
    b.g.visible = delay <= 0;
    b.g.alpha = 1;
    this.drawBolt(b);
  }

  private drawBolt(b: BoltLine): void {
    const g = b.g;
    g.clear();
    const len = Math.hypot(b.x1 - b.x0, b.y1 - b.y0);
    const rnd = Math.random;
    const line = (pts: Array<[number, number]>, width: number, color: number, alpha = 1): void => {
      g.moveTo(Math.round(pts[0][0]), Math.round(pts[0][1]));
      for (let i = 1; i < pts.length; i++) g.lineTo(Math.round(pts[i][0]), Math.round(pts[i][1]));
      g.stroke({ width, color, alpha, cap: 'round', join: 'round' });
    };
    if (b.style === 'streak') {
      const pts: Array<[number, number]> = [[b.x0, b.y0], [b.x1, b.y1]];
      line(pts, 10, b.color, 0.35);
      line(pts, 4, b.color, 0.8);
      line(pts, 1.5, 0xffffff, 1);
      return;
    }
    if (b.style === 'beam') {
      // 메자이의 황금 광선: 하늘에서 곧게 내리꽂히는 굵은 기둥
      const pts: Array<[number, number]> = [[b.x0, b.y0], [b.x1, b.y1]];
      line(pts, 22, 0xd9a94a, 0.3);
      line(pts, 10, 0xffe39a, 0.85);
      line(pts, 3, 0xffffff, 1);
      g.circle(Math.round(b.x1), Math.round(b.y1), 16).stroke({ width: 3, color: 0xffe39a, alpha: 0.9 });
      return;
    }
    if (b.style === 'arc' || b.style === 'violet') {
      // 전류: 노란 빛 → 밝은 노랑 → 흰 심지. scale(bolt 스탯)만큼 굵어지고 크게 꺾인다.
      // violet: 번개의 정령왕 — 같은 모양을 보랏빛으로
      const s = b.scale;
      const [glow, mid, core] = b.style === 'violet' ? [0x6a4bc9, 0xa98bff, 0xf4eeff] : [0xffc21f, 0xffe95c, 0xfffce0];
      const pts = jagged(b.x0, b.y0, b.x1, b.y1, Math.min(18 * s, len * 0.14 * Math.min(s, 1.8)), s >= 2 ? 4 : 3, rnd);
      line(pts, 8 * s, glow, 0.4);
      line(pts, 3.5 * s, mid, 1);
      line(pts, 1.5 * s, core, 1);
      // 굵은 번개는 옆으로 가지가 뻗는다
      if (s >= 2) {
        for (let k = 1; k < pts.length - 1; k++) {
          if (rnd() < 0.45) continue;
          const [px, py] = pts[k];
          const a = Math.atan2(b.y1 - b.y0, b.x1 - b.x0) + (rnd() < 0.5 ? -1 : 1) * (0.5 + rnd() * 0.6);
          const l = (10 + rnd() * 14) * s;
          const fork = jagged(px, py, px + Math.cos(a) * l, py + Math.sin(a) * l, 4 * s, 2, rnd);
          line(fork, 3 * s, glow, 0.45);
          line(fork, 1.2 * s, core, 1);
        }
      }
      return;
    }
    // static: 서로 다르게 꺾인 가는 두 가닥 + 맞은 자리의 작은 불똥
    for (let k = 0; k < 2; k++) {
      const pts = jagged(b.x0, b.y0, b.x1, b.y1, Math.min(10, len * 0.2), 4, rnd);
      line(pts, 5, 0xa98bff, 0.5);
      line(pts, 2, 0xe2d4ff, 1);
      line(pts, 1, 0xffffff, 1);
    }
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + rnd() * 0.8;
      const l = 5 + rnd() * 4;
      g.moveTo(Math.round(b.x1), Math.round(b.y1)).lineTo(Math.round(b.x1 + Math.cos(a) * l), Math.round(b.y1 + Math.sin(a) * l));
      g.stroke({ width: 2, color: 0xe2d4ff, alpha: 1 });
    }
  }

  private updateBolts(dt: number): void {
    for (const b of this.bolts) {
      if (b.wait > 0) {
        b.wait -= dt;
        if (b.wait > 0) continue;
        b.g.visible = true;
      }
      if (b.life <= 0) continue;
      b.life -= dt;
      if (b.life <= 0) {
        b.g.visible = false;
        continue;
      }
      const k = b.life / b.max;
      b.g.alpha = b.style === 'arc' || b.style === 'violet' || b.style === 'beam' ? Math.min(1, k * 1.6) : k;
      // 감전은 한 번 모양을 바꿔서 지지직 떠는 느낌을 준다
      if (b.style === 'static' && !b.redrawn && k < 0.55) {
        b.redrawn = true;
        this.drawBolt(b);
      }
    }
  }

  streak(x0: number, y0: number, x1: number, y1: number, color: number): void {
    this.spawnBolt(x0, y0, x1, y1, 'streak', color);
  }

  playAttackPose(kind: 'attack' | 'cast'): void {
    this.animator?.trigger(kind);
  }

  private finish(): void {
    this.animator?.kill();
    // 변신한 채 쓰러지면 불꽃 테두리·불티·머리 위 한마디는 걷는다 (결과 화면 뒤에 멈춘 채 남지 않게)
    this.auraK = 0;
    this.auraGlow.visible = false;
    this.embers.length = 0;
    this.emberG.clear();
    this.sayText.visible = false;
    this.playerSprite.tint = 0xffffff;
    const survival = Math.floor(this.elapsed);
    const result: RunResult = {
      characterId: this.character.id,
      survivalSec: survival,
      kills: this.kills,
      level: this.level,
      score: computeScore(survival, this.kills, this.level),
      bossesDown: this.bossesDown,
    };
    this.lastScore = result.score;
    audio.play('dead');
    this.setState('over');
    this.cb.onOver(result);
  }

  // ── 메인 루프 ──────────────────────────────────────────

  private tick = (): void => {
    const ms = this.app.ticker.deltaMS;
    this.fps = this.fps * 0.9 + (1000 / Math.max(ms, 1)) * 0.1;

    this.resizeBg();

    if (this.state === 'playing') {
      const real = Math.min(ms / 1000, 0.25);
      if (this.hitstopCd > 0) this.hitstopCd -= real;
      if (this.hitstopT > 0) {
        // 무거운 한 방: 아주 잠깐 멈춘다 (시뮬레이터는 step 을 직접 돌리므로 판에는 영향 없다)
        this.hitstopT -= real;
      } else {
        let scale = 1;
        if (this.slowT > 0) {
          this.slowT -= real;
          // 끝 무렵에는 원래 속도로 부드럽게 돌아온다
          scale = this.slowScale + (1 - this.slowScale) * Math.max(0, 1 - this.slowT / 0.25) * (this.slowT < 0.25 ? 1 : 0);
        }
        this.accumulator += real * scale;
      }
      let steps = 0;
      while (this.accumulator >= CONFIG.FIXED_DT && steps < CONFIG.MAX_STEPS_PER_FRAME) {
        this.step(CONFIG.FIXED_DT);
        this.accumulator -= CONFIG.FIXED_DT;
        steps++;
        if (this.state !== 'playing') break; // 죽거나 레벨업하면 즉시 중단
      }
      if (steps >= CONFIG.MAX_STEPS_PER_FRAME) this.accumulator = 0;
    } else {
      this.accumulator = 0;
    }

    this.updateCamera();
    if (this.state !== 'idle') this.cb.onHud(this.snapshot());
  };

  private resizeBg(): void {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    if (this.bg.width !== w || this.bg.height !== h) {
      this.bg.width = w;
      this.bg.height = h;
    }
  }

  /** 고정 스텝 1회. 시뮬레이터가 직접 호출할 수 있게 공개해 둔다. */
  step(dt: number): void {
    this.landPuffs = 0;
    this.elapsed += dt;
    this.healWindow -= dt;
    if (this.healWindow <= 0) {
      this.healWindow = 1;
      this.healedThisSecond = 0;
    }
    this.input.update();

    this.updatePlayer(dt);
    this.updateSpawner(dt);
    this.updateMapPickups(dt);

    this.rebuildHash();

    this.updateEnemies(dt);
    this.bodyScaleNext = 1;
    this.shieldCapNext = 0;
    let shatter = 0;
    for (const w of this.weapons) shatter = Math.max(shatter, w.stats.shatter);
    this.shatterPower = shatter;
    for (let i = 0; i < this.weapons.length; i++) this.weapons[i].update(this, dt, i);
    this.bodyScale = this.bodyScaleNext;
    this.updateForm();
    this.updateWounds(dt);
    this.updatePalmFx(dt);
    this.shieldCap = this.shieldCapNext * this.playerStats.maxHp;
    if (this.shield > this.shieldCap) this.shield = this.shieldCap;
    this.updateStorms(dt);
    this.updateProjectiles(dt);
    this.updateEnemyShots(dt);
    this.updateBursts(dt);
    this.updateFxSprites(dt);
    this.updateGhosts(dt);
    this.updateCorpses(dt);
    this.updateBolts(dt);
    this.updateStrikeFx(dt);
    this.updateFields(dt);
    this.updateGems(dt);
    this.resolveShocks();
    this.resolveBlasts();
    this.sweepDead();

    if (this.shakeT > 0) this.shakeT -= dt;

    if (this.hp <= 0) {
      this.finish();
      return;
    }
    if (this.pendingLevelUps > 0 || this.pendingChests > 0) {
      const chest = this.pendingLevelUps <= 0;
      if (chest) this.pendingChests--;
      else this.pendingLevelUps--;
      this.activeChoiceSource = chest ? 'chest' : 'level';
      audio.play(chest ? 'chest' : 'levelup');
      this.setState('levelup');
      this.lastChoices = buildChoices(
        this.rng, this.data.weapons, this.data.passives, this.weapons, this.passives,
        this.data.fusions, this.hp / this.playerStats.maxHp, this.character.id,
        this.character.trait?.choices ?? CONFIG.LEVELUP_CHOICES,
      );
      this.prewarmChoices(this.lastChoices);
      this.cb.onLevelUp(this.lastChoices, chest ? 'chest' : 'level');
    }
  }

  // ── 플레이어 ───────────────────────────────────────────

  private updatePlayer(dt: number): void {
    // 1.24 대시 쿨 — 모아 둔 횟수가 덜 찼으면 찬다
    const dsp = this.dashSpec();
    if (this.dashCharges < dsp.charges) {
      this.dashCd -= dt;
      if (this.dashCd <= 0) {
        this.dashCharges++;
        this.dashCd = this.dashCharges < dsp.charges ? dsp.cd : 0;
      }
    }
    // 회광반조로 변신한 동안은 조금 빠르다
    const sp = this.playerStats.moveSpeed * (this.form && this.darkenT <= 0 ? 1 + FORM_SPEED : 1);
    let ix = this.input.dx;
    let iy = this.input.dy;
    if (this.autoPilot) {
      const v = this.autoPilot(this.elapsed);
      ix = v.x;
      iy = v.y;
      this.autoDash(ix, iy); // 자동 조종이 정한 쪽으로 (측정·시뮬레이션에서만)
    }
    if (this.charmImmune > 0) this.charmImmune -= dt;
    if (this.charmT > 0) {
      // 1.22 [매혹]: 조작이 안 되고 홀린 요괴 쪽으로 천천히 걸어간다. 방향을 바꿀 때마다(몸부림) 조금씩 빨리 풀린다
      const src = this.charmSrc && !this.charmSrc.dead && this.charmSrc.uid === this.charmUid ? this.charmSrc : null;
      if (!src) {
        this.endCharm();
      } else {
        this.charmX = src.x;
        this.charmY = src.y;
        this.struggleT -= dt;
        if (ix !== 0 || iy !== 0) {
          const sector = Math.round(Math.atan2(iy, ix) / (Math.PI / 4)) & 7;
          if (sector !== this.struggleSector && this.struggleT <= 0) {
            this.struggleSector = sector;
            this.struggleT = 0.12;
            this.charmT -= CHARM_STRUGGLE;
          }
        } else {
          this.struggleSector = -1;
        }
        const cdx = src.x - this.px;
        const cdy = src.y - this.py;
        const cd = Math.hypot(cdx, cdy) || 1;
        const near = cd < src.radius + 18;
        ix = near ? 0 : (cdx / cd) * CHARM_PULL;
        iy = near ? 0 : (cdy / cd) * CHARM_PULL;
        this.charmT -= dt;
        if (this.charmT <= 0) this.endCharm();
      }
    }
    if (this.dash) {
      // 도약 중: 입력 대신 정해 둔 길을 빠르게 날아간다(끝에서 살짝 멈칫)
      const d = this.dash;
      d.t += dt;
      const u = Math.min(1, d.t / d.dur);
      const e = 1 - (1 - u) * (1 - u);
      this.px = d.x0 + (d.x1 - d.x0) * e;
      this.py = d.y0 + (d.y1 - d.y0) * e;
      ix = d.dirX;
      iy = d.dirY;
      // 1.24 지나가면서 잔상을 흘린다 — 몸이 아직 안 간 자리에 미리 남으면 안 된다
      if (d.ghostT >= 0) {
        d.ghostT -= dt;
        if (d.ghostT <= 0 && u < 0.95) {
          d.ghostT = GHOST_STEP;
          this.spawnGhost(this.px, this.py, !!d.zap, d.zap ? 0.26 : 0.18, d.zap ? 0.45 : 0.3);
        }
      }
      if (u >= 1) {
        this.dash = null;
        if (d.spec) this.landLeap(d);
        else this.landDash(d);
      }
    } else {
      this.px += ix * sp * dt;
      this.py += iy * sp * dt;
    }
    if (ix !== 0 || iy !== 0) {
      const len = Math.hypot(ix, iy) || 1;
      this.faceX = ix / len;
      this.faceY = iy / len;
    }

    // 도트는 좌표가 소수면 매 프레임 떨린다. 반드시 정수로 눕힌다.
    // 몸 크기(타이탄)는 애니메이터보다 먼저 정한다 — 애니메이터는 크기는 두고 좌우 방향만 뒤집는다
    this.playerSprite.scale.set(this.baseScale * this.bodyScale);
    if (this.popT > 0) {
      const u = 1 - this.popT / FORM_POP;
      this.playerSprite.scale.set(this.baseScale * this.bodyScale * (1 + 0.14 * Math.sin(Math.PI * u)));
    } else if (this.slumpT > 0) {
      // 지쳐서 주저앉듯 한 번 낮아졌다가 편다 (발은 그대로 — 바닥 기준 그림)
      const u = 1 - this.slumpT / FORM_SLUMP;
      const k = this.baseScale * this.bodyScale;
      this.playerSprite.scale.set(k * (1 + 0.06 * Math.sin(Math.PI * u)), k * (1 - 0.12 * Math.sin(Math.PI * u)));
    }
    let bob = 0;
    if (this.animator) {
      this.animator.update(dt, ix, iy);
      bob = this.animator.bobOffset;
    }
    this.playerSprite.position.set(
      Math.round(this.px),
      Math.round(this.py) + bob + (this.animator ? FOOT_DROP : 0),
    );
    // 정령왕과 앞뒤를 가린다 — 발이 화면 아래에 있는 쪽이 앞 (보호막은 몸 바로 뒤)
    this.playerSprite.zIndex = Math.round(this.py) + FOOT_DROP;
    this.shieldG.zIndex = this.playerSprite.zIndex - 0.5;

    if (this.iframe > 0) {
      this.iframe -= dt;
      this.playerSprite.alpha = Math.sin(this.elapsed * 40) > 0 ? 0.35 : 1;
    } else {
      this.playerSprite.alpha = 1;
    }

    if (this.playerStats.regen > 0 && this.hp < this.playerStats.maxHp) {
      this.hp = Math.min(this.playerStats.maxHp, this.hp + this.playerStats.regen * dt);
    }
    this.updateFormFx(dt);
    this.drawCharm(dt);
    this.drawShield();
  }

  /** 1.22 [매혹]을 건다 — src 쪽으로 끌려간다. 풀린 뒤 잠깐은 다시 안 걸린다 */
  private applyCharm(src: Enemy | null, time: number): void {
    if (!src || src.dead || time <= 0 || this.charmImmune > 0 || this.dash) return;
    const sp = this.playerSprite;
    if (this.charmT <= 0) {
      this.charmFx.start(this.px, sp.y - (sp.height || 60));
      audio.play('chime', { rate: 0.62, gain: 1.1 });
      this.struggleSector = -1;
    }
    this.charmT = Math.max(this.charmT, time);
    this.charmMax = this.charmT;
    this.charmSrc = src;
    this.charmUid = src.uid;
    this.charmX = src.x;
    this.charmY = src.y;
  }

  private endCharm(): void {
    if (this.charmT > 0 || this.charmSrc) this.charmImmune = CHARM_IMMUNE;
    this.charmT = 0;
    this.charmSrc = null;
  }

  /** 1.22 홀린 몸: 분홍으로 두근두근 · 비틀비틀 · 머리 위 하트 (charmfx.ts) */
  private drawCharm(dt: number): void {
    const sp = this.playerSprite;
    const on = this.charmT > 0;
    const glow = this.charmGlow;
    if (on) {
      const k = 0.35 + 0.2 * Math.sin(this.elapsed * 11);
      sp.tint = mixColor(sp.tint as number, 0xff8cc4, k);
      sp.rotation = Math.sin(this.elapsed * 6) * 0.08;
      // 몸 그림을 분홍으로 한 겹 더해 두근두근
      glow.visible = sp.visible;
      glow.texture = sp.texture;
      glow.anchor.set(sp.anchor.x, sp.anchor.y);
      glow.position.copyFrom(sp.position);
      glow.scale.copyFrom(sp.scale);
      glow.rotation = sp.rotation;
      glow.alpha = (0.28 + 0.2 * Math.sin(this.elapsed * 11)) * sp.alpha;
      glow.zIndex = sp.zIndex + 0.2;
    } else {
      if (sp.rotation !== 0) sp.rotation = 0;
      glow.visible = false;
    }
    this.charmFx.update(dt, on, this.charmT / Math.max(0.01, this.charmMax), this.px, sp.y, sp.y - (sp.height || 60), this.charmX, this.charmY);
  }

  /** 1.22 보스 머리(bosses.ts)가 쓰는 창구 */
  private makeBossHost(): BossHost {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const g = this;
    return {
      get px() {
        return g.px;
      },
      get py() {
        return g.py;
      },
      get rng() {
        return g.rng;
      },
      get warn() {
        return g.warnG;
      },
      get playerCharmed() {
        return g.charmT > 0;
      },
      hurtIfIn: (x, y, r, dmg) => g.hurtIfIn(x, y, r, dmg),
      hurtIfBand: (x, y, dir, len, hw, dmg) => g.hurtIfBand(x, y, dir, len, hw, dmg),
      hurtIfSector: (x, y, r, dir, half, dmg) => g.hurtIfSector(x, y, r, dir, half, dmg),
      enemyShot: (spec) => g.enemyShot(spec),
      fx: (spec) => g.spawnFxSprite(spec),
      stamp: (name, frame, x, y, rot, scale, alpha, sy) => g.stamp(name, frame, x, y, rot, scale, alpha, sy),
      burst: (x, y, r, attr, style, delay) => g.spawnBurst(x, y, r, attr as Attr, style ?? 'ring', undefined, delay ?? 0),
      shakeScreen: (t, mag) => g.shake(t, mag),
      flashScreen: (sec, alpha, tint) => g.flashFx(sec, alpha, tint),
      sound: (name, rate, gain) => audio.play(name as Sfx, { rate: rate ?? 1, gain: gain ?? 1 }),
    };
  }

  /** 플레이어가 원 안이면 때린다 (판정 원을 조금 봐준다) */
  private hurtIfIn(x: number, y: number, r: number, dmg: number): boolean {
    const dx = this.px - x;
    const dy = this.py - y;
    const rr = r + CONFIG.PLAYER_RADIUS * 0.6;
    if (dx * dx + dy * dy > rr * rr) return false;
    this.hurtPlayer(dmg);
    return true;
  }

  /** 띠 안이면 때린다 — (x, y)에서 dir 쪽으로 len, 반폭 hw */
  private hurtIfBand(x: number, y: number, dir: number, len: number, hw: number, dmg: number): boolean {
    const dx = this.px - x;
    const dy = this.py - y;
    const c = Math.cos(dir);
    const s2 = Math.sin(dir);
    const along = dx * c + dy * s2;
    const side = Math.abs(-dx * s2 + dy * c);
    if (along < -CONFIG.PLAYER_RADIUS || along > len + CONFIG.PLAYER_RADIUS * 0.6 || side > hw + CONFIG.PLAYER_RADIUS * 0.6) return false;
    this.hurtPlayer(dmg);
    return true;
  }

  /** 부채꼴 안이면 때린다 */
  private hurtIfSector(x: number, y: number, r: number, dir: number, half: number, dmg: number): boolean {
    const dx = this.px - x;
    const dy = this.py - y;
    const d = Math.hypot(dx, dy);
    if (d > r + CONFIG.PLAYER_RADIUS * 0.6) return false;
    if (d > CONFIG.PLAYER_RADIUS) {
      const a = Math.atan2(dy, dx);
      if (Math.abs(Math.atan2(Math.sin(a - dir), Math.cos(a - dir))) > half) return false;
    }
    this.hurtPlayer(dmg);
    return true;
  }

  /** 1.22 한 번 보여 주고 사라지는 그림 (적 이펙트 아틀라스) */
  private spawnFxSprite(spec: FxSpec): void {
    const art = fxArt(spec.name);
    if (!art) return;
    let f = this.fxSprites.find((q) => !q.spec);
    if (!f) f = this.fxSprites.reduce((a, q) => (a.t / (a.spec?.life ?? 1) >= q.t / (q.spec?.life ?? 1) ? a : q));
    f.spec = spec;
    f.t = 0;
    const sp = f.sprite;
    sp.texture = art.frames[Math.min(art.frames.length - 1, spec.frame ?? 0)];
    sp.blendMode = art.add ? 'add' : 'normal';
    sp.anchor.set(0.5, spec.anchorBottom ? 1 : 0.5);
    sp.tint = spec.tint ?? 0xffffff;
    sp.rotation = spec.rot ?? 0;
    sp.visible = true;
    this.placeFxSprite(f);
  }

  private placeFxSprite(f: FxSprite): void {
    const spec = f.spec!;
    const u = Math.min(1, f.t / spec.life);
    const e = 1 - (1 - u) * (1 - u);
    const k = (spec.scale ?? 1) + ((spec.scale1 ?? spec.scale ?? 1) - (spec.scale ?? 1)) * e;
    const sp = f.sprite;
    sp.scale.set(spec.flipX ? -k : k, k);
    sp.position.set(Math.round(spec.x + (spec.vx ?? 0) * f.t), Math.round(spec.y + (spec.vy ?? 0) * f.t));
    sp.alpha = (spec.alpha ?? 1) * (u < 0.12 ? u / 0.12 : u > 0.6 ? (1 - u) / 0.4 : 1);
  }

  private updateFxSprites(dt: number): void {
    for (const f of this.fxSprites) {
      if (!f.spec) continue;
      f.t += dt;
      if (f.t >= f.spec.life) {
        f.spec = null;
        f.sprite.visible = false;
        continue;
      }
      this.placeFxSprite(f);
    }
  }

  /**
   * 1.24 대시 잔상 한 장. 지금 몸 그림을 그대로 찍어 (x, y) 에 세워 두고 그 자리에서 바래게 한다.
   * 몸은 발밑 기준으로 그려지므로(FOOT_DROP · 걷는 들썩임) 지금 몸 그림이 쓰는 세로 어긋남을 그대로 빌린다.
   */
  private spawnGhost(x: number, y: number, zap: boolean, life: number, a0: number): void {
    const src = this.playerSprite;
    if (!src.texture) return;
    let g = this.ghosts.find((q) => q.life <= 0);
    if (!g) g = this.ghosts.reduce((a, q) => (a.t / a.life >= q.t / q.life ? a : q));
    const i = zap ? 0 : 1;
    const dy = src.position.y - Math.round(this.py);
    for (const sp of [g.body, g.glow]) {
      sp.texture = src.texture;
      sp.anchor.copyFrom(src.anchor);
      sp.scale.set(src.scale.x, src.scale.y);
      sp.position.set(Math.round(x), Math.round(y) + dy);
      sp.visible = true;
    }
    g.body.filters = [this.ghostFilters[i]];
    g.glow.tint = GHOST_COLORS[i].glow;
    g.t = 0;
    g.life = life;
    g.a0 = a0;
    this.fadeGhost(g, 0);
  }

  /** 잔상은 움직이지 않는다 — 그 자리에서 사그라들 뿐 (끝으로 갈수록 빨리 옅어진다) */
  private updateGhosts(dt: number): void {
    for (const g of this.ghosts) {
      if (g.life <= 0) continue;
      g.t += dt;
      if (g.t >= g.life) {
        g.life = 0;
        g.body.visible = false;
        g.glow.visible = false;
        continue;
      }
      this.fadeGhost(g, g.t / g.life);
    }
  }

  /** 잔상 한 장의 진하기 — 빛(glow)이 몸(body)보다 먼저 꺼져서, 번쩍하고 남은 그림자가 스러지듯 보인다 */
  private fadeGhost(g: Ghost, u: number): void {
    const k = (1 - u) * (1 - u);
    g.body.alpha = g.a0 * k;
    g.glow.alpha = g.a0 * k * k;
  }

  /** 1.22 이번 스텝만 보이는 그림 — 스텝이 시작할 때 전부 걷는다 */
  private stamp(name: string, frame: number, x: number, y: number, rot: number, scale: number, alpha: number, sy?: number): void {
    if (this.stampN >= this.stamps.length) return;
    const art = fxArt(name);
    if (!art) return;
    const sp = this.stamps[this.stampN++];
    sp.texture = art.frames[Math.min(art.frames.length - 1, frame)];
    sp.blendMode = art.add ? 'add' : 'normal';
    sp.position.set(Math.round(x), Math.round(y));
    sp.rotation = rot;
    sp.scale.set(scale, sy ?? Math.abs(scale));
    sp.alpha = alpha;
    sp.visible = true;
  }

  private resetStamps(): void {
    for (let i = 0; i < this.stampN; i++) this.stamps[i].visible = false;
    this.stampN = 0;
  }

  /**
   * 변신한 몸의 효과를 매 프레임.
   * - 변신 직전 FORM_IGNITE 초: 몸이 검붉게 물든다 → 다 물들면 변신(beginTransform)
   * - aura 모습(변신 그림이 따로 없는 캐릭터): 몸빛이 검붉고, 몸 그림을 붉게 한 겹 크게 뒤에 둬서 불꽃 테두리처럼
   * - 변신한 동안은 누구나 몸에서 불티가 피어오른다
   * - 풀린 뒤 FORM_COOL 초: 잿빛에서 제 색으로 식는다
   */
  private updateFormFx(dt: number): void {
    const sp = this.playerSprite;
    const lit = this.form !== null && this.darkenT <= 0;
    if (this.darkenT > 0) {
      this.darkenT -= dt;
      if (this.darkenT <= 0) this.beginTransform();
    }
    if (this.popT > 0) this.popT -= dt;
    if (this.slumpT > 0) this.slumpT -= dt;
    if (this.coolT > 0) this.coolT -= dt;
    // 불꽃 테두리는 빨리 붙고(0.25초) 천천히 꺼진다(0.6초)
    const want = lit && this.auraOn ? 1 : 0;
    this.auraK = want >= this.auraK ? Math.min(1, this.auraK + dt / 0.25) : Math.max(0, this.auraK - dt / 0.6);

    if (this.animator) {
      let tint = 0xffffff;
      if (this.darkenT > 0) {
        const k = 1 - Math.max(0, this.darkenT) / FORM_IGNITE;
        tint = mixColor(0xffffff, 0x461212, k);
      } else if (this.coolT > 0) {
        tint = mixColor(0xffffff, 0x8a8480, this.coolT / FORM_COOL);
      } else if (this.auraK > 0) {
        tint = mixColor(0xffffff, 0xffa488, this.auraK);
      }
      sp.tint = tint;
    }

    const g = this.auraGlow;
    g.visible = this.auraK > 0.01 && sp.visible;
    if (g.visible) {
      g.texture = sp.texture;
      g.anchor.set(sp.anchor.x, sp.anchor.y);
      g.position.set(sp.x, sp.y + 1);
      g.scale.set(sp.scale.x * 1.1, sp.scale.y * 1.07);
      g.alpha = this.auraK * (0.4 + 0.14 * Math.sin(this.elapsed * 9) + 0.06 * Math.sin(this.elapsed * 23)) * sp.alpha;
      g.zIndex = sp.zIndex - 0.3;
    }

    // 잿불: 변신한 동안 몸 둘레에서 불티가 피어오른다 (초당 26개)
    if (lit) {
      this.emberAcc += dt * 26;
      const h = sp.height || 60;
      while (this.emberAcc >= 1) {
        this.emberAcc -= 1;
        this.spawnEmber(this.px + (Math.random() - 0.5) * 30, sp.y - Math.random() * h * 0.85, 0);
      }
    } else {
      this.emberAcc = 0;
    }
    this.drawEmbers(dt);

    // 머리 위 한마디
    if (this.sayT > 0) {
      this.sayT -= dt;
      const u = 1 - Math.max(0, this.sayT) / FORM_SAY;
      this.sayText.visible = true;
      this.sayText.position.set(Math.round(this.px), Math.round(sp.y - (sp.height || 60) - 6 - u * 14));
      this.sayText.alpha = u < 0.12 ? u / 0.12 : u > 0.7 ? Math.max(0, (1 - u) / 0.3) : 1;
    } else {
      this.sayText.visible = false;
    }
  }

  /** 머리 위 한마디 글자 그림 — 처음 쓸 때 한 번 캔버스에 두 배 크기로 그린다(글꼴은 게임에 내장한 것) */
  private sayTexture(text: string): Texture {
    const cached = this.sayTex.get(text);
    if (cached) return cached;
    const font = "800 30px 'Google Sans Variable', 'Noto Sans KR Variable', sans-serif";
    const c = document.createElement('canvas');
    let ctx = c.getContext('2d')!;
    ctx.font = font;
    c.width = Math.ceil(ctx.measureText(text).width) + 16;
    c.height = 44;
    ctx = c.getContext('2d')!;
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#1a0606';
    ctx.strokeText(text, 8, 23);
    ctx.fillStyle = '#ffd2b0';
    ctx.fillText(text, 8, 23);
    const tex = Texture.from(c);
    this.sayTex.set(text, tex);
    return tex;
  }

  /** 불티 하나. burst 가 0보다 크면 그 세기로 사방에 흩어진다(풀릴 때). 그림뿐이라 판의 난수(this.rng)는 안 쓴다 — 같은 시드 같은 판 */
  private spawnEmber(x: number, y: number, burst: number): void {
    if (this.embers.length >= 140) return;
    const r = Math.random;
    const a = r() * Math.PI * 2;
    this.embers.push({
      x, y,
      vx: burst > 0 ? Math.cos(a) * burst * (0.4 + r()) : (r() - 0.5) * 18,
      vy: burst > 0 ? Math.sin(a) * burst * (0.4 + r()) - 30 : -36 - r() * 44,
      t: 0,
      life: 0.5 + r() * 0.55,
      grey: burst > 0 && r() < 0.5,
    });
  }

  private drawEmbers(dt: number): void {
    const g = this.emberG;
    g.clear();
    g.zIndex = this.playerSprite.zIndex + 0.4;
    for (let i = this.embers.length - 1; i >= 0; i--) {
      const e = this.embers[i];
      e.t += dt;
      if (e.t >= e.life) {
        this.embers.splice(i, 1);
        continue;
      }
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      e.vx *= 0.96;
      e.vy = e.vy * 0.97 - 8 * dt;
      const u = e.t / e.life;
      // 흰 노랑 → 주황 → 검붉게 식는다 (재는 잿빛)
      const col = e.grey ? (u < 0.5 ? 0xb8b0a8 : 0x6a625e) : u < 0.25 ? 0xfff1b8 : u < 0.55 ? 0xff9a3a : u < 0.8 ? 0xd2401a : 0x6a1a10;
      const sz = u < 0.5 ? 2 : 1;
      g.rect(Math.round(e.x), Math.round(e.y), sz, sz).fill({ color: col, alpha: 1 - u * 0.6 });
    }
  }

  /**
   * 이 캐릭터의 변신 모습. characters.json 에 따로 그린 모습이 없으면 원래 그림에 불꽃 테두리를 입힌다(aura) —
   * 회광반조는 누가 얻어도 변신한다(1.20. 전에는 적소하만 모습이 바뀌었다).
   */
  private formOf(name: string): FormArt | null {
    const f = this.forms.get(name);
    if (f) return f;
    if (!this.atlas || !this.baseAnim) return null;
    return { atlas: this.atlas, anim: this.baseAnim, transform: [], untransform: [], aura: true };
  }

  /**
   * 변신 스킬이 변신 중인지 본다. 켜지면 몸이 검붉게 물든 뒤(FORM_IGNITE) 불꽃 속에서 변신하고,
   * 꺼지면 풀리는 동작 — 스킬을 잃으면(개발자 패널·새 판) 바로 원래대로.
   */
  private updateForm(): void {
    let want: string | null = null;
    let owner: WeaponInstance | null = null;
    for (const w of this.weapons) {
      if (!w.def.form) continue;
      owner = w;
      if (w.formActive) want = w.def.form;
      break;
    }
    if (want === this.form) return;
    const prev = this.form;
    this.form = want;
    if (want) {
      this.darkenT = FORM_IGNITE;
      this.coolT = 0;
      this.slumpT = 0;
    } else if (prev) {
      this.endForm(prev, owner ? owner.formEnd : 'lost');
    }
  }

  private beginTransform(): void {
    const f = this.form ? this.formOf(this.form) : null;
    this.auraOn = f?.aura ?? true;
    if (this.animator && f) {
      // 변신 그림이 있으면 그 동작(적소하 넉 장 0.8초, 템페스 두 장 0.6초), 없으면 몸이 한 번 부풀었다 가라앉는다
      const n = f.transform.length;
      this.animator.playTransform(f.atlas, f.anim, f.transform, n ? Math.min(0.3, 0.8 / n) : 0.2);
      if (!n) this.popT = FORM_POP;
    }
    this.spawnBurst(this.px, this.py, 120, 'red', 'fire', FX_RAMPS.flame);
    for (let i = 0; i < 24; i++) this.spawnEmber(this.px, this.py - 30, 150);
    this.flashFx(0.35, 0.25, 0x5a0a0a);
    this.shake(0.4, 8);
    audio.play('boomFire', { gain: 1.6, rate: 0.8 });
  }

  /**
   * 변신이 풀린다. tired = 기력이 바닥나서(지쳤다), cancel = 스스로 풀었다, lost = 스킬을 잃었다(바로).
   * 풀림 그림이 있으면 그 동작(적소하: 절정 → 불꽃이 잦아듦 → 웅크림, 템페스: 불꽃 속 → 휩싸임) 뒤 원래 모습,
   * 없으면 몸이 한 번 움츠러든다. 불티가 사방으로 흩어지고 연기가 오르고, 몸은 잿빛에서 제 색으로 식는다.
   */
  private endForm(name: string, why: 'tired' | 'cancel' | 'lost'): void {
    const f = this.formOf(name);
    this.darkenT = 0;
    this.auraOn = false;
    this.popT = 0;
    if (this.animator && this.atlas && this.baseAnim) {
      const n = f?.untransform.length ?? 0;
      if (why !== 'lost' && f && n) {
        this.animator.playTransform(this.atlas, this.baseAnim, f.untransform, Math.min(0.24, 0.72 / n), { atlas: f.atlas, def: f.anim });
      } else {
        this.animator.setForm(this.atlas, this.baseAnim);
        if (why === 'tired') this.slumpT = FORM_SLUMP;
      }
    }
    if (why === 'lost') {
      this.auraK = 0;
      return;
    }
    this.coolT = FORM_COOL;
    for (let i = 0; i < 30; i++) this.spawnEmber(this.px, this.py - 28, 110);
    this.spawnBurst(this.px, this.py - 18, 48, 'red', 'ember', FX_RAMPS.flame); // 확 일었다가 검붉은 연기로 사그라든다
    audio.play('steam', { gain: 1.7, rate: 0.72 }); // 불이 꺼지는 "치이익"
    if (why === 'tired') {
      this.sayText.texture = this.sayTexture('지쳤다…');
      this.sayT = FORM_SAY;
      audio.play('boomAir', { gain: 0.9, rate: 0.55 });
    }
  }

  /**
   * 회광반조 검상: 호를 따라 바닥에 상처 그림(MeshRope)을 휘어 붙이고, reveal 초에 걸쳐 a0 쪽부터 드러낸다.
   * 그림이 없으면 잿불 자국 몇 개로 대신한다.
   */
  slashWound(x: number, y: number, radius: number, a0: number, a1: number, reveal: number, damage: number, status: StatusApply): void {
    if (!this.woundTex) {
      for (let k = 0; k < 3; k++) {
        const a = a0 + ((a1 - a0) * (k + 0.5)) / 3;
        this.spawnCinder(x + Math.cos(a) * radius, y + Math.sin(a) * radius, radius * 0.35, damage, status);
      }
      return;
    }
    if (this.wounds.length >= MAX_WOUNDS) {
      this.removeWound(this.wounds[0]);
      this.wounds.shift();
    }
    // 호를 따라 점을 찍는다. 그림 굵기는 반경에 맞춰(반경 240 에서 약 70px)
    const n = 24;
    const pts: Point[] = [];
    for (let i = 0; i <= n; i++) {
      const a = a0 + ((a1 - a0) * i) / n;
      pts.push(new Point(Math.cos(a) * radius, Math.sin(a) * radius));
    }
    const rope = new MeshRope({ texture: this.woundTex, points: pts, width: Math.max(34, radius * 0.45) });
    rope.position.set(Math.round(x), Math.round(y));
    const mask = new Graphics();
    mask.position.set(Math.round(x), Math.round(y));
    this.fieldLayer.addChild(rope, mask);
    rope.mask = mask;
    this.wounds.push({ rope, mask, x, y, radius, a0, a1, t: 0, reveal, life: WOUND_LIFE, tick: reveal, damage, status });
  }

  private removeWound(w: Wound): void {
    w.rope.mask = null;
    w.mask?.destroy();
    w.mask = null;
    w.rope.destroy();
  }

  private updateWounds(dt: number): void {
    for (let i = 0; i < this.wounds.length; i++) {
      const w = this.wounds[i];
      w.t += dt;
      w.life -= dt;
      // "휘이익" — 칼이 지나가는 만큼 부채꼴 가리개를 넓혀 상처가 따라 드러나게
      if (w.mask) {
        const u = Math.min(1, w.t / w.reveal);
        const e = 1 - Math.pow(1 - u, 2.4);
        const R = w.radius * 1.6;
        const end = w.a0 + (w.a1 - w.a0) * e;
        const pts: number[] = [0, 0];
        const steps = 16;
        for (let k = 0; k <= steps; k++) {
          const a = w.a0 - Math.sign(w.a1 - w.a0) * 0.08 + ((end - w.a0 + Math.sign(w.a1 - w.a0) * 0.08) * k) / steps;
          pts.push(Math.cos(a) * R, Math.sin(a) * R);
        }
        w.mask.clear().poly(pts).fill(0xffffff);
        if (u >= 1) {
          w.rope.mask = null;
          w.mask.destroy();
          w.mask = null;
        }
      }
      // 남은 시간의 마지막 40% 동안 흐려진다
      w.rope.alpha = Math.min(1, w.life / (WOUND_LIFE * 0.4));
      // 다 드러난 뒤로 0.4초마다 상처 위의 적을 태운다
      if (!w.mask) {
        w.tick -= dt;
        if (w.tick <= 0) {
          w.tick = 0.4;
          for (let k = 0; k < 5; k++) {
            const a = w.a0 + ((w.a1 - w.a0) * (k + 0.5)) / 5;
            this.areaDamage(w.x + Math.cos(a) * w.radius, w.y + Math.sin(a) * w.radius, w.radius * 0.2, w.damage, 0, 0, 0, -1, 0, w.status);
          }
        }
      }
      if (w.life <= 0) {
        this.removeWound(w);
        this.wounds.splice(i, 1);
        i--;
      }
    }
  }

  /** 세포 역설 보호막: 몸을 두른 초록 고리. 찰수록 진하다 */
  private drawShield(): void {
    const g = this.shieldG;
    g.clear();
    if (this.shield <= 0 || this.shieldCap <= 0) return;
    const k = this.shield / this.shieldCap;
    const r = Math.round(26 * this.bodyScale);
    const x = Math.round(this.px);
    const y = Math.round(this.py) - 8;
    g.circle(x, y, r).stroke({ width: 5, color: 0x3aa06a, alpha: 0.25 + 0.3 * k });
    g.circle(x, y, r).stroke({ width: 2, color: 0xc9f5e0, alpha: 0.4 + 0.5 * k });
  }

  private hurtPlayer(amount: number): void {
    if (this.iframe > 0 || this.dash) return; // 도약하는 동안은 맞지 않는다
    const guard = this.currentGuard();
    let dealt = Math.max(1, (amount - this.playerStats.armor - this.playerStats.tough) * (1 - guard));
    this.iframe = CONFIG.PLAYER_IFRAME;
    // 세포 역설: 보호막이 먼저 받는다. 깨지는 순간 세포가 터진다
    if (this.shield > 0) {
      const a = Math.min(this.shield, dealt);
      this.shield -= a;
      dealt -= a;
      if (this.shield <= 0.01) {
        this.shield = 0;
        this.cellBurst();
      }
    }
    if (dealt <= 0) return;
    this.hp -= dealt;
    this.shake(0.18, 7);
    audio.play('hurt');
  }

  /** 보호막이 깨질 때 — 주변으로 세포가 터진다 */
  private cellBurst(): void {
    const R = 170;
    this.areaDamage(this.px, this.py, R, this.shieldBurst, 380, 0, 0, -1, 0, this.shieldStatus);
    this.spawnBurst(this.px, this.py, R, 'grey', 'air', RAMPS.grey);
    audio.play('cellBurst');
    this.shake(0.25, 9);
    this.heavyHit();
  }

  private shake(time: number, mag: number): void {
    this.shakeT = Math.max(this.shakeT, time);
    this.shakeMag = mag;
  }

  /**
   * 화면이 좁은 폰에서는 카메라를 물려(축소) 더 넓게 보여 준다. 짧은 변이 640px 보다 작으면 그만큼, 최대 0.6배까지.
   * 세로 폰(375px)에서 1배로 보면 옆으로 375px 밖에 안 보여서, 표준 화면 밖(가로 700px)에서 오는 적을 코앞에서야 봤다.
   * 그림만 바뀌고 판정·스폰은 그대로 표준 화면(VIEW_W×VIEW_H) 기준이다.
   */
  get viewZoom(): number {
    const m = Math.min(this.app.renderer.width, this.app.renderer.height);
    return m >= 640 ? 1 : Math.max(0.6, m / 640);
  }

  private updateCamera(): void {
    const halfW = this.app.renderer.width / 2;
    const halfH = this.app.renderer.height / 2;
    const z = this.viewZoom;
    let sx = 0;
    let sy = 0;
    if (this.shakeT > 0) {
      sx = (Math.random() * 2 - 1) * this.shakeMag;
      sy = (Math.random() * 2 - 1) * this.shakeMag;
    }
    // 카메라도 정수로 눕혀야 도트가 안 떨린다
    this.world.scale.set(z);
    this.world.position.set(Math.round(halfW - this.px * z + sx), Math.round(halfH - this.py * z + sy));
    this.bg.tileScale.set(z);
    this.bg.tilePosition.set(this.world.position.x, this.world.position.y);
    this.map.update(this.px, this.py, (halfW * 2) / z, (halfH * 2) / z);
    this.viewHW = halfW / z;
    this.viewHH = halfH / z;
    this.cullOffscreen();
  }

  /** (x, y) 가 화면 안(여유 margin 포함)인가 */
  private inView(x: number, y: number, margin: number): boolean {
    return Math.abs(x - this.px) <= this.viewHW + margin && Math.abs(y - this.py) <= this.viewHH + margin;
  }

  /**
   * 화면 밖 그림은 그리지 않는다(renderable). 판정·움직임·상태는 그대로 — 눈에 안 보이는 그리기만 건너뛴다.
   * 후반에는 적의 절반, 보석의 3분의 2가 화면 밖인데 전부 그려서 폰에서 무거웠다.
   * 보스는 크고(기술 그림이 멀리 뻗는다) 몇 안 되니 늘 그린다.
   */
  private cullOffscreen(): void {
    const en = this.enemies.items;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = en[i];
      const on = e.boss || this.inView(e.x, e.y, CULL_MARGIN);
      e.sprite.renderable = on;
      e.shadow.renderable = on;
      e.gild.renderable = on;
    }
    const gems = this.gems.items;
    for (let i = 0; i < this.gems.count; i++) {
      const g = gems[i];
      g.sprite.renderable = this.inView(g.x, g.y, CULL_MARGIN);
    }
    for (const c of this.corpses) {
      if (c.life > 0) c.sprite.renderable = this.inView(c.sprite.x, c.sprite.y, CULL_MARGIN);
    }
  }

  // ── 스폰 ───────────────────────────────────────────────

  /**
   * 표준 화면(CONFIG.VIEW_W×VIEW_H) 사각형 바깥 테두리 위의 한 점. 각도를 주면 그 방향의 경계 좌표를 돌려준다.
   * 실제 창 크기를 쓰지 않는다 — 기기마다 적이 오는 거리가 같아야 같은 판이 된다.
   */
  private spawnPoint(angle: number, out: { x: number; y: number }): void {
    const margin = 60;
    const halfW = CONFIG.VIEW_W / 2 + margin;
    const halfH = CONFIG.VIEW_H / 2 + margin;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const t = Math.min(halfW / Math.max(Math.abs(cos), 1e-4), halfH / Math.max(Math.abs(sin), 1e-4));
    out.x = this.px + cos * t;
    out.y = this.py + sin * t;
  }

  /** 표준 화면 밖으로 이만큼 벗어나면 반대편으로 돌려보낸다. */
  private get despawnRadius(): number {
    return Math.hypot(CONFIG.VIEW_W, CONFIG.VIEW_H) / 2 + 500;
  }

  private updateSpawner(dt: number): void {
    // 보스
    while (this.elapsed >= (this.nextBossIndex + 1) * CONFIG.BOSS_INTERVAL) {
      const bosses = this.data.enemies.filter((e) => e.boss);
      if (bosses.length > 0) {
        // 적이 꽉 차 있으면(600) 보스가 안 나오고 순번만 넘어갔다(8분 나찰이 통째로 빠진 판이 있었다) — 가장 먼 일반 적을 치운다
        if (this.enemies.full) this.freeFarthestEnemy();
        const def = bosses[this.nextBossIndex % bosses.length];
        const e = this.spawnEnemy(def, true, CONFIG.bossHpScale(this.nextBossIndex, bosses.length));
        if (e) {
          const lap = Math.floor(this.nextBossIndex / bosses.length);
          e.brain = e.art ? makeBrain(def.pattern, lap) : null;
          const title = (BOSS_TITLE[def.pattern ?? ''] ?? '보스') + (lap > 0 ? ` · ${lap + 1}번째` : '');
          this.cb.onBanner(def.name, title, def.color, 'boss', e.art ? `assets/enemies/boss_${def.art}.png` : undefined);
        }
        this.shake(0.5, 12);
        audio.play('boss');
      }
      this.nextBossIndex++;
    }

    const candidates = this.data.enemies.filter(
      (e) => !e.boss && e.weight > 0 && this.elapsed >= e.minTime && this.elapsed <= e.maxTime,
    );
    if (candidates.length === 0) return;
    const weights = candidates.map((c) => c.weight);

    // 엘리트: 가끔 크고 단단한 금빛 놈이 섞여 나온다. 잡으면 보물 상자
    this.eliteTimer -= dt;
    if (this.eliteTimer <= 0) {
      this.eliteTimer = ELITE_EVERY;
      const e = this.spawnEnemy(candidates[this.rng.weighted(weights)], false);
      if (e) this.makeElite(e);
    }

    this.spawnTimer -= dt;
    if (this.spawnTimer > 0) return;
    this.spawnTimer = CONFIG.spawnInterval(this.elapsed);

    // 1.23 잡몹이 세지는 만큼 한 번에 나오는 수와 동시에 살아 있는 수를 줄인다 — 뒤로 갈수록 수보다 한 마리 한 마리가 무섭게
    const n = Math.max(1, Math.round(CONFIG.spawnBatch(this.elapsed) / CONFIG.mobTierMul(CONFIG.mobTier(this.elapsed)).count));
    const cap = CONFIG.mobCap(this.elapsed);
    for (let i = 0; i < n; i++) {
      if (this.enemies.full || this.enemies.count >= cap) break;
      this.spawnEnemy(candidates[this.rng.weighted(weights)], false);
    }
  }

  /** 가장 먼 일반 적 하나를 풀에서 바로 뺀다 (죽음 처리·보석 없이). 해시를 다시 짓기 전(updateSpawner)에만 부른다 */
  private freeFarthestEnemy(): void {
    let bi = -1;
    let bd = -1;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.boss || e.elite || e === this.charmSrc) continue;
      const d = (e.x - this.px) ** 2 + (e.y - this.py) ** 2;
      if (d > bd) {
        bd = d;
        bi = i;
      }
    }
    if (bi < 0) return;
    const e = this.enemies.items[bi];
    e.dead = true;
    e.sprite.visible = false;
    e.shadow.visible = false;
    e.gild.visible = false;
    e.brain = null;
    this.enemies.removeAt(bi);
  }

  private spawnEnemy(def: EnemyDef, boss: boolean, bossHpMul = 1): Enemy | null {
    const e = this.enemies.spawn();
    if (!e) return null;
    this.spawnPoint(this.rng.next() * Math.PI * 2, this.spawnScratch);
    this.initEnemy(e, def, boss, bossHpMul);
    if (!boss) this.applyTier(e, CONFIG.mobTier(this.elapsed));
    e.x = this.spawnScratch.x;
    e.y = this.spawnScratch.y;
    e.sprite.position.set(Math.round(e.x), Math.round(e.y));
    this.startFadeIn(e);
    return e;
  }

  /** 1.23 잡몹 단계를 붙인다 — 조금 크고 단단하고 아프고 빠르다. 경험치도 그만큼 (수는 스폰에서 줄인다) */
  private applyTier(e: Enemy, tier: number): void {
    if (tier <= 1) return;
    const m = CONFIG.mobTierMul(tier);
    e.tier = tier;
    e.maxHp *= m.hp;
    e.hp = e.maxHp;
    e.damage *= m.damage;
    e.speed *= m.speed;
    e.radius *= m.size;
    e.drawScale *= m.size;
    if (!e.art) e.sprite.scale.set(e.sprite.scale.x * m.size);
    e.xp = Math.max(1, Math.round(e.xp * m.xp));
  }

  /** 엘리트로 만든다 — 체력 10배, 크고 금빛, 조금 더 아프다. 밀어내기는 덜 먹는다 */
  private makeElite(e: Enemy): void {
    e.elite = true;
    e.maxHp *= 10;
    e.hp = e.maxHp;
    e.radius *= 1.4;
    e.damage *= 1.5;
    e.xp *= 6;
    e.drawScale *= 1.45;
    e.sprite.scale.set(e.drawScale);
    // 도트 그림은 곱하기 색으로는 금빛이 옅다 — 흰 실루엣을 금빛으로 더해(gild) 입힌다
    e.baseTint = e.art ? 0xfff2cc : 0xffe7a0;
    e.sprite.tint = e.baseTint;
    audio.play('elite');
    this.cb.onBanner('엘리트 출현', `금빛 ${e.def.name} — 잡으면 보물 상자`, '#ffd76e', 'notice');
  }

  /** 서서히 나타나게 한다. 그림만 바뀌고 판정은 그대로다(맞고, 때린다). */
  private startFadeIn(e: Enemy): void {
    e.fadeIn = CONFIG.ENEMY_FADE_IN;
    e.sprite.alpha = 0;
  }

  /** 적 한 마리를 초기화한다. 스폰과 분열이 같이 쓴다. */
  private initEnemy(e: Enemy, def: EnemyDef, boss: boolean, bossHpMul: number): void {
    e.uid = this.uidCounter++;
    e.def = def;
    e.vx = 0;
    e.vy = 0;
    // 보스 체력은 등장 시각에 맞춰 이미 조정돼 있으므로 시간 배율을 곱하지 않는다
    e.maxHp = boss ? def.hp * bossHpMul : def.hp * CONFIG.enemyHpScale(this.elapsed);
    e.hp = e.maxHp;
    e.damage = def.damage * CONFIG.enemyDamageScale(this.elapsed);
    e.speed = def.speed * CONFIG.enemySpeedScale(this.elapsed);
    e.radius = def.radius;
    e.xp = def.xp;
    e.boss = boss;
    e.dead = false;
    e.flash = 0;
    e.slowT = 0;
    e.slowAmt = 0;
    e.wepCd.fill(0);

    e.burn = 0;
    e.burnDmg = 0;
    e.burnTimer = 0;
    e.frost = 0;
    e.frozen = 0;
    e.frostImmune = 0;
    e.stun = 0;
    e.airT = 0;
    e.airCd = 0;
    e.sprite.rotation = 0;
    e.shock = false;
    e.burnLock = 0;
    e.aiT = this.rng.range(0, 1.2);
    e.aiPhase = 0;
    e.noSplit = false;
    e.drawScale = 1;
    e.fadeIn = 0;
    e.elite = false;
    e.tier = 1;
    e.chain = 0;
    e.reactT = 0;
    e.sprite.alpha = 1;

    const pix = this.enemyTex.get(def.id);
    if (pix) {
      e.baseTint = 0xffffff;
      e.sprite.texture = pix;
      e.sprite.tint = 0xffffff;
      e.sprite.scale.set(e.drawScale);
    } else {
      const v = this.enemyVisuals.get(def.id) ?? resolveVisual(def.sprite, def.shape, def.color);
      e.baseTint = v.tint;
      e.sprite.texture = v.texture;
      e.sprite.tint = v.tint;
      e.sprite.scale.set(e.radius / v.baseRadius);
    }
    // 1.22 받은 도트 그림 — 발 기준, 동작, 그림자
    const art = enemyArtOf(def.art);
    e.art = art;
    e.brain = null;
    e.castT = 0;
    e.shots = 0;
    e.gild.visible = false;
    e.artScale = def.artScale ?? 1;
    if (art) {
      e.moveAnim = art.moves[e.uid % art.moves.length];
      e.anim = e.moveAnim;
      e.animT = Math.random() * 2;
      e.foot = Math.round(art.moveH * e.artScale * 0.42);
      e.baseTint = 0xffffff;
      const f = art.anims.get(e.moveAnim)![0];
      e.sprite.texture = f.tex;
      e.sprite.anchor.set(f.ax, f.ay);
      e.sprite.tint = 0xffffff;
      e.sprite.scale.set(e.artScale);
      e.shadow.texture = this.shadowTex;
      e.shadow.visible = true;
      e.shadow.alpha = 1;
    } else {
      e.foot = 0;
      e.sprite.anchor.set(0.5);
      e.shadow.visible = false;
    }
    e.sprite.visible = true;
  }

  // ── 공간 분할 ──────────────────────────────────────────

  private rebuildHash(): void {
    this.hash.clear();
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.dead) continue;
      this.hash.insert(e.x, e.y, i);
    }
  }

  // ── 적 ─────────────────────────────────────────────────

  private updateEnemies(dt: number): void {
    const despawn = this.despawnRadius;
    const items = this.enemies.items;
    this.warnG.clear();
    hazards.length = 0;
    this.resetStamps();

    for (let i = 0; i < this.enemies.count; i++) {
      const e = items[i];
      if (e.dead) continue;

      let dx = this.px - e.x;
      let dy = this.py - e.y;
      const dist = Math.hypot(dx, dy) || 1;

      // 너무 멀어지면 반대편으로 재활용 (제거/재할당 없이)
      if (dist > despawn) {
        this.spawnPoint(this.rng.next() * Math.PI * 2, this.spawnScratch);
        e.x = this.spawnScratch.x;
        e.y = this.spawnScratch.y;
        this.startFadeIn(e);
        continue;
      }

      dx /= dist;
      dy /= dist;

      // 무기 슬롯별 재타격 쿨다운 감소
      for (let s = 0; s < e.wepCd.length; s++) {
        if (e.wepCd[s] > 0) e.wepCd[s] -= dt;
      }
      if (e.reactT > 0) e.reactT -= dt;

      this.tickStatus(e, dt);

      let speed = e.speed;
      if (e.slowT > 0) {
        e.slowT -= dt;
        speed *= 1 - e.slowAmt;
      }
      // [서리]는 중첩만큼 느려지고, [빙결]/[기절]이면 아예 못 움직인다
      if (e.frost > 0) {
        speed *= 1 - Math.min(ST.FROST_SLOW_MAX, e.frost * ST.FROST_SLOW_PER_STACK);
      }
      if (e.frozen > 0 || e.stun > 0) speed = 0;

      // ── 덤비는 방식 ──────────────────────────────────
      // 전부 곧장 달려들면 피하는 재미가 없다. 종류마다 다르게 움직인다.
      let mx = dx;
      let my = dy;
      const ai = e.def.ai ?? 'chase';
      const still = e.frozen > 0 || e.stun > 0;
      // 그림은 플레이어 쪽을 본다 (돌진·할퀴기는 그쪽으로)
      if (Math.abs(this.px - e.x) > 8) e.face = this.px > e.x ? 1 : -1;
      let anim = e.moveAnim;

      if (e.brain) {
        // 1.22 보스: 움직임과 기술은 머리(bosses.ts)가 정한다. 얼면 멈춘다
        if (e.frozen <= 0) e.brain.update(this.bossHost, e, dt);
        speed = 0;
      } else if (ai === 'charger') {
        e.aiT -= dt;
        if (e.aiPhase === 0) {
          if (dist < (e.def.chargeRange ?? 320) && e.aiT <= 0 && !still) {
            e.aiPhase = 1;
            e.aiT = 0.5;          // 멈춰서 노린다
            e.dashX = dx;
            e.dashY = dy;
          }
        } else if (e.aiPhase === 1) {
          speed = 0;              // 조준 중에는 멈춘다 (피할 틈)
          anim = 'aim';
          e.face = e.dashX >= 0 ? 1 : -1;
          // 덮칠 쪽을 옅은 점선으로 (그림이 있는 적만 — 웅크린 자세와 같이 읽힌다)
          if (e.art) warnDash(this.warnG, e.x, e.y, Math.atan2(e.dashY, e.dashX), e.speed * (e.def.chargeSpeed ?? 3.4) * 0.45, 1 - e.aiT / (e.chain > 0 ? 0.3 : 0.5));
          if (e.aiT <= 0) {
            e.aiPhase = 2;
            e.aiT = 0.45;
            if (e.art) audio.play('pounce', { rate: 1.25 + Math.random() * 0.25, gain: 0.4 });
          }
        } else {
          mx = e.dashX;           // 조준한 방향으로만 간다
          my = e.dashY;
          speed *= e.def.chargeSpeed ?? 3.4;
          e.face = e.dashX >= 0 ? 1 : -1;
          anim = e.aiT > 0.18 ? 'dash' : 'attack'; // 덮치다 끝에서 할퀸다
          if (e.aiT <= 0) {
            // 1.23 2단계부터는 짧게 다시 노려 한 번 더(4단계부터 두 번 더) 덮친다 — 첫 덮침을 피해도 끝이 아니다
            if (e.chain < (e.tier >= 4 ? 2 : e.tier >= 2 ? 1 : 0) && !still) {
              e.chain++;
              e.aiPhase = 1;
              e.aiT = 0.3;
              const qx = this.px - e.x;
              const qy = this.py - e.y;
              const qd = Math.hypot(qx, qy) || 1;
              e.dashX = qx / qd;
              e.dashY = qy / qd;
            } else {
              e.chain = 0;
              e.aiPhase = 0;
              e.aiT = 1.8;
            }
          }
        }
      } else if (ai === 'bruiser') {
        // 1.22 거상: 가까우면 멈춰 앞발을 들고(부채꼴 경고) → 할퀴며 연기를 뿜는다
        e.aiT -= dt;
        const reach = (e.def.attackRange ?? 120) + e.radius + 18;
        if (e.aiPhase === 0) {
          if (dist < reach - 10 && e.aiT <= 0 && !still) {
            e.aiPhase = 1;
            e.aiT = 0.7;
            e.dashX = dx;
            e.dashY = dy;
          }
        } else if (e.aiPhase === 1) {
          speed = 0;
          anim = 'aim';
          e.face = e.dashX >= 0 ? 1 : -1;
          const dir = Math.atan2(e.dashY, e.dashX);
          warnSector(this.warnG, e.x, e.y, reach, dir, 0.85, 1 - e.aiT / (e.chain > 0 ? 0.5 : 0.7));
          if (e.aiT <= 0 && !still) {
            e.aiPhase = 2;
            e.aiT = 0.42;
            this.hurtIfSector(e.x, e.y, reach, dir, 0.85, e.damage);
            const back = e.dashX < 0;
            this.spawnFxSprite({
              name: 'smoke_trail', x: e.x + e.dashX * reach * 0.55, y: e.y + e.dashY * reach * 0.55 - 12, life: 0.5,
              scale: 0.75, scale1: 0.95, rot: back ? Math.atan2(-e.dashY, -e.dashX) : dir, flipX: back,
            });
            audio.play('windBlast', { rate: 1.35, gain: 0.5 });
          } else if (e.aiT <= 0) {
            e.aiT = 0.1;
          }
        } else {
          speed = 0;
          anim = 'attack';
          e.face = e.dashX >= 0 ? 1 : -1;
          if (e.aiT <= 0) {
            // 1.23 3단계부터는 한 번 더 — 플레이어 쪽으로 다시 앞발을 든다(경고는 짧게)
            if (e.tier >= 3 && e.chain < 1 && !still) {
              e.chain++;
              e.aiPhase = 1;
              e.aiT = 0.5;
              const qx = this.px - e.x;
              const qy = this.py - e.y;
              const qd = Math.hypot(qx, qy) || 1;
              e.dashX = qx / qd;
              e.dashY = qy / qd;
            } else {
              e.chain = 0;
              e.aiPhase = 0;
              e.aiT = 3;
            }
          }
        }
      } else if (ai === 'charmer') {
        // 1.22 요화: 거리를 두고 비스듬히 돌다가 손을 모아 하트를 날린다
        const keep = e.def.keepDistance ?? 250;
        const side = e.uid & 1 ? 1 : -1;
        if (dist < keep * 0.8) {
          mx = -dx;
          my = -dy;
        } else if (dist < keep * 1.2) {
          mx = -dy * side + dx * 0.12;
          my = dx * side + dy * 0.12;
        }
        e.aiT -= dt;
        if (e.castT > 0) {
          speed = 0;
          anim = 'shoot';
          if (!still) e.castT -= dt;
          if (e.castT <= 0) this.fireEnemyShot(e, true, false);
        } else if (e.aiT <= 0 && !still && dist < 470) {
          e.aiT = e.def.shotInterval ?? 4.5;
          if (e.art) {
            e.castT = 0.45;
            anim = 'shoot';
          } else {
            this.fireEnemyShot(e, true, false);
          }
        }
      } else if (ai === 'orbiter') {
        // 반지름 방향보다 접선 방향이 강하다 = 빙 돌면서 파고든다
        mx = dx * 0.5 - dy * 0.95;
        my = dy * 0.5 + dx * 0.95;
        const l = Math.hypot(mx, my) || 1;
        mx /= l;
        my /= l;
      } else if (ai === 'ranged') {
        const keep = e.def.keepDistance ?? 280;
        if (dist > keep * 1.15) {
          // 다가온다
        } else if (dist < keep * 0.85) {
          mx = -dx;               // 물러선다
          my = -dy;
        } else {
          mx = -dy;               // 옆으로 돈다
          my = dx;
        }
        e.aiT -= dt;
        if (e.castT > 0) {
          // 1.22 권귀: 손을 모았다가 쏜다 (세 번째마다 크게 모아 큰 장풍)
          speed = 0;
          anim = e.aiPhase === 2 ? 'charge' : 'shoot';
          if (!still) e.castT -= dt;
          if (e.castT <= 0) {
            this.fireEnemyShot(e, false, e.aiPhase === 2);
            e.aiPhase = 0;
          }
        } else if (e.aiT <= 0 && !still) {
          e.aiT = e.def.shotInterval ?? 2.2;
          const big = !!e.def.chargeEvery && (e.shots + 1) % e.def.chargeEvery === 0;
          if (e.art) {
            e.aiPhase = big ? 2 : 1;
            e.castT = big ? 0.7 : 0.3;
            anim = big ? 'charge' : 'shoot';
          } else {
            this.fireEnemyShot(e, false, big);
          }
        }
      } else if (ai === 'chase' && e.tier >= 3) {
        // 1.23 3단계부터: 가까우면 잠깐 웅크렸다(멈칫) 그쪽으로 튀어 든다 — 멈칫이 신호다
        e.aiT -= dt;
        if (e.aiPhase === 0) {
          if (dist < 230 && dist > 70 && e.aiT <= 0 && !still) {
            e.aiPhase = 1;
            e.aiT = 0.32;
            e.dashX = dx;
            e.dashY = dy;
          }
        } else if (e.aiPhase === 1) {
          speed *= 0.12;
          if (e.aiT <= 0) {
            e.aiPhase = 2;
            e.aiT = 0.3;
          }
        } else {
          mx = e.dashX;
          my = e.dashY;
          speed *= 2.4;
          if (e.aiT <= 0) {
            e.aiPhase = 0;
            e.aiT = 2.2 + this.rng.next() * 1.6;
          }
        }
      }

      if (anim !== e.anim) {
        e.anim = anim;
        e.animT = anim === e.moveAnim ? Math.random() : 0;
      }

      e.x += mx * speed * dt + e.vx * dt;
      e.y += my * speed * dt + e.vy * dt;

      // 넉백 감쇠 — 공중에 뜬 동안은 땅에 끌리지 않아 훨씬 멀리 날아간다
      const damp = e.airT > 0 ? AIR_DAMP : 0.86;
      e.vx *= damp;
      e.vy *= damp;

      // 겹침 완화: 같은 격자 주변 후보 몇 개만 밀어낸다
      const near = this.hash.query(e.x, e.y, e.radius * 2, this.scratch2);
      let checks = 0;
      for (let k = 0; k < near.length && checks < 6; k++) {
        const j = near[k];
        if (j === i || j >= this.enemies.count) continue;
        const o = items[j];
        if (o.dead) continue;
        const ox = e.x - o.x;
        const oy = e.y - o.y;
        const d2 = ox * ox + oy * oy;
        const min = e.radius + o.radius;
        if (d2 > 0.0001 && d2 < min * min) {
          const d = Math.sqrt(d2);
          const push = ((min - d) / d) * 0.45;
          e.x += ox * push;
          e.y += oy * push;
          checks++;
        }
      }

      // 플레이어 접촉
      const pdx = this.px - e.x;
      const pdy = this.py - e.y;
      const rr = e.radius + CONFIG.PLAYER_RADIUS;
      if (e.frozen <= 0 && e.stun <= 0 && !e.brain?.airborne && pdx * pdx + pdy * pdy < rr * rr) this.hurtPlayer(e.damage);

      this.drawEnemy(e, dt, speed > 0);
    }
  }

  /** 1.22 적 그림 — 동작 · 보는 쪽 · 발 위치 · 그림자 · 맞을 때 흰 번쩍 · 엘리트 금빛 */
  private drawEnemy(e: Enemy, dt: number, moving: boolean): void {
    const sp = e.sprite;
    const tint = e.frozen > 0 ? 0x8fd8ff : e.burn > 0 ? 0xffb08f : e.baseTint;
    if (e.fadeIn > 0) {
      e.fadeIn = Math.max(0, e.fadeIn - dt);
      sp.alpha = 1 - e.fadeIn / CONFIG.ENEMY_FADE_IN;
    }
    if (e.flash > 0) e.flash -= dt;
    // 화면 밖(보이는 경계보다 조금 더 밖)이면 그림 장면·자세 계산을 건너뛴다 — 어차피 안 그린다(cullOffscreen)
    if (!e.boss && !this.inView(e.x, e.y, CULL_MARGIN + 60)) return;
    const art = e.art;
    if (!art) {
      sp.tint = e.flash > 0 ? 0xffffff : tint;
      const air = airPose(e);
      sp.position.set(Math.round(e.x), Math.round(e.y - air.lift));
      sp.rotation = air.rot;
      sp.zIndex = e.y;
      return;
    }
    const b = e.brain;
    let anim: string;
    let t: number;
    let loop: boolean;
    if (b) {
      anim = b.anim;
      t = b.animT;
      loop = b.loop;
      e.face = b.face;
    } else {
      // 얼거나 기절하면 멈춘다. 제자리에 서 있으면 걷기를 느리게
      if (e.frozen <= 0 && e.stun <= 0) e.animT += dt * (moving || e.anim !== e.moveAnim ? 1 : 0.35);
      anim = e.anim;
      t = e.animT;
      loop = anim === e.moveAnim || anim === 'idle' || anim === 'aim';
    }
    let frames = art.anims.get(anim);
    if (!frames) {
      anim = e.moveAnim;
      frames = art.anims.get(anim)!;
    }
    const fps = art.fps[anim] ?? 8;
    let i = Math.floor(t * fps);
    i = loop ? i % frames.length : Math.min(frames.length - 1, i);
    const f = frames[i];
    const hit = e.flash > 0;
    sp.texture = hit ? f.flash : f.tex;
    sp.anchor.set(f.ax, f.ay);
    sp.tint = hit ? 0xffffff : tint;
    let k = e.drawScale * e.artScale;
    // 불미물은 숨 쉬듯 부풀었다 줄었다 (다칠수록 빨리)
    if (e.def.ai === 'exploder') k *= 1 + 0.07 * Math.sin(this.elapsed * (e.hp < e.maxHp * 0.5 ? 17 : 8) + e.uid);
    const flip = artFlip(art, anim, e.face, !!b);
    sp.scale.set(flip ? -k : k, k);
    const air = airPose(e);
    const lift = b ? b.lift : air.lift;
    const fx = Math.round(e.x);
    const fy = Math.round(e.y + e.foot * e.drawScale);
    sp.position.set(fx, fy - Math.round(lift));
    sp.rotation = air.rot;
    if (air.rot !== 0) {
      // 발끝이 기준점이라 그대로 돌리면 추처럼 흔들린다 — 기준점을 몸 한가운데로 옮겨 제자리에서 돈다
      const w = sp.texture.width * sp.scale.x;
      const h = sp.texture.height * sp.scale.y;
      sp.anchor.set(0.5, 0.5);
      sp.position.set(fx + (0.5 - f.ax) * w, fy - Math.round(lift) + (0.5 - f.ay) * h);
    }
    sp.zIndex = fy;
    const sh = e.shadow;
    const sw = (e.radius * 2.4) / 24 * (1 - Math.min(0.5, lift / 240));
    sh.position.set(fx, fy);
    sh.scale.set(sw, sw);
    sh.alpha = sp.alpha;
    if (e.elite) {
      const g = e.gild;
      g.visible = sp.visible;
      g.texture = f.flash;
      g.anchor.set(f.ax, f.ay);
      g.scale.copyFrom(sp.scale);
      g.position.copyFrom(sp.position);
      g.anchor.copyFrom(sp.anchor);
      g.rotation = sp.rotation;
      g.tint = 0xffc23a;
      g.alpha = (0.2 + 0.12 * Math.sin(this.elapsed * 5 + e.uid)) * sp.alpha;
      g.zIndex = fy + 0.01;
    } else if (e.tier >= 2) {
      // 1.23 잡몹 단계 색 — 몸 위에 은은하게 더해 한눈에 "센 놈"이 보이게 (단계가 오를수록 진하다)
      const g = e.gild;
      g.visible = sp.visible;
      g.texture = f.flash;
      g.anchor.set(f.ax, f.ay);
      g.scale.copyFrom(sp.scale);
      g.position.copyFrom(sp.position);
      g.anchor.copyFrom(sp.anchor);
      g.rotation = sp.rotation;
      g.tint = CONFIG.MOB_TIER_COLORS[Math.min(e.tier, CONFIG.MOB_TIER_COLORS.length - 1)];
      g.alpha = (0.1 + 0.05 * e.tier + 0.06 * Math.sin(this.elapsed * 4 + e.uid)) * sp.alpha;
      g.zIndex = fy + 0.01;
    }
  }

  /** 1.22 쓰러지는 적 — 죽는 동작을 보여 주고 사라진다 (그림이 없으면 작아지며 사라진다) */
  private spawnCorpse(e: Enemy): void {
    // 화면 밖에서 죽은 적은 쓰러지는 모습을 그리지 않는다 — 아무도 못 보는데 자리만 차지했다.
    // 그만큼 자리가 남아, 화면 안에서 죽은 적은 끝까지 또렷하게 쓰러진다. 보스는 늘 그린다
    if (!e.boss && !this.inView(e.x, e.y, CULL_MARGIN)) return;
    // 빈 자리 → 화면 밖으로 나간 시체 → 거의 다 쓰러진 시체 순으로 빌린다(화면 안에서 쓰러지는 중인 것은 되도록 안 끊는다)
    let c = this.corpses.find((q) => q.life <= 0) ?? this.corpses.find((q) => !q.sprite.renderable);
    if (!c) c = this.corpses.reduce((a, q) => (a.t / a.life >= q.t / q.life ? a : q));
    const src = e.sprite;
    const sp = c.sprite;
    const art = e.art;
    let frames: EFrame[] | null = null;
    let key = 'death';
    if (art) {
      key = art.anims.has('death_b') && (e.def.ai === 'splitter' || (e.uid & 1) === 1) ? 'death_b' : 'death';
      frames = art.anims.get(key) ?? null;
      c.fps = art.fps[key] ?? 10;
    }
    c.frames = frames;
    c.t = 0;
    c.life = frames ? frames.length / c.fps + 0.35 : 0.2;
    const f = frames?.[0];
    sp.texture = f ? f.tex : src.texture;
    sp.anchor.set(f ? f.ax : src.anchor.x, f ? f.ay : src.anchor.y);
    const k = Math.abs(src.scale.y);
    const flip = art && f ? artFlip(art, key, e.face, e.boss) : src.scale.x < 0;
    sp.scale.set(flip ? -k : k, k);
    sp.position.set(src.x, art ? Math.round(e.y + e.foot * e.drawScale) : Math.round(e.y)); // 공중에서 죽어도 땅에 눕는다
    sp.tint = e.frozen > 0 ? 0x8fd8ff : e.baseTint;
    sp.alpha = src.alpha;
    sp.visible = true;
    sp.rotation = 0;
    // 공중에서 죽었다 — 남은 비행을 시체가 마저 한다(넉백 속도 그대로, 떨어진 뒤 사라진다)
    c.x = sp.x;
    c.y = sp.y;
    c.flyT = 0;
    if (e.airT > 0 && e.airDur > 0) {
      c.flyT = e.airT;
      c.flyDur = e.airDur;
      c.flyH = e.airH;
      c.turns = e.airTurns;
      c.face = e.face >= 0 ? 1 : -1;
      c.vx = e.vx;
      c.vy = e.vy;
      c.life = Math.max(c.life, e.airT + 0.45);
    }
    sp.renderable = true;
  }

  private updateCorpses(dt: number): void {
    for (const c of this.corpses) {
      if (c.life <= 0) continue;
      c.t += dt;
      const sp = c.sprite;
      if (c.t >= c.life) {
        c.life = 0;
        sp.visible = false;
        continue;
      }
      if (c.frames) {
        const f = c.frames[Math.min(c.frames.length - 1, Math.floor(c.t * c.fps))];
        sp.texture = f.tex;
        sp.anchor.set(f.ax, f.ay);
        const fadeAt = c.life - 0.35;
        sp.alpha = c.t > fadeAt ? Math.max(0, 1 - (c.t - fadeAt) / 0.35) : 1;
      } else {
        const u = c.t / c.life;
        sp.alpha = 1 - u;
        sp.scale.set(sp.scale.x * (1 - dt * 3), sp.scale.y * (1 - dt * 3));
      }
      if (c.flyT > 0) this.flyCorpse(c, dt);
    }
  }

  /** 광풍에 휘말린 채 죽은 시체: 날아가며 몸 한가운데를 축으로 돌고, 떨어지면 멈춘다 */
  private flyCorpse(c: Corpse, dt: number): void {
    const sp = c.sprite;
    c.flyT = Math.max(0, c.flyT - dt);
    c.x += c.vx * dt;
    c.y += c.vy * dt;
    const damp = Math.pow(AIR_DAMP, dt * 60);
    c.vx *= damp;
    c.vy *= damp;
    if (c.flyT <= 0) {
      sp.rotation = 0;
      sp.position.set(Math.round(c.x), Math.round(c.y));
      if (c.flyH >= 50 && this.landPuffs < 6) {
        this.landPuffs++;
        this.spawnBurst(c.x, c.y - 4, 20, 'grey', 'air', RAMPS.grey);
      }
      return;
    }
    const u = 1 - c.flyT / c.flyDur;
    const lift = c.flyH * Math.sin(Math.PI * u);
    sp.rotation = Math.PI * 2 * c.turns * u * c.face;
    // 발끝이 기준점이라 그대로 돌리면 추처럼 흔들린다 — 몸 한가운데로 옮겨 제자리에서 돈다
    const w = sp.texture.width * sp.scale.x;
    const h = sp.texture.height * sp.scale.y;
    const ax = sp.anchor.x;
    const ay = sp.anchor.y;
    sp.anchor.set(0.5, 0.5);
    sp.position.set(c.x + (0.5 - ax) * w, c.y - lift + (0.5 - ay) * h);
  }

  /** 죽은 적을 실제로 풀에서 빼고 보석을 떨군다. 인덱스 안정성을 위해 프레임 끝에서만 한다. */
  private sweepDead(): void {
    let died = 0;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (!e.dead) continue;
      this.onEnemyDeath(e);
      this.dropGem(e.x, e.y, e.xp);
      if (e.sprite.visible) this.spawnCorpse(e);
      if (e.boss && e.art) {
        // 보스가 무너진다
        this.spawnBurst(e.x, e.y - 20, 150, 'red', 'fire', FX_RAMPS.flame);
        this.spawnBurst(e.x, e.y, 200, 'grey', 'air', RAMPS.grey, 0.15);
      }
      e.sprite.visible = false;
      e.shadow.visible = false;
      e.gild.visible = false;
      e.brain = null;
      this.enemies.removeAt(i);
      i--;
      died++;
    }
    // 한 프레임에 수십 마리가 죽어도 소리는 한 번만 (나머지는 최소 간격에서 걸러진다)
    if (died > 0) audio.play('kill');
  }

  /** 적 하나의 상태효과를 흘려보낸다. */
  private tickStatus(e: Enemy, dt: number): void {
    if (e.stun > 0) e.stun -= dt;
    if (e.airCd > 0) e.airCd -= dt;
    if (e.airT > 0) {
      e.airT -= dt;
      if (e.airT <= 0) {
        e.airT = 0;
        e.sprite.rotation = 0;
        // 높이 떴다 떨어지면 흙먼지 — 한 프레임에 몇 번만(한꺼번에 수십 마리가 떨어진다)
        if (e.airH >= 50 && this.landPuffs < 6) {
          this.landPuffs++;
          this.spawnBurst(e.x, e.y, 16 + e.radius * 0.6, 'grey', 'air', RAMPS.grey);
        }
      }
    }
    if (e.frostImmune > 0) e.frostImmune -= dt;

    if (e.frozen > 0) {
      e.frozen -= dt;
      if (e.frozen <= 0) {
        e.frost = 0;
        e.frostImmune = ST.FROST_IMMUNE_AFTER;
      }
    } else if (e.frost > 0) {
      e.frost = Math.max(0, e.frost - ST.FROST_DECAY_PER_SEC * dt);
    }

    if (e.burnLock > 0) e.burnLock -= dt;

    if (e.burn > 0) {
      e.burnTimer -= dt;
      if (e.burnTimer <= 0) {
        e.burnTimer = ST.BURN_INTERVAL;
        // [화염]: 중첩 × 한 중첩 피해(불을 건 스킬 공격력 × 화염 배수)를 입고 중첩이 사라진다.
        // 단 회광반조가 붙인 불은 꺼지지 않고 계속 쌓인다.
        this.damageEnemy(e, e.burn * burnPer(e), 0, 0, 0);
        if (e.burnLock <= 0) e.burn = Math.floor(e.burn * ST.BURN_DECAY);
      }
    }
  }

  /** 명중한 적에게 상태효과를 건다. */
  private applyStatus(e: Enemy, st: StatusApply | null | undefined): void {
    if (!st) return;
    if (st.burn) {
      if (e.burn <= 0) {
        e.burnTimer = ST.BURN_INTERVAL;
        e.burnDmg = 0;
      }
      // 한 중첩 피해는 새로 얹는 중첩 수만큼 가중 평균 — 약한 불과 센 불이 섞이면 섞인 만큼
      const add = Math.min(st.burn, ST.BURN_MAX - e.burn);
      if (add > 0) {
        const per = st.burnDmg && st.burnDmg > 0 ? st.burnDmg : ST.BURN_DAMAGE_PER_STACK;
        e.burnDmg = (e.burnDmg * e.burn + per * add) / (e.burn + add);
        e.burn += add;
      }
    }
    if (st.frost && e.frozen <= 0 && e.frostImmune <= 0) {
      e.frost += st.frost;
      // 소한공의 [서리]는 얼리지 못한다 — 얼기 바로 전까지만 (다른 무기의 [서리]가 더해지면 언다)
      if (st.chill) e.frost = Math.min(e.frost, ST.FROST_FREEZE_AT - 0.5);
      else if (e.frost >= ST.FROST_FREEZE_AT) {
        if (e.frozen <= 0) audio.play('freeze');
        e.frozen = ST.FREEZE_DURATION;
        e.vx = 0;
        e.vy = 0;
      }
    }
    if (st.burnLock) e.burnLock = Math.max(e.burnLock, st.burnLock);
    if (st.shock) e.shock = true;
    if (st.stun && !e.boss) e.stun = Math.max(e.stun, st.stun);
    this.react(e, st);
  }

  /**
   * 점화와 상태효과 반응. 터지는 것은 프레임 끝(resolveBlasts)에 한꺼번에 처리한다.
   *   점화(회광반조)  [화염] 이 ignite 만큼 쌓이면 터져 옆으로 불을 옮긴다 — 옮은 불이 또 쌓이면 또 터진다
   *   증기 폭발       [화염] 3 이상 + [서리] 3 이상 → 둘 다 날리고 김이 터진다
   *   쇄빙           [빙결] 인 적에게 [감전] → 얼음이 깨지며 크게 다치고 주변에 [서리]
   *   작렬           [화염] 3 이상인 적이 [기절] → 불이 한꺼번에 터진다(중첩 절반만 남는다)
   */
  private react(e: Enemy, st: StatusApply): void {
    const per = burnPer(e);
    if (st.ignite && e.burn >= st.ignite) {
      this.blasts.push({
        kind: 'ignite', x: e.x, y: e.y, radius: 75, damage: e.burn * per * 1.5,
        status: { burn: Math.ceil(e.burn / 4), ignite: st.ignite, burnDmg: per },
      });
      e.burn = 0;
      return;
    }
    if (e.reactT > 0) return;
    if ((st.burn || st.frost) && e.burn >= 3 && e.frost >= 3) {
      this.blasts.push({ kind: 'steam', x: e.x, y: e.y, radius: 60, damage: 10 + (e.burn * per + e.frost * ST.BURN_DAMAGE_PER_STACK) * 0.8 });
      e.burn = 0;
      e.frost = 0;
      e.frozen = 0;
      e.reactT = 0.5;
    } else if (st.shock && e.frozen > 0) {
      const dmg = 20 + Math.min(e.boss ? 200 : 1e9, e.maxHp * 0.08);
      this.blasts.push({ kind: 'shatter', x: e.x, y: e.y, radius: 60, damage: dmg, status: { frost: 3 } });
      e.frozen = 0;
      e.frost = 0;
      e.frostImmune = ST.FROST_IMMUNE_AFTER;
      e.reactT = 0.5;
    } else if (st.stun && e.burn >= 3) {
      this.blasts.push({ kind: 'blast', x: e.x, y: e.y, radius: 55, damage: e.burn * per * 1.5 });
      e.burn = Math.floor(e.burn / 2);
      e.reactT = 0.5;
    }
  }

  /** 모인 폭발을 터뜨린다. 한 프레임에 너무 많으면 다음 프레임으로 넘긴다(연쇄 점화가 한 번에 화면을 다 태우지 않게). */
  private resolveBlasts(): void {
    // 한 프레임에 16개까지 — 연쇄 점화가 한 번에 화면을 다 태우지 않게, 남은 것은 다음 프레임에
    let n = 0;
    while (this.blasts.length > 0 && n < 16) {
      const b = this.blasts.shift()!;
      n++;
      this.areaDamage(b.x, b.y, b.radius, b.damage, 90, 0, 0, -1, 0, b.status);
      switch (b.kind) {
        case 'steam':
          this.spawnBurst(b.x, b.y, b.radius, 'white', 'air', FX_RAMPS.fog);
          audio.play('steam');
          break;
        case 'shatter':
          this.spawnBurst(b.x, b.y, b.radius, 'blue', 'spark', RAMPS.blue);
          audio.play('shatter');
          break;
        case 'blast':
        case 'ignite':
          this.spawnBurst(b.x, b.y, b.radius, 'red', 'fire', FX_RAMPS.flame);
          audio.play('boomFire');
          break;
      }
    }
  }

  damageEnemy(e: Enemy, amount: number, kbX: number, kbY: number, knockback: number): void {
    if (e.dead) return;
    // [빙결]인 적은 더 아프게 맞고 얼음이 깨진다
    if (e.frozen > 0) {
      amount *= ST.FREEZE_DAMAGE_MUL;
      // 풍화설영 쇄화: 산산조각 — 더 아프고, 얼음 조각이 주변에 [서리]를 뿌린다
      if (this.shatterPower > 0) {
        amount *= 1 + 0.3 * this.shatterPower;
        this.blasts.push({ kind: 'shatter', x: e.x, y: e.y, radius: 80, damage: amount * 0.15 * this.shatterPower, status: { frost: 2 } });
      }
      e.frozen = 0;
      e.frost = 0;
      e.frostImmune = ST.FROST_IMMUNE_AFTER;
    }
    // [감전]: 한 번에 큰 피해를 받으면 주변으로 터진다
    if (e.shock && amount >= ST.SHOCK_TRIGGER) {
      e.shock = false;
      this.pendingShocks.push({ x: e.x, y: e.y, dmg: amount * ST.SHOCK_SPLASH_RATIO, uid: e.uid });
    }
    e.hp -= amount;
    e.flash = 0.07;
    e.sprite.tint = 0xffffff;
    if (knockback > 0 && !e.boss) {
      const d = Math.hypot(kbX, kbY) || 1;
      const k = e.elite ? knockback * 0.35 : knockback;
      e.vx += (kbX / d) * k;
      e.vy += (kbY / d) * k;
    }
    if (e.hp <= 0) {
      e.dead = true;
      this.kills++;
      if (e.boss) {
        this.bossesDown++;
        this.shake(0.4, 10);
        // 보스를 쓰러뜨린 순간은 느리게 — 한숨 돌리는 박자
        this.slowmo(0.9, 0.3);
        this.flashFx(0.3, 0.22, 0xffffff);
        audio.play('clear');
      }
    }
  }

  // ── WeaponHost 구현 ────────────────────────────────────

  get actorLayer(): Container {
    return this.playerLayer;
  }

  get groundLayer(): Container {
    return this.fieldLayer;
  }

  strongestEnemy(x: number, y: number, maxRadius: number): Enemy | null {
    const near = this.hash.query(x, y, maxRadius, this.scratch);
    const r2 = maxRadius * maxRadius;
    let best: Enemy | null = null;
    let bestScore = -1;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead || (e.x - x) ** 2 + (e.y - y) ** 2 > r2) continue;
      // 보스 > 엘리트 > 체력 많은 적
      const score = (e.boss ? 2e9 : e.elite ? 1e9 : 0) + e.hp;
      if (score > bestScore) {
        bestScore = score;
        best = e;
      }
    }
    return best;
  }

  lineDamage(
    x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number,
    damage: number, knockback: number, status?: StatusApply,
  ): number {
    return this.bandDamage(x, y, dirX, dirY, length, halfWidth, damage, knockback, status);
  }

  flashScreen(sec: number, alpha: number, tint: number): void {
    this.flashFx(sec, alpha, tint);
  }

  banner(text: string, sub: string, color: string): void {
    this.cb.onBanner(text, sub, color, 'fusion');
  }

  nearestEnemy(x: number, y: number, maxRadius: number): Enemy | null {
    // 가까운 반경부터 넓혀가며 찾는다(먼 거리 전체 조회를 피하려고)
    const rings = [160, 340, 640, maxRadius];
    for (const r of rings) {
      if (r > maxRadius) break;
      const near = this.hash.query(x, y, r, this.scratch);
      let best: Enemy | null = null;
      let bestD = Infinity;
      for (let k = 0; k < near.length; k++) {
        const i = near[k];
        if (i >= this.enemies.count) continue;
        const e = this.enemies.items[i];
        if (e.dead) continue;
        const d = (e.x - x) ** 2 + (e.y - y) ** 2;
        if (d < bestD) {
          bestD = d;
          best = e;
        }
      }
      if (best) return best;
    }
    return null;
  }

  areaDamage(
    x: number,
    y: number,
    radius: number,
    damage: number,
    knockback: number,
    slowT: number,
    slowAmt: number,
    slot = -1,
    reHit = 0,
    status?: StatusApply,
  ): number {
    // 적 중심이 옆 칸에 있어도 반지름만큼은 겹칠 수 있으므로 질의 반경에 여유를 준다
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch);
    let hits = 0;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead) continue;
      const dx = e.x - x;
      const dy = e.y - y;
      const rr = radius + e.radius;
      if (dx * dx + dy * dy > rr * rr) continue;
      if (slot >= 0) {
        if (e.wepCd[slot] > 0) continue;
        e.wepCd[slot] = reHit;
      }
      if (slowT > 0) {
        e.slowT = Math.max(e.slowT, slowT);
        e.slowAmt = Math.max(e.slowAmt, slowAmt);
      }
      this.damageEnemy(e, damage, dx, dy, knockback);
      this.applyStatus(e, status);
      hits++;
    }
    return hits;
  }

  coneDamage(
    x: number,
    y: number,
    dirX: number,
    dirY: number,
    radius: number,
    arcDeg: number,
    damage: number,
    knockback: number,
    status?: StatusApply,
  ): number {
    const cosLimit = Math.cos((arcDeg * Math.PI) / 180 / 2);
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch);
    let hits = 0;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead) continue;
      const dx = e.x - x;
      const dy = e.y - y;
      const d = Math.hypot(dx, dy);
      if (d > radius + e.radius) continue;
      if (d > 0.001) {
        const dot = (dx / d) * dirX + (dy / d) * dirY;
        if (dot < cosLimit) continue;
      }
      this.damageEnemy(e, damage, dx, dy, knockback);
      this.applyStatus(e, status);
      hits++;
    }
    return hits;
  }

  spawnProjectile(spec: ProjectileSpec): void {
    const p = this.projectiles.spawn();
    if (!p) return;
    const { x, y, def, radius } = spec;
    p.x = x;
    p.y = y;
    p.vx = spec.vx;
    p.vy = spec.vy;
    p.damage = spec.damage;
    p.pierce = spec.pierce;
    p.radius = radius;
    p.knockback = spec.knockback;
    p.life = spec.life;
    p.hitCount = 0;
    p.spin = 0;
    p.status = spec.status;
    p.explodeRadius = spec.explodeRadius ?? 0;
    p.explodeDamage = spec.explodeDamage ?? 0;
    p.halfWidth = spec.halfWidth ?? 0;
    p.fall = spec.fall ?? false;
    p.pool = spec.pool ?? false;
    p.lifesteal = spec.lifesteal ?? 0;
    p.attr = def.attr;
    const look = lookOf(def);
    p.look = look;
    p.ramp = rampOf(def);
    p.spin = look.spin ?? 0;
    p.animT = this.rng.next();

    const pix = this.weaponTex.get(def.id);
    if (pix) {
      p.frames = pix.length > 1 ? pix : null;
      p.sprite.texture = pix[0];
      p.sprite.tint = 0xffffff;
      const k = Math.max(0.5, radius / Math.max(1, def.base.size ?? 8));
      // 옆으로 긴 탄(파도)은 기본 폭으로 구운 그림을 지금 폭에 맞춰 옆으로만 늘린다
      const baseW = def.base.width ?? 0;
      p.sprite.scale.set(k, p.halfWidth > 0 && baseW > 0 ? p.halfWidth / baseW : k);
    } else {
      p.frames = null;
      const v = this.weaponVisuals.get(def.id) ?? resolveVisual(def.sprite, def.shape, def.color);
      p.sprite.texture = v.texture;
      p.sprite.tint = v.tint;
      p.sprite.scale.set(radius / v.baseRadius);
    }
    p.sprite.position.set(Math.round(x), Math.round(y));
    p.sprite.rotation = Math.atan2(spec.vy, spec.vx);
    p.sprite.visible = true;
  }

  /** 장판 그림 (크기 4px 단위로 구워 둔다) */
  private fieldTex(def: WeaponDef, radius: number): Texture[] {
    const key = def.id + `:` + Math.round(radius / 4) * 4;
    let tex = this.fieldTexCache.get(key);
    if (!tex) {
      tex = fieldFrames(def.fx, Math.round(radius / 4) * 4, rampOf(def));
      this.fieldTexCache.set(key, tex);
    }
    return tex;
  }

  /** 장판 그림을 미리 굽는다 — 레벨업 카드를 고른 순간(판이 멈춰 있을 때) 부른다 */
  warmField(def: WeaponDef, radius: number): void {
    this.fieldTex(def, radius);
  }

  spawnField(
    x: number,
    y: number,
    radius: number,
    damage: number,
    duration: number,
    slow: number,
    def: WeaponDef,
    status: StatusApply,
    opts: FieldOpts = {},
  ): void {
    const tex = this.fieldTex(def, radius);
    const look = lookOf(def);
    this.placeField(x, y, radius, damage, duration, slow, status, tex, look.fieldSpin ?? 0, look.fieldAlpha ?? 0.9, opts);
  }

  /** 바닥에 잠깐 타오르는 잿불 자국 */
  spawnCinder(x: number, y: number, radius: number, damage: number, status: StatusApply): void {
    const r = Math.max(16, Math.round(radius / 8) * 8);
    const key = `cinder:${r}`;
    let tex = this.fieldTexCache.get(key);
    if (!tex) {
      tex = fieldFrames('cinder', r, FX_RAMPS.flame);
      this.fieldTexCache.set(key, tex);
    }
    this.placeField(x, y, r, damage, 1.6, 0, status, tex, 0, 0.85);
  }

  private placeField(
    x: number, y: number, radius: number, damage: number, duration: number, slow: number,
    status: StatusApply, tex: Texture[], spin: number, alphaMax: number, opts: FieldOpts = {},
  ): void {
    const f = this.fields.spawn();
    if (!f) return;
    f.x = x;
    f.y = y;
    f.follow = opts.follow ?? false;
    f.radius = radius;
    f.damage = damage;
    f.life = duration;
    f.maxLife = duration;
    f.slow = slow;
    f.tick = 0;
    f.status = status;
    // 메자이: 안에 있으면 받는 피해가 준다 (예전에는 늘 0 이라 이 효과가 없었다)
    f.guard = opts.guard ?? 0;
    f.beam = opts.beam ?? 0;
    f.beamT = f.beam * 0.4;
    f.beamDamage = opts.beamDamage ?? 0;
    f.heal = opts.heal ?? 0;
    f.frames = tex.length > 1 ? tex : null;
    f.animT = this.rng.next();
    f.spin = spin * (this.rng.next() < 0.5 ? -1 : 1);
    f.alphaMax = alphaMax;
    f.sprite.texture = tex[0];
    f.sprite.rotation = this.rng.next() * Math.PI * 2 * (f.spin !== 0 ? 1 : 0);
    f.sprite.tint = 0xffffff;
    f.sprite.scale.set(1);
    f.sprite.position.set(Math.round(x), Math.round(y));
    f.sprite.visible = true;
  }

  vacuumGems(x: number, y: number, radius: number): void {
    const r2 = radius * radius;
    const items = this.gems.items;
    for (let i = 0; i < this.gems.count; i++) {
      const g = items[i];
      if (g.magnet || g.kind !== 'xp') continue;
      if ((g.x - x) ** 2 + (g.y - y) ** 2 <= r2) g.magnet = true;
    }
  }

  catchShots(x: number, y: number, radius: number, reflectDamage: number): number {
    const items = this.shots.items;
    const r2 = radius * radius;
    let n = 0;
    for (let i = 0; i < this.shots.count; i++) {
      const s = items[i];
      if (s.reflected) continue;
      if ((s.x - x) ** 2 + (s.y - y) ** 2 > r2) continue;
      n++;
      if (reflectDamage > 0) {
        // 되받아친다: 왔던 쪽으로 더 빠르게, 이제 적을 맞힌다
        s.reflected = true;
        s.orbit = 0;
        s.homing = 0;
        s.charm = 0;
        s.vx *= -1.4;
        s.vy *= -1.4;
        s.damage = reflectDamage;
        s.life = 1.6;
        s.sprite.tint = 0x9fd8ff;
        s.sprite.rotation += Math.PI;
      } else {
        // 흩는다
        if (n <= 3) this.spawnBurst(s.x, s.y, 16, 'grey', 'air', RAMPS.grey);
        s.sprite.visible = false;
        this.shots.removeAt(i);
        i--;
      }
    }
    return n;
  }

  pickTargets(x: number, y: number, radius: number, n: number): Enemy[] {
    const out = this.targetBuf;
    out.length = 0;
    const near = this.hash.query(x, y, radius, this.scratch4);
    const r2 = radius * radius;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead || (e.x - x) ** 2 + (e.y - y) ** 2 > r2) continue;
      out.push(e);
    }
    // 고르게 n 마리: 섞어서 앞에서부터
    for (let i = 0; i < Math.min(n, out.length); i++) {
      const j = i + this.rng.int(out.length - i);
      const t = out[i];
      out[i] = out[j];
      out[j] = t;
    }
    if (out.length > n) out.length = n;
    return out;
  }

  heavyHit(): void {
    if (this.hitstopCd > 0) return;
    this.hitstopT = 0.04;
    this.hitstopCd = 0.3;
  }

  shakeScreen(time: number, mag: number): void {
    this.shake(time, mag);
  }

  setBodyScale(k: number): void {
    this.bodyScaleNext = Math.max(this.bodyScaleNext, k);
  }

  setOverheal(cap: number, burstDamage: number, status: StatusApply): void {
    this.shieldCapNext = Math.max(this.shieldCapNext, cap);
    this.shieldBurst = burstDamage;
    this.shieldStatus = status;
  }

  /** 실제 시간으로 잠깐 느리게 (시뮬레이터에는 영향 없다) */
  private slowmo(sec: number, scale: number): void {
    this.slowT = Math.max(this.slowT, sec);
    this.slowScale = scale;
  }

  /** 화면 전체가 번쩍 */
  private flashFx(sec: number, alpha: number, tint: number): void {
    this.flashT = sec;
    this.flashMax = sec;
    this.flashAlpha = alpha;
    this.flash.tint = tint;
  }

  // ── 고유 스킬이 쓰는 것들 ──────────────────────────────

  /**
   * 적이 가장 두껍게 몰린 지점.
   * 전부 훑으면 비싸니 몇 마리만 뽑아서 그 주변 밀도를 세어 제일 나은 곳을 고른다.
   */
  densestSpot(x: number, y: number, maxRadius: number): { x: number; y: number } | null {
    const n = this.enemies.count;
    if (n === 0) return null;
    let best: { x: number; y: number } | null = null;
    let bestScore = -1;
    const tries = Math.min(14, n);
    for (let t = 0; t < tries; t++) {
      const e = this.enemies.items[this.rng.int(n)];
      if (e.dead) continue;
      const dx = e.x - x;
      const dy = e.y - y;
      if (dx * dx + dy * dy > maxRadius * maxRadius) continue;
      const near = this.hash.query(e.x, e.y, 150, this.scratch3);
      let score = 0;
      for (let k = 0; k < near.length; k++) {
        const i = near[k];
        if (i < this.enemies.count && !this.enemies.items[i].dead) score++;
      }
      if (score > bestScore) {
        bestScore = score;
        best = { x: e.x, y: e.y };
      }
    }
    return best;
  }

  teleport(x: number, y: number): void {
    this.px = x;
    this.py = y;
    this.iframe = Math.max(this.iframe, 0.35); // 적진 한복판에 떨어지므로 잠깐 무적
    this.shake(0.2, 8);
  }

  // ── 액티브 스킬 (사냥 준비의 도약) ─────────────────────

  get dashing(): boolean {
    return this.dash !== null;
  }

  /** 변신하거나 풀리는 연출 중인가 */
  get transforming(): boolean {
    return this.darkenT > 0 || (this.animator?.transforming ?? false);
  }

  /** HUD 의 액티브 스킬 칸들 — 먼저 얻은 것이 Space, 다음이 E (대시 칸은 따로, Shift) */
  private activeSnapshot(): HudSnapshot['actives'] {
    const out: HudSnapshot['actives'] = [];
    for (const w of this.weapons) {
      const st = w.activeState;
      if (!st) continue;
      out.push({ name: w.def.name, color: w.def.color, shape: w.def.shape, sprite: w.def.sprite, key: ACTIVE_KEYS[out.length] ?? '', ...st });
      if (out.length >= ACTIVE_KEYS.length) break;
    }
    return out;
  }

  /** 화면 위 기력 게이지 (회광반조) */
  private vigorSnapshot(): HudSnapshot['vigor'] {
    let i = 0;
    for (const w of this.weapons) {
      if (!w.isActive) continue;
      if (w.toggleForm) {
        const on = w.formActive;
        return { value: w.formVigor * 100, on, tired: w.formTired, ready: !on && w.formVigor >= 1, key: ACTIVE_KEYS[i] ?? '' };
      }
      i++;
    }
    return null;
  }

  /** 1.24 지금 대시의 값 — 전격 질주를 가지고 있으면 그 무기의 수치로 (없으면 기본 대시) */
  private dashSpec(): { dist: number; dur: number; cd: number; iframe: number; charges: number; damage: number; status?: StatusApply; zap: boolean } {
    const w = this.weapons.find((v) => v.def.behavior === 'dash');
    if (!w) return { ...DASH_BASE, charges: 1, damage: 0, zap: false };
    const ps = this.playerStats;
    const dist = Math.max(60, w.stats.leap);
    return {
      dist,
      // 멀리 갈수록 조금 더 오래 — 기본 대시와 같은 빠르기(약 1,100px/초)로 느껴지게. 0.2초를 넘기지는 않는다
      dur: Math.min(0.2, 0.05 + dist / 1900),
      cd: Math.max(0.6, w.stats.cooldown * ps.cooldownMul),
      iframe: 0.3,
      charges: Math.max(1, w.stats.charges),
      damage: w.stats.damage * ps.damageMul * ps.magicMul,
      status: w.status,
      zap: true,
    };
  }

  /**
   * 1.24 대시 — Shift(폰은 버튼). 이동하는 쪽으로, 안 누르고 있으면 바라보는 쪽으로.
   * (dirX, dirY) 를 주면 그쪽으로 — 측정·시뮬레이션의 자동 조종이 쓴다.
   */
  useDash(dirX = 0, dirY = 0): boolean {
    if (this.state !== 'playing' || this.dash) return false;
    if (this.charmT > 0) return false; // [매혹] 중에는 못 쓴다
    if (this.dashCharges <= 0) return false;
    const d = this.dashSpec();
    let ux = dirX;
    let uy = dirY;
    let len = Math.hypot(ux, uy);
    if (len <= 0.01) {
      ux = this.input.dx;
      uy = this.input.dy;
      len = Math.hypot(ux, uy);
    }
    if (len > 0.01) {
      ux /= len;
      uy /= len;
    } else {
      ux = this.faceX;
      uy = this.faceY;
    }
    this.dashCharges--;
    if (this.dashCd <= 0) this.dashCd = d.cd;
    const x1 = this.px + ux * d.dist;
    const y1 = this.py + uy * d.dist;
    // 번개 대시는 지나간 줄을 긋는다
    if (d.damage > 0) this.bandDamage(this.px, this.py, ux, uy, d.dist, 44, d.damage, 150, d.status);
    this.zapDepart(this.px, this.py, x1, y1, d.zap);
    this.dash = {
      x0: this.px, y0: this.py, x1, y1, t: 0, dur: d.dur, dirX: ux, dirY: uy, spec: null,
      zap: d.zap ? { damage: d.damage, status: d.status } : null, ghostT: GHOST_STEP,
    };
    this.iframe = Math.max(this.iframe, d.iframe);
    if (d.zap) {
      audio.play('zap', { rate: 0.85, gain: 0.9 });
      audio.play('dash', { rate: 1.1, gain: 0.6 });
      this.shake(0.12, 5);
      this.flashFx(0.08, 0.1, 0x9a6bff);
    } else {
      audio.play('dash', { rate: 1.35, gain: 0.5 });
    }
    return true;
  }

  /**
   * 1.24 측정·시뮬레이션에서 사람 대신 대시를 쓴다 (autoActive — 도약과 같은 규칙).
   * 전격 질주를 들었으면 피해가 붙으므로 적이 가장 두꺼운 쪽으로 꿰뚫는다.
   * 맨몸 대시는 피해가 없으니 쿨마다 쓸 이유가 없다 — 위험할 때만, 자동 조종이 이미 잡아 둔
   * 도망갈 쪽(ix, iy)으로 쓴다. 위험은 "적이 100px 안" 또는 "바닥 경고가 220px 안".
   */
  private autoDash(ix: number, iy: number): void {
    if (!this.autoActive || this.dash || this.dashCharges <= 0 || this.charmT > 0) return;
    if (this.dashSpec().zap) {
      const spot = this.densestSpot(this.px, this.py, 620);
      if (spot) this.useDash(spot.x - this.px, spot.y - this.py);
      return;
    }
    if (ix === 0 && iy === 0) return;
    let danger = this.nearestEnemy(this.px, this.py, 100) !== null;
    if (!danger) {
      for (const h of hazards) {
        if (Math.hypot(this.px - h.x, this.py - h.y) < 220) {
          danger = true;
          break;
        }
      }
    }
    if (danger) this.useDash(ix, iy);
  }

  /**
   * 대시가 끝나는 자리. 닿은 자리에 잔상 한 장(몸에 가리지 않게 온 길 쪽으로 조금 물려서)과
   * 소용돌이를 남기고, 번개 대시면 도착 자리도 한 번 친다.
   */
  private landDash(d: DashState): void {
    const zap = !!d.zap;
    this.spawnGhost(d.x1 - d.dirX * 24, d.y1 - d.dirY * 24, zap, zap ? 0.5 : 0.3, zap ? 0.8 : 0.5);
    const tint = zap ? undefined : 0xa8dcff;
    this.spawnFxSprite({
      name: 'zap_swirl', x: d.x1, y: d.y1 - 12, rot: Math.atan2(d.dirY, d.dirX),
      life: zap ? 0.55 : 0.34, scale: zap ? 1.1 : 0.7, scale1: zap ? 1.7 : 1.05, alpha: zap ? 1 : 0.7, tint,
    });
    if (!d.zap) return;
    this.spawnFxSprite({
      name: 'zap_ring', frame: (Math.random() * 4) | 0, x: d.x1, y: d.y1 - 12,
      rot: Math.atan2(d.dirY, d.dirX) + Math.PI, life: 0.42, scale: 0.7, scale1: 1.2, alpha: 0.9,
    });
    this.areaDamage(d.x1, d.y1, 54, d.zap.damage * 0.5, 130, 0, 0, -1, 0, d.zap.status);
    this.spawnBurst(d.x1, d.y1, 58, 'yellow', 'spark');
  }

  /**
   * 1.24 대시가 떠나는 순간의 연출 — 어디서 어디로 가는지 한눈에 보이게.
   * 받은 시트(tools/art/전격질주.png)에서 뗀 그림: 고리(출발) · 줄기·꼬리(길) · 번개 조각(잔류).
   * 도착 쪽(소용돌이·도착 고리·닿은 자리 잔상)은 정말 닿았을 때 landDash 가 낸다.
   * 몸 잔상은 여기서 한 장(떠난 자리)만 남기고, 나머지는 질주하면서 흘린다(updatePlayer).
   * 기본 대시는 같은 그림을 작게·희푸르게, 전격 질주는 크게·보랏빛 그대로 오래 남긴다.
   */
  private zapDepart(x0: number, y0: number, x1: number, y1: number, zap: boolean): void {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dist = Math.hypot(dx, dy) || 1;
    const rot = Math.atan2(dy, dx);
    const tint = zap ? undefined : 0xa8dcff;
    const k = dist / 230;
    // ── 떠난 자리 ── 고리가 크게 번지고, 몸이 있던 자리에 잔상이 제일 오래 남는다
    this.spawnFxSprite({
      name: 'zap_ring', frame: (Math.random() * 4) | 0, x: x0, y: y0 - 12, rot,
      life: zap ? 0.5 : 0.32, scale: zap ? 1.05 : 0.62, scale1: zap ? 1.6 : 1.0, alpha: zap ? 1 : 0.7, tint,
    });
    this.spawnGhost(x0, y0, zap, zap ? 0.55 : 0.32, zap ? 0.85 : 0.55);
    this.spawnFxSprite({
      name: 'zap_tail', frame: (Math.random() * 4) | 0, x: x0 + dx * 0.16, y: y0 + dy * 0.16 - 14, rot,
      life: zap ? 0.42 : 0.26, scale: (zap ? 1.0 : 0.7) * Math.max(0.6, k), alpha: zap ? 0.85 : 0.5, tint,
    });
    // ── 지나갈 길 ── 번개가 한 줄로 내리꽂힌다. 줄기·조각은 몸보다 먼저 길을 긋는다(그래서 판정도 여기서 난다)
    const streaks = zap ? 3 : 2;
    for (let i = 0; i < streaks; i++) {
      const u = (i + 0.5) / streaks;
      this.spawnFxSprite({
        name: 'zap_streak', frame: i % 4, x: x0 + dx * u, y: y0 + dy * u - 12, rot,
        life: zap ? 0.7 : 0.38, scale: (zap ? 1.15 : 0.8) * Math.max(0.5, k), alpha: zap ? 0.95 : 0.6, tint,
      });
    }
    const bolts = zap ? 4 : 2;
    for (let i = 0; i < bolts; i++) {
      const u = (i + 0.35) / bolts;
      this.spawnFxSprite({
        name: 'zap_bolt', frame: i % 4, x: x0 + dx * u, y: y0 + dy * u - 10 + (Math.random() - 0.5) * 16,
        rot: rot + (Math.random() - 0.5) * 0.7,
        life: zap ? 1.1 : 0.5, scale: zap ? 0.95 : 0.6, alpha: zap ? 0.9 : 0.5, tint,
      });
    }
    if (zap) {
      this.spawnFxSprite({
        name: 'zap_tail', frame: 2, x: x0 + dx * 0.5, y: y0 + dy * 0.5 - 14, rot,
        life: 0.5, scale: 1.0 * Math.max(0.6, k), alpha: 0.8,
      });
    }
  }

  /**
   * 액티브 스킬을 쓴다 — i 번째 액티브(0 = Space, 1 = E) / 화면 버튼.
   * 도약은 지금 누르고 있는 이동 방향(멈춰 있으면 바라보는 쪽)으로, 변신은 켜고 끈다. 쓸 수 없으면 false.
   */
  useActive(i = 0): boolean {
    if (this.state !== 'playing') return false;
    if (this.charmT > 0) return false; // 1.22 [매혹] — 홀린 동안은 액티브도 안 된다
    const w = this.weapons.filter((v) => v.isActive)[i];
    if (!w) return false;
    return w.activate(this, this.input.dx, this.input.dy);
  }

  /** 도약을 시작한다. 지나갈 길의 적은 바로 할퀴고(착지 피해의 40%), 착지는 날아간 뒤에 터진다. */
  leap(spec: LeapSpec): boolean {
    if (this.dash) return false;
    const x1 = this.px + spec.dirX * spec.distance;
    const y1 = this.py + spec.dirY * spec.distance;
    this.bandDamage(this.px, this.py, spec.dirX, spec.dirY, spec.distance, 46, spec.damage * 0.4, spec.knockback * 0.4, spec.status);
    this.spawnBolt(this.px, this.py, x1, y1, 'streak', 0xff8fa3);
    this.dash = { x0: this.px, y0: this.py, x1, y1, t: 0, dur: DASH_TIME, dirX: spec.dirX, dirY: spec.dirY, spec, zap: null, ghostT: -1 };
    return true;
  }

  private landLeap(d: DashState): void {
    const s = d.spec;
    if (!s) return; // 도약(사냥 준비)일 때만 부른다 — 1.24 기본 대시는 landDash 로 간다
    const before = this.kills;
    this.areaDamage(d.x1, d.y1, s.radius, s.damage, s.knockback, 0, 0, -1, 0, s.status);
    this.heavyHit();
    s.onLand?.(this.kills - before);
    this.spawnBurst(d.x1, d.y1, s.radius, s.attr, s.burst, s.ramp);
    if (s.burstSfx) audio.play(s.burstSfx);
    this.iframe = Math.max(this.iframe, 0.3); // 적진 한복판에 떨어지므로 잠깐 무적
    this.shake(0.2, 8);
  }

  palmStrike(
    x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number,
    damage: number, knockback: number, status: StatusApply, consume: number, bonus: number, big: boolean,
  ): number {
    let hits = 0;
    const items = this.enemies.items;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = items[i];
      if (e.dead) continue;
      const rx = e.x - x;
      const ry = e.y - y;
      const along = rx * dirX + ry * dirY;
      if (along < -e.radius || along > length + e.radius) continue;
      if (Math.abs(rx * -dirY + ry * dirX) > halfWidth + e.radius) continue;
      let d = damage;
      // [2] 쇄상: [서리]를 녹여 그만큼 더 아프게
      if (consume > 0 && e.frost > 0) {
        const c = Math.min(consume, e.frost);
        e.frost -= c;
        d *= 1 + bonus * c;
      }
      this.damageEnemy(e, d, dirX, dirY, knockback);
      this.applyStatus(e, status);
      hits++;
    }
    this.spawnPalmFx(x, y, dirX, dirY, length, halfWidth, big);
    return hits;
  }

  /** 한기 소용돌이가 손끝에서 띠 끝까지 날아가고, 끝에서 터진다 */
  private spawnPalmFx(x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number, big: boolean): void {
    const x0 = x + dirX * 14;
    const y0 = y + dirY * 14 - 20;
    const x1 = x + dirX * length;
    const y1 = y + dirY * length - 20;
    if (!this.palmTex) {
      this.spawnBolt(x0, y0, x1, y1, 'streak', 0x9fe8ff);
      return;
    }
    let fx = this.palmFx.find((f) => !f.sprite.visible);
    if (!fx) {
      if (this.palmFx.length >= 12) return;
      const sp = new Sprite(this.palmTex);
      sp.anchor.set(0.6, 0.5);
      this.fxLayer.addChild(sp);
      fx = { sprite: sp, t: 0, x0, y0, x1, y1, hw: halfWidth, big };
      this.palmFx.push(fx);
    }
    Object.assign(fx, { t: 0, x0, y0, x1, y1, hw: halfWidth, big });
    const sp = fx.sprite;
    sp.visible = true;
    sp.alpha = 1;
    sp.rotation = Math.atan2(dirY, dirX);
    // 소용돌이 높이가 띠 폭에 맞게 (위아래 뒤집힘은 방향 따라)
    const k = (halfWidth * 2.2) / this.palmTex.height;
    sp.scale.set(k, dirX < 0 ? -k : k);
    sp.position.set(Math.round(x0), Math.round(y0));
  }

  private updatePalmFx(dt: number): void {
    const FLY = 0.14;
    for (const f of this.palmFx) {
      if (!f.sprite.visible) continue;
      f.t += dt;
      const u = Math.min(1, f.t / FLY);
      const e = 1 - (1 - u) * (1 - u);
      f.sprite.position.set(Math.round(f.x0 + (f.x1 - f.x0) * e), Math.round(f.y0 + (f.y1 - f.y0) * e));
      if (f.t >= FLY && f.sprite.alpha === 1) {
        // 끝에 닿으면 한기가 터진다 (쇄상은 크게)
        this.spawnBurst(f.x1, f.y1 + 20, f.hw * (f.big ? 1.5 : 1.1), 'blue', f.big ? 'spark' : 'air', FX_RAMPS.ice);
        f.sprite.alpha = 0.99;
      }
      if (f.t > FLY) f.sprite.alpha = Math.max(0, 0.99 - (f.t - FLY) / 0.12);
      if (f.t > FLY + 0.12) f.sprite.visible = false;
    }
  }

  /** 시작점에서 dir 방향 length 까지, 반폭 halfWidth 의 띠 안에 걸친 적을 전부 때린다. 맞은 수. */
  private bandDamage(
    x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number,
    damage: number, knockback: number, status?: StatusApply,
  ): number {
    let hits = 0;
    const items = this.enemies.items;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = items[i];
      if (e.dead) continue;
      const rx = e.x - x;
      const ry = e.y - y;
      const along = rx * dirX + ry * dirY;
      if (along < -e.radius || along > length + e.radius) continue;
      const across = Math.abs(rx * -dirY + ry * dirX);
      if (across > halfWidth + e.radius) continue;
      this.damageEnemy(e, damage, dirX, dirY, knockback);
      this.applyStatus(e, status);
      hits++;
    }
    return hits;
  }

  burst(x: number, y: number, radius: number, attr: string, style: BurstStyle = 'ring', ramp?: readonly string[], delay = 0): void {
    this.spawnBurst(x, y, radius, attr, style, ramp, delay);
  }

  boltLine(x0: number, y0: number, x1: number, y1: number, style: 'arc' | 'static' | 'violet', scale = 1, delay = 0): void {
    this.spawnBolt(x0, y0, x1, y1, style, 0xffffff, delay, scale);
  }

  slowMotion(sec: number, scale: number): void {
    this.slowmo(sec, scale);
  }

  healPlayer(amount: number): void {
    if (amount <= 0) return;
    // 흡혈만으로 영원히 버티는 걸 막는다. 1초에 최대 체력의 22%까지만 돌려받는다.
    const cap = this.playerStats.maxHp * 0.22;
    const room = Math.max(0, cap - this.healedThisSecond);
    const given = Math.min(amount, room);
    this.healedThisSecond += given;
    const over = this.hp + given - this.playerStats.maxHp;
    this.hp = Math.min(this.playerStats.maxHp, this.hp + given);
    // 세포 역설 [과포화]: 넘친 몫은 보호막으로 남는다
    if (over > 0 && this.shieldCap > 0) this.shield = Math.min(this.shieldCap, this.shield + over);
  }

  /**
   * 지금 받는 피해가 얼마나 줄어드는지 (타이탄·메자이·변신). 0~GUARD_CAP.
   * 여러 개가 겹치면 큰 쪽 하나만 — 더하지 않는다.
   */
  private currentGuard(): number {
    // 회광반조로 변신한 동안은 받는 피해가 준다 (다른 감소와는 큰 쪽만)
    let g = this.form && this.darkenT <= 0 ? FORM_GUARD : 0;
    for (const w of this.weapons) {
      if (w.def.behavior === 'aura' && w.stats.guard > 0) g = Math.max(g, w.stats.guard);
    }
    for (let i = 0; i < this.fields.count; i++) {
      const f = this.fields.items[i];
      if (f.guard <= 0) continue;
      const dx = this.px - f.x;
      const dy = this.py - f.y;
      if (dx * dx + dy * dy <= f.radius * f.radius) g = Math.max(g, f.guard);
    }
    return Math.min(GUARD_CAP, g);
  }

  /**
   * 맵에 놓이는 픽업.
   * 가만히 서서 버티는 걸 막으려고 둔다 — 회복도 큰 경험치도 "보러 가야" 얻는다.
   */
  private updateMapPickups(dt: number): void {
    const P = CONFIG.PICKUP;
    const r = this.rng;

    this.healTimer -= dt;
    if (this.healTimer <= 0) {
      this.healTimer = r.range(P.HEAL_INTERVAL[0], P.HEAL_INTERVAL[1]);
      let onMap = 0;
      for (let i = 0; i < this.gems.count; i++) if (this.gems.items[i].kind === 'heal') onMap++;
      if (onMap < P.HEAL_MAX_ON_MAP) this.placePickup('heal', 0, P.HEAL_RATIO);
    }

    this.xpTimer -= dt;
    if (this.xpTimer <= 0) {
      this.xpTimer = r.range(P.XP_INTERVAL[0], P.XP_INTERVAL[1]);
      let onMap = 0;
      for (let i = 0; i < this.gems.count; i++) if (this.gems.items[i].onMap) onMap++;
      if (onMap < P.XP_MAX_ON_MAP) this.placePickup('xp', P.XP_VALUE, 0);
    }

    this.xpBigTimer -= dt;
    if (this.xpBigTimer <= 0) {
      this.xpBigTimer = r.range(P.XP_BIG_INTERVAL[0], P.XP_BIG_INTERVAL[1]);
      this.placePickup('xpBig', P.XP_BIG_VALUE, 0);
    }

    if (this.elapsed >= P.XP_HUGE_AFTER) {
      this.xpHugeTimer -= dt;
      if (this.xpHugeTimer <= 0) {
        this.xpHugeTimer = r.range(P.XP_HUGE_INTERVAL[0], P.XP_HUGE_INTERVAL[1]);
        this.placePickup('xpHuge', P.XP_HUGE_VALUE, 0);
      }
    }
  }

  /**
   * 표준 화면(CONFIG.VIEW_W×VIEW_H) 안이되 플레이어에게서 조금 떨어진 곳에 놓는다.
   * 실제 창 크기를 쓰지 않으므로, 폭이 좁은 폰에서는 화면 바로 밖에 놓일 수도 있다.
   */
  private placePickup(kind: PickupKind, value: number, healRatio: number): void {
    const g = this.gems.spawn();
    if (!g) return;
    const P = CONFIG.PICKUP;
    const halfW = CONFIG.VIEW_W / 2 - P.SPAWN_MARGIN;
    const halfH = CONFIG.VIEW_H / 2 - P.SPAWN_MARGIN;
    const maxR = Math.max(P.SPAWN_MIN + 40, Math.min(halfW, halfH));
    const a = this.rng.next() * Math.PI * 2;
    const d = this.rng.range(P.SPAWN_MIN, maxR);

    g.x = this.px + Math.cos(a) * d;
    g.y = this.py + Math.sin(a) * d;
    g.vx = 0;
    g.vy = 0;
    g.magnet = false;
    g.onMap = true;
    g.bob = this.rng.next() * Math.PI * 2;
    g.kind = kind === 'heal' ? 'heal' : 'xp';
    g.value = value;
    g.heal = healRatio;

    g.sprite.texture = this.pickupTex[kind];
    g.sprite.tint = 0xffffff;
    g.sprite.scale.set(1);
    g.sprite.position.set(Math.round(g.x), Math.round(g.y));
    g.sprite.visible = true;
  }

  // ── 탄환 / 장판 / 보석 ─────────────────────────────────

  private updateProjectiles(dt: number): void {
    const items = this.projectiles.items;
    for (let i = 0; i < this.projectiles.count; i++) {
      const p = items[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;
      p.sprite.position.set(Math.round(p.x), Math.round(p.y));
      p.sprite.rotation += p.spin * dt;
      if (p.frames) {
        p.animT += dt;
        p.sprite.texture = p.frames[Math.floor(p.animT * 14) % p.frames.length];
      }

      if (p.fall) {
        // 하늘에서 떨어지는 탄: 날아가는 동안은 안 맞고, 땅에 닿으면 터진다
        if (p.life <= 0) {
          this.land(p);
          p.sprite.visible = false;
          this.projectiles.removeAt(i);
          i--;
        }
        continue;
      }

      let consumed = false;
      const wide = p.halfWidth > 0;
      const near = this.hash.query(p.x, p.y, Math.max(p.radius, p.halfWidth) + 32, this.scratch);
      // 옆으로 긴 탄은 진행 방향 기준으로 앞뒤 radius, 좌우 halfWidth 의 띠로 맞는다
      const sp = wide ? Math.hypot(p.vx, p.vy) || 1 : 1;
      const ux = p.vx / sp;
      const uy = p.vy / sp;
      for (let k = 0; k < near.length; k++) {
        const j = near[k];
        if (j >= this.enemies.count) continue;
        const e = this.enemies.items[j];
        if (e.dead) continue;
        if (p.alreadyHit(e.uid)) continue;
        const dx = e.x - p.x;
        const dy = e.y - p.y;
        if (wide) {
          if (Math.abs(dx * ux + dy * uy) > p.radius + e.radius) continue;
          if (Math.abs(dx * -uy + dy * ux) > p.halfWidth + e.radius) continue;
        } else {
          const rr = p.radius + e.radius;
          if (dx * dx + dy * dy > rr * rr) continue;
        }

        // 맞는 소리는 탄 하나당 처음 한 번만 (관통 탄이 열 마리를 뚫어도 한 번)
        if (p.hitCount === 0 && p.look?.hit) audio.play(p.look.hit, { gain: p.look.hitGain ?? 1 });
        this.damageEnemy(e, p.damage, p.vx, p.vy, p.knockback);
        this.applyStatus(e, p.status);
        // 흡혈 탄(넘실이는 파도): 탄은 무기를 떠나 여기서 맞으므로, 되찾는 것도 여기서 한다
        if (p.lifesteal > 0) this.healPlayer(p.damage * p.lifesteal);
        p.markHit(e.uid);
        if (p.pierce <= 0) {
          consumed = true;
          break;
        }
        p.pierce--;
      }

      if (consumed || p.life <= 0) {
        if (p.explodeRadius > 0) this.explode(p);
        p.sprite.visible = false;
        this.projectiles.removeAt(i);
        i--;
      }
    }
  }

  /** 탄이 터진다. 직격과 별개로 주변에 한 번 더 피해를 준다. */
  private explode(p: Projectile): void {
    const hits = this.areaDamage(p.x, p.y, p.explodeRadius, p.explodeDamage, p.knockback * 0.6, 0, 0, -1, 0, p.status ?? undefined);
    if (p.lifesteal > 0 && hits > 0) this.healPlayer(hits * p.explodeDamage * p.lifesteal);
    this.spawnBurst(p.x, p.y, p.explodeRadius, p.attr, p.look?.burst ?? 'ring', p.ramp ?? undefined);
    if (p.look?.burstSfx) audio.play(p.look.burstSfx);
    // 불바다: 터진 자리에 불이 남는다
    if (p.pool) this.spawnCinder(p.x, p.y, p.explodeRadius * 0.9, p.explodeDamage * 0.25, { burn: 2, burnDmg: p.status?.burnDmg });
  }

  /** 하늘에서 떨어진 탄이 땅에 닿았다: 한가운데는 제 피해를 다, 둘레는 폭발 피해 */
  private land(p: Projectile): void {
    const hits = this.areaDamage(p.x, p.y, p.radius * 2, p.damage, p.knockback, 0, 0, -1, 0, p.status ?? undefined);
    if (hits > 0 && p.look?.hit) audio.play(p.look.hit, { gain: p.look.hitGain ?? 1 });
    if (hits > 0 && p.look?.heavy) this.heavyHit();
    this.explode(p);
    this.shake(0.08, 3);
  }

  /** 폭발 자국. 프레임을 짧게 재생하고 다시 풀로 돌려보낸다. */
  /**
   * 폭발 그림을 미리 굽는다 — 처음 보는 폭발을 판 도중에 굽느라 멈칫했다(큰 불폭발은 한 번에 0.1~0.17초).
   * 목록(data/fxwarm.json)은 모든 무기·고유 스킬과 캐릭터 여덟으로 판을 돌리며 실제로 나온 것을 모았다.
   * 모양·색·크기가 같으면 spawnBurst 와 같은 열쇠라, 게임에서는 구워 둔 것을 그대로 쓴다. 로딩 중 약 1초.
   * 목록에 없는 폭발은 예전처럼 처음 나올 때 굽는다.
   */
  async warmFx(list: ReadonlyArray<readonly unknown[]>, loadBudgetMs = 1500): Promise<void> {
    // 작은 것부터 — 초반에 나오는 작은 폭발부터 준비되고, 비싼 큰 폭발(늦게 나온다)은 뒤로
    const items = [...list].sort((a, b) => (a[2] as number) - (b[2] as number));
    const bake = (item: readonly unknown[]): void => {
      const [style, attr, r, ramp] = item as [BurstStyle, string, number, string[] | undefined];
      const pal = ramp ?? RAMPS[attr as Attr] ?? RAMPS.white;
      const key = `${style}:${pal[2]}:${r}`;
      if (!this.burstCache.has(key)) this.burstCache.set(key, burstFrames(style, r, pal));
    };
    // 1) 로딩 중: 정해진 시간까지만 굽는다(느린 폰에서도 로딩이 길어지지 않게)
    const start = performance.now();
    let slice = start;
    let i = 0;
    while (i < items.length && performance.now() - start < loadBudgetMs) {
      bake(items[i++]);
      if (performance.now() - slice > 50) {
        await new Promise((res) => setTimeout(res, 0)); // 브라우저가 멈춘 것처럼 보이지 않게
        slice = performance.now();
      }
    }
    // 2) 나머지는 판이 멈춰 있을 때(메뉴·레벨업·일시정지)만 하나씩 굽는다 — 싸우는 도중에는 절대 안 굽는다
    for (const item of items.slice(i)) this.warmQueue.push(() => bake(item));
    this.pumpWarm();
  }

  /** 대기열의 그림을 판이 멈춰 있을 때만 하나씩 굽는다 */
  private pumpWarm(): void {
    if (this.warmPumping) return;
    this.warmPumping = true;
    const tick = (): void => {
      if (this.warmQueue.length === 0) {
        this.warmPumping = false;
        return;
      }
      if (this.state !== 'playing') this.warmQueue.shift()!();
      setTimeout(tick, this.state !== 'playing' ? 8 : 250);
    };
    setTimeout(tick, 0);
  }

  /**
   * 레벨업 카드가 뜬 동안 — 각 카드를 고르면 필요해질 무거운 그림(장판·폭발)을 미리 굽는다.
   * 사람이 카드를 읽는 동안 굽고, 고르는 순간에는 이미 구워져 있다(큰 조합은 고르는 순간 0.5초 가까이 걸렸다).
   * 게임에는 아무 영향 없다 — 어차피 나중에 구울 그림을 앞당길 뿐이다.
   */
  private prewarmChoices(choices: readonly LevelUpChoice[]): void {
    const queued: WeaponHost = {
      playerStats: this.playerStats,
      warmField: (def: WeaponDef, radius: number) => this.warmQueue.push(() => this.warmField(def, radius)),
      warmBurst: (radius: number, attr: string, style: BurstStyle, ramp?: readonly string[]) =>
        this.warmQueue.push(() => this.warmBurst(radius, attr, style, ramp)),
    } as unknown as WeaponHost;
    for (const c of choices) {
      const previews: WeaponInstance[] = [];
      if (c.kind === 'fusion') {
        const def = this.data.fusions.find((f) => f.id === c.id);
        if (def) previews.push(new WeaponInstance(def, true));
      } else if (c.kind === 'weapon-new') {
        const def = this.data.weapons.find((w) => w.id === c.id);
        if (def) previews.push(new WeaponInstance(def));
      } else if (c.kind === 'weapon-up') {
        const w = this.weapons.find((x) => x.def.id === c.id);
        if (w) {
          // 지금 무기와 같은 레벨·갈래로 만든 뒤 한 단계 올린다. 갈림길이면 갈래마다
          const picks = c.branches?.length ? c.branches.map((b) => b.id) : [undefined];
          for (const pick of picks) {
            const p = new WeaponInstance(w.def, w.isFusion);
            let bi = 0;
            while (p.level < w.level) p.levelUp(p.atFork ? w.picks[bi++] : undefined);
            p.levelUp(pick);
            previews.push(p);
          }
        }
      }
      for (const p of previews) {
        p.warmTextures(queued);
        p.destroy();
      }
    }
    this.pumpWarm();
  }

  /** 폭발 그림 (반경 8px 단위로 구워 둔다) */
  private burstTex(radius: number, attr: string, style: BurstStyle, ramp?: readonly string[]): FxFrames {
    const r = Math.round(radius / 8) * 8;
    const pal = ramp ?? RAMPS[attr as Attr] ?? RAMPS.white;
    const key = `${style}:${pal[2]}:${r}`;
    let frames = this.burstCache.get(key);
    if (!frames) {
      frames = burstFrames(style, r, pal);
      this.burstCache.set(key, frames);
    }
    return frames;
  }

  /** 폭발 그림을 미리 굽는다 — 레벨업 카드를 고른 순간(판이 멈춰 있을 때) 부른다 */
  warmBurst(radius: number, attr: string, style: BurstStyle, ramp?: readonly string[]): void {
    this.burstTex(radius, attr, style, ramp);
  }

  private spawnBurst(
    x: number, y: number, radius: number, attr: string,
    style: BurstStyle = 'ring', ramp?: readonly string[], delay = 0,
  ): void {
    const b = this.bursts.find((v) => !v.sprite.visible);
    if (!b) return;
    const frames = this.burstTex(radius, attr, style, ramp);
    b.frames = frames;
    b.t = 0;
    b.wait = delay;
    b.sprite.alpha = delay > 0 ? 0 : 1;
    b.sprite.texture = frames.textures[0];
    b.sprite.anchor.set(frames.anchorX, frames.anchorY);
    b.sprite.position.set(Math.round(x), Math.round(y));
    b.sprite.visible = true;
  }

  private updateBursts(dt: number): void {
    for (const b of this.bursts) {
      if (!b.sprite.visible || !b.frames) continue;
      if (b.wait > 0) {
        b.wait -= dt;
        if (b.wait > 0) continue;
        b.sprite.alpha = 1;
      }
      b.t += dt;
      const i = Math.floor(b.t * (b.frames.fps ?? 26));
      if (i >= b.frames.textures.length) {
        b.sprite.visible = false;
        continue;
      }
      b.sprite.texture = b.frames.textures[i];
    }
  }

  private updateFields(dt: number): void {
    const items = this.fields.items;
    for (let i = 0; i < this.fields.count; i++) {
      const f = items[i];
      f.life -= dt;
      f.tick -= dt;
      if (f.follow) {
        f.x = this.px;
        f.y = this.py;
        f.sprite.position.set(Math.round(f.x), Math.round(f.y));
      }
      f.sprite.alpha = f.alphaMax * Math.min(1, f.life / Math.max(f.maxLife * 0.3, 0.001));
      if (f.spin !== 0) f.sprite.rotation += f.spin * dt;
      if (f.frames) {
        f.animT += dt;
        f.sprite.texture = f.frames[Math.floor(f.animT * 5) % f.frames.length];
      }
      if (f.tick <= 0) {
        f.tick = 0.4;
        this.areaDamage(f.x, f.y, f.radius, f.damage, 0, 0.6, f.slow, -1, 0, f.status ?? undefined);
      }
      // 메자이: 영역 안에 서 있으면 회복, 그리고 영역 안 가장 센 적에게 황금 광선
      if (f.heal > 0 && (this.px - f.x) ** 2 + (this.py - f.y) ** 2 <= f.radius * f.radius) {
        this.healPlayer(this.playerStats.maxHp * f.heal * dt);
      }
      if (f.beam > 0) {
        f.beamT -= dt;
        if (f.beamT <= 0) {
          f.beamT = f.beam;
          this.judgeBeam(f.x, f.y, f.radius, f.beamDamage);
        }
      }
      if (f.life <= 0) {
        f.sprite.visible = false;
        this.fields.removeAt(i);
        i--;
      }
    }
  }

  /** 메자이 심판의 눈: 영역 안에서 가장 센 적(보스 → 엘리트 → 체력 순)에게 하늘에서 황금 광선 */
  private judgeBeam(x: number, y: number, radius: number, damage: number): void {
    const near = this.hash.query(x, y, radius + this.maxEnemyRadius, this.scratch4);
    let best: Enemy | null = null;
    let bestScore = -1;
    for (let k = 0; k < near.length; k++) {
      const i = near[k];
      if (i >= this.enemies.count) continue;
      const e = this.enemies.items[i];
      if (e.dead || (e.x - x) ** 2 + (e.y - y) ** 2 > radius * radius) continue;
      const score = e.hp + (e.boss ? 1e9 : e.elite ? 1e8 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = e;
      }
    }
    if (!best) return;
    this.damageEnemy(best, damage, 0, 0, 0);
    this.applyStatus(best, { stun: 0.4 });
    this.spawnBolt(best.x, best.y - 340, best.x, best.y, 'beam', 0xffe39a);
    this.spawnBurst(best.x, best.y, 44, 'yellow', 'air', FX_RAMPS.gold);
    audio.play('beam');
    this.heavyHit();
  }

  /** 엘리트가 떨군 보물 상자. 주우면 카드 한 장을 더 고른다. 멀리 있다고 재활용되지 않는다 */
  private dropChest(x: number, y: number): void {
    let g = this.gems.spawn();
    if (!g) {
      // 풀이 꽉 찼으면 가장 먼 경험치 보석 자리를 빌린다(그 경험치는 가장 가까운 보석에 얹을 수 없으니 상자 쪽이 우선)
      let worst = -1;
      let worstD = -1;
      for (let i = 0; i < this.gems.count; i++) {
        const q = this.gems.items[i];
        if (q.onMap) continue;
        const d = (q.x - this.px) ** 2 + (q.y - this.py) ** 2;
        if (d > worstD) {
          worstD = d;
          worst = i;
        }
      }
      if (worst < 0) return;
      g = this.gems.items[worst];
    }
    g.x = x;
    g.y = y;
    g.vx = 0;
    g.vy = 0;
    g.value = 0;
    g.magnet = false;
    g.kind = 'chest';
    g.heal = 0;
    g.onMap = true;
    g.bob = 0;
    g.sprite.texture = this.chestTex;
    g.sprite.tint = 0xffffff;
    g.sprite.scale.set(1);
    g.sprite.position.set(Math.round(x), Math.round(y));
    g.sprite.visible = true;
  }

  private dropGem(x: number, y: number, value: number): void {
    let g = this.gems.spawn();
    if (!g) {
      // 풀이 꽉 찼다. 안 주운 보석이 바닥에 깔려 새 보석이 안 생기면
      // 후반에 아무리 죽여도 경험치가 안 들어온다(실제로 그랬다).
      // 제일 멀리 있는 = 어차피 못 주울 보석을 새 자리로 옮기되, 그 경험치는 버리지 않고 새 보석에 얹는다.
      // (예전에는 버렸다. 6분 이후 떨어진 경험치의 95% 넘게가 사라져서, 레벨업 속도가 처치가 아니라
      //  플레이어가 밟고 지나간 땅 넓이로 정해졌다 — 멀리서 죽이는 조합일수록 레벨이 안 올랐다.)
      // 맵 픽업은 건드리지 않는다.
      let worst = -1;
      let worstD = -1;
      for (let i = 0; i < this.gems.count; i++) {
        const q = this.gems.items[i];
        if (q.onMap) continue;
        const d = (q.x - this.px) ** 2 + (q.y - this.py) ** 2;
        if (d > worstD) {
          worstD = d;
          worst = i;
        }
      }
      if (worst < 0) return;
      g = this.gems.items[worst];
      if (g.kind === 'xp') value += g.value;
    }
    // 멀리서 묵어 치운 보석들의 경험치를 여기 얹는다
    value += this.xpCarry;
    this.xpCarry = 0;
    g.age = 0;
    g.x = x;
    g.y = y;
    g.vx = 0;
    g.vy = 0;
    g.value = value;
    g.magnet = false;
    g.kind = 'xp';
    g.heal = 0;
    g.onMap = false;
    g.sprite.texture = this.gemTex;
    g.sprite.tint = value >= 10 ? 0xffd76e : value >= 4 ? 0xc79ff0 : 0xffffff;
    g.sprite.scale.set(Math.min(2.2, 0.9 + value * 0.05));
    g.sprite.position.set(Math.round(x), Math.round(y));
    g.sprite.visible = true;
  }

  private updateGems(dt: number): void {
    const items = this.gems.items;
    const pickR = this.playerStats.pickupRadius;
    for (let i = 0; i < this.gems.count; i++) {
      const g = items[i];
      const dx = this.px - g.x;
      const dy = this.py - g.y;
      const d = Math.hypot(dx, dy) || 1;
      g.age += dt;

      // 어느 화면에서도 안 보일 만큼 멀리서 오래 묵은 경험치 보석은 치운다(후반 렉).
      // 경험치는 버리지 않고 다음에 떨어지는 보석에 얹는다 — 보석이 꽉 찼을 때 먼 보석을 새 자리로 옮기던 것과 같은 셈이다.
      // 상자·회복 구슬·맵 픽업은 건드리지 않는다.
      if (g.kind === 'xp' && !g.magnet && !g.onMap && g.age > GEM_EXPIRE_AGE && d > GEM_EXPIRE_DIST) {
        this.xpCarry += g.value;
        g.sprite.visible = false;
        this.gems.removeAt(i);
        i--;
        continue;
      }

      if (!g.magnet && d < pickR) {
        g.magnet = true;
        g.magT = 0;
      }

      if (g.onMap && !g.magnet) {
        // 멀리서도 눈에 띄게 위아래로 떠 있는다
        const t = this.elapsed * 3.4 + g.bob;
        g.sprite.position.set(Math.round(g.x), Math.round(g.y + Math.sin(t) * 3));
        // 회복 구슬은 크기까지 뛴다. 이건 놓치면 안 되는 물건이다.
        if (g.kind === 'heal') g.sprite.scale.set(1 + Math.sin(t * 1.6) * 0.14);
      }

      if (g.magnet) {
        const ax = (dx / d) * CONFIG.GEM_MAGNET_ACCEL;
        const ay = (dy / d) * CONFIG.GEM_MAGNET_ACCEL;
        g.vx += ax * dt;
        g.vy += ay * dt;
        // 옆으로 미끄러지는 몫만 조금씩 뺀다 — 처음의 와리가리는 그대로, 오래 돌수록 더 빼서 결국 들어온다.
        // (예전에는 이게 없어 몇 개는 20초 넘게 몸 둘레를 돌았다)
        g.magT += dt;
        const ux = dx / d;
        const uy = dy / d;
        const along = g.vx * ux + g.vy * uy;
        const keep = Math.exp(-(GEM_SIDE_DAMP + g.magT * GEM_SIDE_DAMP_GROW) * dt);
        g.vx = along * ux + (g.vx - along * ux) * keep;
        g.vy = along * uy + (g.vy - along * uy) * keep;
        const sp = Math.hypot(g.vx, g.vy);
        if (sp > CONFIG.GEM_MAGNET_MAX) {
          g.vx = (g.vx / sp) * CONFIG.GEM_MAGNET_MAX;
          g.vy = (g.vy / sp) * CONFIG.GEM_MAGNET_MAX;
        }
        g.x += g.vx * dt;
        g.y += g.vy * dt;
        g.sprite.position.set(Math.round(g.x), Math.round(g.y));
      }

      if (d < CONFIG.PLAYER_RADIUS + GEM_CATCH) {
        if (g.kind === 'heal') {
          audio.play('levelup');
          this.healPlayer(this.playerStats.maxHp * g.heal);
        } else if (g.kind === 'chest') {
          this.pendingChests++;
        } else {
          audio.play('gem');
          this.gainXp(g.value);
        }
        g.sprite.visible = false;
        this.gems.removeAt(i);
        i--;
      }
    }
  }

  private gainXp(value: number): void {
    this.xp += value * this.playerStats.xpMul;
    while (this.xp >= this.xpNeed) {
      this.xp -= this.xpNeed;
      this.level++;
      this.xpNeed = CONFIG.xpToNext(this.level);
      this.pendingLevelUps++;
    }
  }

  // ── 레벨업 선택 적용 ───────────────────────────────────

  applyChoice(choice: LevelUpChoice): void {
    switch (choice.kind) {
      case 'weapon-up': {
        this.weapons.find((w) => w.def.id === choice.id)?.levelUp(choice.pick);
        break;
      }
      case 'weapon-new': {
        const def = this.data.weapons.find((w) => w.id === choice.id);
        if (def && this.weapons.length < CONFIG.MAX_WEAPONS) this.weapons.push(new WeaponInstance(def));
        break;
      }
      case 'passive-up': {
        const p = this.passives.find((x) => x.def.id === choice.id);
        if (p && p.level < p.def.maxLevel) p.level++;
        break;
      }
      case 'passive-new': {
        const def = this.data.passives.find((p) => p.id === choice.id);
        if (def && this.passives.length < CONFIG.MAX_PASSIVES) this.passives.push({ def, level: 1 });
        break;
      }
      case 'fusion': {
        const def = this.data.fusions.find((f) => f.id === choice.id);
        if (def && choice.consumes) {
          // 재료를 없애고 그 자리에 하나를 넣는다. 슬롯이 둘 비므로 새 무기를 더 받을 수 있다.
          const eaten = choice.consumes;
          const kept: WeaponInstance[] = [];
          for (const w of this.weapons) {
            if (eaten.includes(w.def.id)) w.destroy();
            else kept.push(w);
          }
          this.weapons = kept;
          this.weapons.push(new WeaponInstance(def, true));
          // 조합하는 순간: 0.6초 느려지고, 화면이 번쩍이고, 이름 띠가 뜨고, 전용 소리
          audio.play('fusion');
          this.slowmo(0.6, 0.25);
          this.flashFx(0.25, 0.2, 0xfff1c0); // 슬로모션 중에는 게임 시간이 느려 실제로는 1초쯤 남는다
          this.shake(0.3, 6);
          this.cb.onBanner(def.name.replace('【고유】 ', ''), def.origin ?? '고유 스킬', def.color, 'fusion');
        }
        break;
      }
      case 'heal':
        this.hp = Math.min(this.playerStats.maxHp, this.hp + this.playerStats.maxHp * 0.3);
        if (this.activeChoiceSource === 'level') {
          // 경험치는 이미 소모했다. 치유를 고른 레벨업만 취소한다.
          this.level = Math.max(1, this.level - 1);
          this.xpNeed = CONFIG.xpToNext(this.level);
          // 새 레벨의 필요 경험치보다 남은 경험치가 많으면 다음 선택을 예약한다.
          this.gainXp(0);
        }
        break;
    }

    this.refreshPlayerStats();
    // 새 레벨·새 범위에 맞는 이펙트 그림을 판이 멈춰 있는 지금 굽는다(다음 프레임에 굽느라 멈칫하지 않게)
    for (const w of this.weapons) w.prepareFx(this);

    if (this.pendingLevelUps > 0 || this.pendingChests > 0) {
      const chest = this.pendingLevelUps <= 0;
      if (chest) this.pendingChests--;
      else this.pendingLevelUps--;
      this.activeChoiceSource = chest ? 'chest' : 'level';
      this.lastChoices = buildChoices(
        this.rng, this.data.weapons, this.data.passives, this.weapons, this.passives,
        this.data.fusions, this.hp / this.playerStats.maxHp, this.character.id,
        this.character.trait?.choices ?? CONFIG.LEVELUP_CHOICES,
      );
      this.prewarmChoices(this.lastChoices);
      this.cb.onLevelUp(this.lastChoices, chest ? 'chest' : 'level');
      return;
    }
    this.activeChoiceSource = null;
    this.input.reset();
    this.setState('playing');
  }

  /** 패시브 + 캐릭터 특성(베기/마법 배율, 경험치)을 합친 스탯 */
  private statsFor(ch: CharacterDef): PlayerStats {
    const s = computePlayerStats(ch.maxHp, ch.moveSpeed, this.passives);
    s.slashMul = 1 + (ch.trait?.slash ?? 0);
    s.magicMul = 1 + (ch.trait?.magic ?? 0);
    s.xpMul += ch.trait?.xp ?? 0;
    s.damageMul += ch.trait?.damage ?? 0;
    return s;
  }

  /** 패시브 변화가 최대 체력 등에 반영되도록 다시 계산한다. 최대 체력이 늘면 그만큼 채워 준다. */
  private refreshPlayerStats(): void {
    const before = this.playerStats.maxHp;
    this.playerStats = this.statsFor(this.character);
    const gained = this.playerStats.maxHp - before;
    if (gained > 0) this.hp += gained;
    this.hp = Math.min(this.hp, this.playerStats.maxHp);
  }

  // ── 개발 모드 (dev/devpanel.ts 가 쓴다) ────────────────
  // 레벨업 카드를 건너뛰고 바로 바꾼다. 무기 칸(MAX_WEAPONS)·패시브 칸(MAX_PASSIVES)·만렙은 지킨다
  // — 무기 칸은 적마다 칸 번호로 재타격 시간을 재므로 넘으면 안 된다.

  /** 판이 돌고 있는가 (메뉴·결과 화면에서는 바꿀 것이 없다) */
  get devActive(): boolean {
    return this.state === 'playing' || this.state === 'paused' || this.state === 'levelup';
  }

  /** 지금 가진 무기·패시브 (사본) */
  devLoadout(): {
    weapons: Array<{ id: string; level: number; picks: string[]; fusion: boolean }>;
    passives: Array<{ id: string; level: number }>;
  } {
    return {
      weapons: this.weapons.map((w) => ({ id: w.def.id, level: w.level, picks: [...w.picks], fusion: w.isFusion })),
      passives: this.passives.map((p) => ({ id: p.def.id, level: p.level })),
    };
  }

  /** 레벨을 바로 정한다. 경험치는 그 레벨의 0부터, 카드는 안 뜬다. */
  devSetLevel(level: number): void {
    this.level = Math.max(1, Math.floor(level));
    this.xp = 0;
    this.xpNeed = CONFIG.xpToNext(this.level);
  }

  /** 레벨업을 한 번 준다 — 평소처럼 카드가 뜬다(일시정지 중이면 계속할 때). */
  devGrantLevelUp(): void {
    this.level++;
    this.xp = 0;
    this.xpNeed = CONFIG.xpToNext(this.level);
    this.pendingLevelUps++;
  }

  /**
   * 무기(고유 스킬 포함)를 이 레벨·갈래로 맞춘다. 없으면 빈 칸에 넣고, level 0 이면 뺀다.
   * picks 는 갈림길 순서대로의 갈래 id — 모자라거나 틀리면 첫 갈래. 칸이 없으면 false.
   */
  devSetWeapon(id: string, level: number, picks: readonly string[] = []): boolean {
    const idx = this.weapons.findIndex((w) => w.def.id === id);
    if (level <= 0) {
      if (idx < 0) return false;
      this.weapons[idx].destroy();
      this.weapons.splice(idx, 1);
      return true;
    }
    const fusion = this.data.fusions.find((f) => f.id === id);
    const def = fusion ?? this.data.weapons.find((w) => w.id === id);
    if (!def) return false;
    if (idx < 0 && this.weapons.length >= CONFIG.MAX_WEAPONS) return false;
    const w = new WeaponInstance(def, !!fusion);
    let b = 0;
    // picks 는 갈림길마다 한 개씩 (고를 것 없는 갈림길 자리도 센다 — 틀리면 levelUp 이 고를 수 있는 첫 갈래로)
    while (w.level < Math.min(level, def.maxLevel)) w.levelUp(w.atFork ? picks[b++] : undefined);
    if (idx >= 0) {
      this.weapons[idx].destroy();
      this.weapons[idx] = w; // 같은 칸에 둔다
    } else {
      this.weapons.push(w);
    }
    return true;
  }

  /**
   * 고유 스킬 10종을 한 번에 넣는다(개발 전용 — 무기 칸 5개 제한을 넘긴다). 이미 가진 것은 레벨만 맞춘다.
   * 적마다 무기 칸 번호로 재타격 시간을 재는데, 그 칸이 16개라 기본 무기 + 고유 10종까지는 괜찮다.
   */
  devAddAllFusions(max: boolean): number {
    let n = 0;
    for (const def of this.data.fusions) {
      const lv = max ? def.maxLevel : 1;
      const idx = this.weapons.findIndex((w) => w.def.id === def.id);
      const w = new WeaponInstance(def, true);
      while (w.level < lv) w.levelUp();
      if (idx >= 0) {
        this.weapons[idx].destroy();
        this.weapons[idx] = w;
      } else {
        if (this.weapons.length >= 16) break;
        this.weapons.push(w);
      }
      n++;
    }
    return n;
  }

  /** 패시브를 이 레벨로 맞춘다. 없으면 빈 칸에 넣고, level 0 이면 뺀다. 칸이 없으면 false. */
  devSetPassive(id: string, level: number): boolean {
    const idx = this.passives.findIndex((p) => p.def.id === id);
    if (level <= 0) {
      if (idx < 0) return false;
      this.passives.splice(idx, 1);
    } else {
      const def = this.data.passives.find((p) => p.id === id);
      if (!def) return false;
      const lv = Math.min(Math.floor(level), def.maxLevel);
      if (idx >= 0) this.passives[idx].level = lv;
      else if (this.passives.length < CONFIG.MAX_PASSIVES) this.passives.push({ def, level: lv });
      else return false;
    }
    this.refreshPlayerStats();
    return true;
  }

  /**
   * 1.22 개발자 패널: 적 하나를 플레이어 둘레(dist)에 부른다. 보스면 기술과 등장 띠까지.
   * 보스 체력은 첫 바퀴 그대로. 돌려주는 값은 부른 적 (콘솔에서 쓰기 좋게)
   */
  devSpawnEnemy(id: string, dist = 320): Enemy | null {
    if (this.state === 'idle' || this.state === 'over') return null;
    const def = this.data.enemies.find((d) => d.id === id);
    if (!def) return null;
    const e = this.spawnEnemy(def, !!def.boss, 1);
    if (!e) return null;
    const a = this.rng.next() * Math.PI * 2;
    e.x = this.px + Math.cos(a) * dist;
    e.y = this.py + Math.sin(a) * dist * 0.7;
    if (def.boss) {
      e.brain = e.art ? makeBrain(def.pattern) : null;
      this.cb.onBanner(def.name, BOSS_TITLE[def.pattern ?? ''] ?? '보스', def.color, 'boss', e.art ? `assets/enemies/boss_${def.art}.png` : undefined);
      audio.play('boss');
    }
    return e;
  }

  /** 1.22 개발자 패널: 보스만 빼고 적을 전부 치운다 (보스도 치우려면 all) */
  devClearEnemies(all = false): number {
    let n = 0;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.dead || (e.boss && !all)) continue;
      e.dead = true;
      e.xp = 0;
      n++;
    }
    return n;
  }

  // ── HUD ────────────────────────────────────────────────

  private snapshot(): HudSnapshot {
    let bossName: string | null = null;
    let bossRatio = 0;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.boss && !e.dead) {
        bossName = e.def.name;
        bossRatio = Math.max(0, e.hp / e.maxHp);
        break;
      }
    }
    return {
      hp: Math.max(0, this.hp),
      maxHp: this.playerStats?.maxHp ?? 100,
      level: this.level,
      xp: this.xp,
      xpNeed: this.xpNeed,
      kills: this.kills,
      elapsed: this.elapsed,
      bossesDown: this.bossesDown,
      nextBossIn: Math.max(0, (this.nextBossIndex + 1) * CONFIG.BOSS_INTERVAL - this.elapsed),
      bossName,
      bossHpRatio: bossRatio,
      dash: {
        charges: this.dashCharges,
        max: this.dashSpec().charges,
        ratio: this.dashCharges > 0 ? 1 : 1 - Math.max(0, this.dashCd) / Math.max(0.05, this.dashSpec().cd),
        zap: this.dashSpec().zap,
      },
      weapons: this.weapons.map((w) => ({
        name: w.def.name,
        level: w.level,
        color: w.def.color,
        shape: w.def.shape,
        sprite: w.def.sprite,
      })),
      passives: this.passives.map((p) => ({
        name: p.def.name,
        level: p.level,
        color: p.def.color,
        shape: p.def.shape,
        sprite: p.def.sprite,
      })),
      actives: this.activeSnapshot(),
      vigor: this.vigorSnapshot(),
      shield: this.shield,
      enemyCount: this.enemies.count,
      fps: this.fps,
    };
  }
}

/** 액티브 스킬 키 — 먼저 얻은 것부터 */
const ACTIVE_KEYS = ['Space', 'E'];
/** 변신: 그림 없는 캐릭터가 한 번 부푸는 시간 · 지쳐서 움츠러드는 시간 · 풀린 뒤 식는 시간 · 머리 위 한마디가 떠 있는 시간 */
const FORM_POP = 0.35;
const FORM_SLUMP = 0.55;
const FORM_COOL = 0.9;
const FORM_SAY = 1.4;
/** 변신한 동안: 받는 피해 감소 · 이동 속도 증가 */
const FORM_GUARD = 0.3;
/**
 * 받는 피해 감소의 상한. 메자이를 다 키운 값(기본 50% + 「성역」 8% + 「[견고] +3」 8%)과 같다.
 * 예전 상한 0.55 에서는 메자이 기본 50% 에 붙는 두 강화가 거의 아무 일도 안 했다(카드는 -8% 라고 했는데).
 */
const GUARD_CAP = 0.66;
const FORM_SPEED = 0.1;

/** 변신 모습 하나 (characters.json forms 를 읽은 것) */
interface FormArt {
  atlas: Atlas;
  anim: AnimDef;
  /** 변신하는 순간 재생할 프레임 (이 모습의 아틀라스) */
  transform: number[];
  /** 풀릴 때 재생할 프레임 (이 모습의 아틀라스) */
  untransform: number[];
  /** 그림 위에 불꽃 테두리·몸빛을 덧입는다 */
  aura: boolean;
}

/** 변신한 몸에서 피어오르는 불티 */
interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  t: number;
  life: number;
  /** 재(풀릴 때 섞여 흩어진다) */
  grey: boolean;
}

/** 두 색을 섞는다 (k = 0 이면 a, 1 이면 b) */
/** 1.22 적 발밑 그림자 — 24×10 도트 타원 (가운데가 짙다) */
/** 광풍에 뜬 적의 높이(px)와 회전. 한 번 쓰고 버리는 공용 칸이다 — 받자마자 쓴다 */
const AIR = { lift: 0, rot: 0 };
/** 적에게 붙은 [화염] 한 중첩의 피해 (예전 불은 고정값) */
function burnPer(e: Enemy): number {
  return e.burnDmg > 0 ? e.burnDmg : ST.BURN_DAMAGE_PER_STACK;
}
/**
 * 광풍에 뜰 수 있는 적의 원래 몸집(반지름) 상한.
 * 뜬다: 미물 12 · 아귀 13 · 요화 13 · 권귀 15 · 불미물 15 · 원귀 15 / 안 뜬다: 털괴 17 · 왕미물 20 · 거상 28 · 보스 · 엘리트
 */
const LIFT_MAX_RADIUS = 16;
/** 화면 밖 그림을 끌 때 둘 여유(px) — 큰 적 그림·공중에 뜬 몸이 화면 끝에서 뚝 끊겨 보이지 않게 */
const CULL_MARGIN = 160;
/**
 * 경험치 보석을 치우는 조건: 플레이어에게서 이만큼(px) 멀고, 떨어진 지 이만큼(초) 지났다.
 * 1200px 은 큰 모니터(1920px 너비 → 반폭 960)에서도 화면 밖이다.
 */
const GEM_EXPIRE_DIST = 1200;
const GEM_EXPIRE_AGE = 20;
/**
 * 끌려오는 보석: 옆으로 도는 속도를 초당 이만큼(비율) 빼고, 끌려온 지 1초마다 이만큼 더 뺀다.
 * 처음 한두 바퀴 와리가리는 남기고, 궤도에 걸려 오래 도는 것만 잡는다.
 */
const GEM_SIDE_DAMP = 0.9;
const GEM_SIDE_DAMP_GROW = 1.1;
/** 몸에 닿았다고 치는 거리(몸 반지름에 더하는 몫). 10 → 13 */
const GEM_CATCH = 13;
/** 공중에 뜬 적·시체의 넉백 감쇠(한 프레임). 땅(0.86)보다 덜 줄어 멀리 날아간다 */
const AIR_DAMP = 0.95;
function airPose(e: Enemy): { lift: number; rot: number } {
  if (e.airT <= 0 || e.airDur <= 0) {
    AIR.lift = 0;
    AIR.rot = 0;
    return AIR;
  }
  const u = 1 - e.airT / e.airDur;
  AIR.lift = e.airH * Math.sin(Math.PI * u);
  // 바퀴 수만큼 돌고 제자리에서 떨어진다(도는 쪽은 바라보는 쪽)
  AIR.rot = Math.PI * 2 * e.airTurns * u * (e.face >= 0 ? 1 : -1);
  return AIR;
}

function shadowTexture(): Texture {
  const w = 24;
  const h = 10;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const nx = (x + 0.5 - w / 2) / (w / 2);
      const ny = (y + 0.5 - h / 2) / (h / 2);
      const d = nx * nx + ny * ny;
      if (d > 1) continue;
      const i = (y * w + x) * 4;
      img.data[i + 3] = d < 0.45 ? 92 : 60;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';
  tex.source.autoGenerateMipmaps = false;
  return tex;
}

function mixColor(a: number, b: number, k: number): number {
  const t = Math.max(0, Math.min(1, k));
  const ch = (sh: number): number => Math.round(((a >> sh) & 255) + (((b >> sh) & 255) - ((a >> sh) & 255)) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}
